import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Card,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Checkbox,
  FormGroup,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  Paper,
  TextField,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import EditIcon from "@mui/icons-material/Edit";
import FlagIcon from "@mui/icons-material/Flag";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import StraightenIcon from "@mui/icons-material/Straighten";
import AdjustIcon from "@mui/icons-material/Adjust";
import { ProtractorIcon, SetSquareIcon } from "../../../components/measurement/MeasurementToolIcons";
import GridViewIcon from "@mui/icons-material/GridView";
import { toast } from "react-toastify";
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import api, { resolveQuestionHtmlAssetUrl } from "../../../api";
import useContentVerification, { formatVerifierButtonLabel, getVerifierButtonColors } from "../../../hooks/useContentVerification";
import QuestionVerifyDialog from "../../../components/QuestionVerifyDialog";
import QuestionBankQuestionEditDialog from "../../QuestionBank/components/QuestionBankQuestionEditDialog";
import { testerHasPermission } from "../../../utils/testerPermissions";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import FormatInstructions from "../../../components/FormatInstructions";
import HtmlWithLatex from "../../QuestionBank/components/HtmlWithLatex";
import QuestionPaperLayout from "../../QuestionBank/components/QuestionPaperLayout";
import QuestionAnnotationCanvas from "../MockExams/components/QuestionAnnotationCanvas";
import { TypeDrawInput, ScratchPad, MathInputTools, isPastPaperTextAnswerEmpty } from "../components/TypeDrawQuestionInput";
import ImageOverlayFillInput from "../components/ImageOverlayFillInput";
import InteractiveDiagramCanvas from "../components/InteractiveDiagramCanvas";
import MeasurementWorkspace from "../../../components/measurement/MeasurementWorkspace";
import { MeasurementToolsProvider, AnnotateProvider } from "../../../components/measurement/MeasurementToolsContext";
import VirtualProtractorOverlay from "../../../components/measurement/VirtualProtractorOverlay";
import VirtualRulerOverlay from "../../../components/measurement/VirtualRulerOverlay";
import VirtualSetSquareOverlay from "../../../components/measurement/VirtualSetSquareOverlay";
import VirtualCompassOverlay from "../../../components/measurement/VirtualCompassOverlay";
import GeometryMarkSegment from "../../../components/measurement/GeometryMarkSegment";
import { getMeasurementMode, measurementModeForSub, getPxPerCm, MEASUREMENT_MODES } from "../../../utils/measurementQuestionDetect";
import { isMeasurementVerifyCorrect } from "../../../utils/measurementVerify";
import { isAnswerCorrect, requiresOrderedAnswer, splitMultiValueAnswer, singleValueMatches, isSameValueMultiset, decodeHtmlEntities, questionAsksForSimplestForm, questionAsksForFractionForm, fractionIsInSimplestForm, valueIsFraction, submissionIsDeterministicallyWrong, correctIsLabelSequence, isNumericPowerExpression, matchesNumericPower } from "../../../utils/answerMatchUtils";
import { compressDrawingForVerify, compositeWithContext, skipContextForDrawing } from "../../../utils/answerDrawingUtils";
import EnglishErrorFindingLayout from "../../QuestionBank/components/EnglishErrorFindingLayout";
import EnglishClozePassageLayout from "../../QuestionBank/components/EnglishClozePassageLayout";
import PassageWithLineNumbers from "../../QuestionBank/components/PassageWithLineNumbers";
import { computePassageWrapWidth } from "../../QuestionBank/utils/passageLineExtract";
import { isErrorFindingStandalone, isErrorFindingContext, isClozeWordChoice } from "../../QuestionBank/utils/englishPaperDetection";
import {
  cleanStemText,
  extractSubCore,
  isComprehensionPassage,
  toSubLabel,
  countBlanksInText,
  groupedAnswerHasContent,
} from "../utils/groupedQuestionUtils";
import { interactiveWrittenOf, interactiveArrayOf, interactiveValueOf, interactiveHasContent, interactiveWrittenText } from "../utils/interactiveAnswerValue";
// The reveal ("correct solution") value is derived from the full spec by the SHARED helper. This page
// used to carry its own copy, which silently drifted — it never learned the `groups` (multi-group pie
// chart) or `metrics` modes, so those revealed as a blank chart. Import it so take/review/result agree.
import { interactiveRevealValue as revealValueOf } from "../utils/interactiveRevealValue";
import {
  isFillQuestionType,
  getDisplayFillMarksTotal,
} from "../../../utils/fillQuestionUtils";

const STUDENT_BASE = process.env.REACT_APP_STUDENT_PREFIX || "student";

function flagQuillImageHandler() {
  const quill = this.quill;
  const input = document.createElement('input');
  input.setAttribute('type', 'file');
  input.setAttribute('accept', 'image/png,image/jpeg,image/webp,image/gif');
  input.click();
  input.onchange = async () => {
    const file = input.files?.[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await api.post('/student/upload-flag-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data?.data?.image_url;
      if (!url) throw new Error('No image URL');
      const range = quill.getSelection(true) || { index: quill.getLength() };
      quill.insertEmbed(range.index, 'image', url, 'user');
      quill.setSelection(range.index + 1);
    } catch {
      toast.error('Image upload failed');
    }
  };
}
// Upload any base64-embedded images (from paste/drag-drop) to R2 before storing.
// Prevents SQLSTATE[22001] "string too long" when base64 screenshots exceed TEXT column.
let _flagReasonUploading = false;
async function processFlagReasonHtml(html, setter) {
  if (!html.includes('data:image/') || _flagReasonUploading) { setter(html); return; }
  _flagReasonUploading = true;
  let processed = html;
  for (const match of [...html.matchAll(/src="(data:image\/[^"]+)"/g)]) {
    try {
      const blob = await (await fetch(match[1])).blob();
      const file = new File([blob], `flag.${blob.type.split('/')[1] || 'png'}`, { type: blob.type });
      const fd = new FormData();
      fd.append('image', file);
      const res = await api.post('/student/upload-flag-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = res.data?.data?.image_url;
      if (url) processed = processed.replace(match[0], `src="${url}"`);
    } catch { toast.error('Image upload failed'); }
  }
  _flagReasonUploading = false;
  setter(processed);
}
const FLAG_QUILL_MODULES = {
  toolbar: {
    container: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ script: 'sub' }, { script: 'super' }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      ['link', 'image'],
      ['clean'],
    ],
    handlers: { image: flagQuillImageHandler },
  },
};
const FLAG_QUILL_FORMATS = ['header', 'bold', 'italic', 'underline', 'strike', 'script', 'list', 'bullet', 'indent', 'link', 'image'];
const pauseStorageKey = (id) => `paperPaused_${id}`;
const questionRichHtmlSx = { maxWidth: "100%", wordBreak: "normal", overflowWrap: "break-word", lineHeight: 1.65 };
const optionRichHtmlSx = { maxWidth: "100%", lineHeight: 1.65, fontSize: "1rem" };

const STATUS = {
  NOT_VISITED: "not-visited",
  UNANSWERED: "unanswered",
  ANSWERED: "answered",
  PARTIAL: "partial",
  FLAGGED: "flagged",
  ANSWER_LATER: "answer-later",
};

const statusColors = {
  [STATUS.NOT_VISITED]: "#e0e0e0",
  [STATUS.UNANSWERED]: "#ef5350",
  [STATUS.ANSWERED]: "#4caf50",
  [STATUS.PARTIAL]: "#9ccc65",
  [STATUS.FLAGGED]: "#ff9800",
  [STATUS.ANSWER_LATER]: "#1565c0",
};

const normalizeQuestionType = (question) => String(question?.type || question?.question_type || "").toLowerCase();
const isGroupedQuestion = (question) => normalizeQuestionType(question) === "grouped";
const isEssayType = (question) => normalizeQuestionType(question).includes("essay");
const isDrawingType = (question) => normalizeQuestionType(question) === "drawing";
const interactiveSpecOf = (question) => question?.answer_data?.interactive || null;
const isInteractiveDrawing = (question) => isDrawingType(question) && !!interactiveSpecOf(question);
// interactiveWrittenOf / interactiveArrayOf / interactiveWrittenText — shared from utils.
const interactiveImageOf = (question) =>
  interactiveSpecOf(question)?.image_url
  || (Array.isArray(question?.image_urls) ? question.image_urls.find(Boolean) : null)
  || question?.image_url
  || "";
const interactiveImgKey = (u) => String(u || "").split("?")[0].split("/").pop();
// Grid-coord point placements a student made on one drawing sub-part, each [gx, gy, label?]. Cut-drag
// and object-shaped answers carry no plottable points, so they contribute nothing.
const placedGridPointsOf = (subAnswer) => {
  const arr = interactiveArrayOf(subAnswer);
  return Array.isArray(arr) ? arr.filter((p) => Array.isArray(p) && p.length >= 2 && !Array.isArray(p[0])) : [];
};
// Build the read-only context_points a drawing sub inherits from EARLIER drawing subs that share the
// same background image — so parts that build on one figure (plot A/B/C, then reflect ABC) render on a
// single continuous grid. Only points the student has actually placed are carried forward. The current
// part's own reflect/rotate guide (original_shape) already shows the source shape, so we skip a part's
// context when it already draws that shape itself.
const sharedGridContextPoints = (subQuestions, safeAnswers, targetSub) => {
  const targetKey = interactiveImgKey(interactiveImageOf(targetSub));
  if (!targetKey) return [];
  // Points the target part already draws itself (a reflect/rotate's source shape) aren't repeated.
  const targetSpec = interactiveSpecOf(targetSub);
  // …nor points the part already carries as its own printed/given corners — a carried duplicate would
  // draw a second grey dot on top of an authored, joinable one.
  const ownShape = [
    ...(Array.isArray(targetSpec?.original_shape) ? targetSpec.original_shape : []),
    ...(Array.isArray(targetSpec?.given_points) ? targetSpec.given_points : []),
    ...(Array.isArray(targetSpec?.context_points) ? targetSpec.context_points : []),
  ];
  const ownKeys = new Set(ownShape.map((p) => `${Math.round(p[0])},${Math.round(p[1])}`));
  const out = [];
  const seen = new Set(ownKeys);
  for (const s of subQuestions) {
    if (s.id === targetSub.id) break;
    if (!isInteractiveDrawing(s)) continue;
    if (interactiveImgKey(interactiveImageOf(s)) !== targetKey) continue;
    const spec = interactiveSpecOf(s);
    if (spec?.kind !== "points" && spec?.kind !== "drag-shape" && spec?.kind !== "polygon" && spec?.kind !== "reflect" && spec?.kind !== "rotate") continue;
    const labels = Array.isArray(spec?.point_labels) ? spec.point_labels : [];
    placedGridPointsOf(safeAnswers[s.id]).forEach((p, i) => {
      const key = `${Math.round(p[0])},${Math.round(p[1])}`;
      if (seen.has(key)) return;
      seen.add(key);
      const lbl = (typeof p[2] === "string" && p[2] !== "") ? p[2] : (labels[i] || "");
      out.push(lbl ? [p[0], p[1], lbl] : [p[0], p[1]]);
    });
  }
  return out;
};
// One colour per part on a shared grid, so a student can see which mark belongs to which sub-question.
const SHARED_GRID_COLORS = ["#1565c0", "#2e7d32", "#ef6c00", "#6a1b9a", "#c62828", "#00838f"];
// Only plotting kinds build on each other's marks. Parts that each work a fresh copy of the same
// picture (shade this fraction, then that one; draw a symbol here, then there) must keep their own
// canvas — sharing one would show a single figure and leave every other part with no image at all.
const SHARED_GRID_KINDS = ["points", "drag-shape", "polygon", "rect", "reflect", "rotate", "lines"];
// The drawing parts of a group that all sit on ONE background image — the whole group is then answered
// on a single canvas instead of one canvas per part.
const sharedGridDrawingSubs = (subQuestions) => {
  const draws = (subQuestions || []).filter(
    (s) => isInteractiveDrawing(s) && SHARED_GRID_KINDS.includes(interactiveSpecOf(s)?.kind)
  );
  if (draws.length < 2) return [];
  const key0 = interactiveImgKey(interactiveImageOf(draws[0]));
  if (!key0) return [];
  return draws.every((s) => interactiveImgKey(interactiveImageOf(s)) === key0) ? draws : [];
};
// A part's committed drawing, in the shape InteractiveDiagramCanvas draws as a read-only context layer.
// points/polygon answers are [[gx,gy,label?]] or {points, edges:["gx,gy|gx,gy"]}; lines answers are
// fractional [[x,y],[x,y]] pairs.
// Edges/lines only — points travel via spec.context_points instead, because the canvas makes THOSE
// joinable ("join A to B" needs to tap a corner an earlier part plotted) while a layer is inert.
const sharedGridLayer = (sub, subAnswer, color, label) => {
  const v = interactiveValueOf(subAnswer);
  if (!v) return null;
  if (interactiveSpecOf(sub)?.kind === "lines") {
    const lines = Array.isArray(v)
      ? v.filter((l) => Array.isArray(l) && l.length === 2 && Array.isArray(l[0]) && Array.isArray(l[1]))
      : [];
    return lines.length ? { id: sub.id, label, color, lines } : null;
  }
  const edges = (Array.isArray(v?.edges) ? v.edges : [])
    .map((s) => String(s).split("|").map((h) => h.split(",").map(Number)))
    .filter((e) => e.length === 2 && e.every((h) => h.length === 2 && h.every(Number.isFinite)));
  return edges.length ? { id: sub.id, label, color, edges } : null;
};
// Points every OTHER part of the shared grid has plotted, tagged [gx, gy, label, colour] so each part's
// marks stay identifiable while still being joinable corners.
const sharedGridOtherPoints = (parts, safeAnswers, targetSub, colorOf) => {
  const targetSpec = interactiveSpecOf(targetSub);
  const seen = new Set([
    ...(Array.isArray(targetSpec?.given_points) ? targetSpec.given_points : []),
    ...(Array.isArray(targetSpec?.context_points) ? targetSpec.context_points : []),
    ...(Array.isArray(targetSpec?.original_shape) ? targetSpec.original_shape : []),
  ].map((p) => `${Math.round(p[0])},${Math.round(p[1])}`));
  const out = [];
  for (const s of parts) {
    if (s.id === targetSub.id) continue;
    const spec = interactiveSpecOf(s);
    if (spec?.kind === "lines") continue;
    const labels = Array.isArray(spec?.point_labels) ? spec.point_labels : [];
    placedGridPointsOf(safeAnswers[s.id]).forEach((p, i) => {
      const key = `${Math.round(p[0])},${Math.round(p[1])}`;
      if (seen.has(key)) return;
      seen.add(key);
      const lbl = (typeof p[2] === "string" && p[2] !== "") ? p[2] : (labels[i] || "");
      out.push([p[0], p[1], lbl, colorOf(s.id)]);
    });
  }
  return out;
};
const interactiveHint = (spec) => {
  if (!spec) return null;
  // Author-supplied per-question instructions override the generic per-kind hint
  // (e.g. a "draw the cut" lines question must not say "line of symmetry").
  if (typeof spec.instructions === "string" && spec.instructions.trim()) return spec.instructions.trim();
  if (spec.kind === "shade") {
    if (spec.require_labels) {
      return "Tap each hour section to shade it from the table (tap again to undo). Type the activity name in each label box.";
    }
    return "Tap the shapes/regions to shade them. Tap again to undo.";
  }
  if (spec.kind === "rect") return "Tap two opposite corners on the grid to draw your rectangle.";
  if (spec.kind === "lines") return "Tap two points to draw each line of symmetry. Add as many as a shape needs; tap a line to remove it. Leave a shape blank if it has none.";
  if (spec.kind === "cut-drag") return "Tap two points to draw your straight cut — the shape splits into two pieces. Then drag a piece to show how they fit together to make the new shape.";
  if (spec.kind === "segments") return "Tap one cross then another to join them with a straight line. Tap a line to remove it.";
  if (spec.kind === "points" || spec.kind === "drag-shape") {
    const labels = Array.isArray(spec.point_labels) ? spec.point_labels.filter(Boolean) : [];
    const labelReminder = spec.require_point_labels
      ? " Type the matching letter in the box next to each point — an unlabelled point is not enough."
      : "";
    const shapeLabelReminder = spec.require_shape_label
      ? " After the shape is complete, type the letter that names it in the popup — an unlabelled shape is not enough."
      : "";
    // shape_count>1 joins the points as that many SEPARATE lines, taken in tap order — the student must
    // know to finish one line before starting the next, or the runs group the wrong endpoints together.
    const lines = Number(spec.shape_count) > 1 ? Number(spec.shape_count) : 0;
    const lineReminder = lines
      ? ` Your points are joined as ${lines} separate lines — plot both ends of one line before starting the next.`
      : "";
    return labels.length
      ? `Tap the grid to plot, in order: ${labels.join(", ")}. Tap a point again to remove it.${lineReminder}${labelReminder}${shapeLabelReminder}`
      : `Tap the grid intersections to plot your point(s). Tap a point again to remove it.${lineReminder}${labelReminder}${shapeLabelReminder}`;
  }
  if (spec.kind === "polygon") {
    const shape = spec.require_shape ? spec.require_shape : "shape";
    const multiShape = Number(spec.shape_count) > 1 || (Array.isArray(spec.target_areas) && spec.target_areas.length > 1);
    const named = spec.require_point_labels && Array.isArray(spec.point_labels) ? spec.point_labels.filter(Boolean) : [];
    const labelReminder = named.length
      ? ` Name the corners you are asked for (${named.join(", ")}) — an unlabelled corner is not enough. Corners already printed on the diagram need no label.`
      : "";
    const shapeLabelReminder = spec.require_shape_label
      ? " After the shape is complete, type the letter that names it in the popup."
      : "";
    if (spec.closed === false || multiShape) return `Tap the grid points to draw your ${shape}. Tap a point again to remove it.${labelReminder}${shapeLabelReminder}`;
    return `Tap empty grid dots to place the corners of your ${shape}. Then tap one corner and another to draw an edge between them; work round the shape. Tap an edge to remove it, or tap a corner then its × to delete it. Use “Fill in” to colour it.${labelReminder}${shapeLabelReminder}`;
  }
  if (spec.kind === "glyph") {
    const palette = Array.isArray(spec.symbol_palette) ? spec.symbol_palette.filter(Boolean) : [];
    // Palette questions (reflect/rotate a tile of symbols like △ ▽ ○ + ↔) are tap-a-symbol, not type —
    // and the transformation decides which way a symbol points, so the student picks the right glyph
    // (e.g. the down triangle ▽ after a half turn) rather than rotating one.
    if (palette.length) {
      return `Tap a cell to add a symbol, then pick it from the palette (${palette.join(" ")}) — choose the one that points the right way. Click a symbol to select it (✕ removes it); drag it or use the arrow keys to nudge it into place. Click elsewhere to close the tools.`;
    }
    return "Tap the diagram to add a letter, then type it. Click any letter to select it — its tools (rotate ⟳ / flip ⇋ ⤢ / remove ✕) appear beside it. Drag a letter, or use the arrow keys, to move it into place. Click elsewhere to close the tools.";
  }
  if (spec.kind === "reflect") {
    const shapeLabelReminder = spec.require_shape_label
      ? " After the reflection is complete, type the letter that names it in the popup."
      : "";
    return `The shape and mirror line are shown. Tap empty grid dots to place the corners of its reflection. Then tap one corner and another to draw an edge between them; work round the shape. Tap an edge to remove it, or tap a corner then its × to delete it. Use “Fill in” to colour the finished shape.${shapeLabelReminder}`;
  }
  if (spec.kind === "rotate") {
    const shapeLabelReminder = spec.require_shape_label
      ? " After the rotation is complete, type the letter that names it in the popup."
      : "";
    return `The shape and rotation centre are shown. Tap empty grid dots to place the corners of the rotated shape. Then tap one corner and another to draw an edge between them; work round the shape. Tap an edge to remove it, or tap a corner then its × to delete it. Use “Fill in” to colour the finished shape.${shapeLabelReminder}`;
  }
  if (spec.kind === "clock") return "Drag the short (hour) and long (minute) hands to show the time.";
  if (spec.kind === "marker") return spec.style === "bar" ? "Tap the axis to set the height of the bar." : "Tap the number line / scale to mark the value.";
  if (spec.kind === "pictogram") return "Tap each icon to fill it in — tap once for a whole, then again for ¾, ½ and ¼, and once more to clear. Complete the row to show the right amount.";
  return null;
};
const isTextQuestionType = (question) => {
  const type = normalizeQuestionType(question);
  if (type === "drawing") return true;
  return ["short_answer", "short-answer", "essay", "short answer", "text"].some((value) => type.includes(value));
};
// Short-answer keeps Type + Draw tabs (type selected by default). Essay stays type-only.
// Drawing opens on the Draw tab.
const allowsDrawAnswer = (question) => isDrawingType(question) || (isTextQuestionType(question) && !isEssayType(question));
const isFillType = isFillQuestionType;
const isImageFill = (question) => normalizeQuestionType(question) === "image-fill";
// True only when the image-fill overlay IS the question's figure (its image_url matches one of the
// question's own static images), so painting both would double it. When the overlay is a small
// standalone answer-line box and the real content lives in a separate image_url(s) (e.g. clue
// scrolls to read + a "(…,…)" answer box), the statics must still show.
const overlayReplacesFigure = (question) => {
  const ov = question?.answer_data?.overlay_layout?.image_url;
  if (!ov) return false;
  const ovKey = String(ov).split("?")[0].split("/").pop();
  const statics = [];
  if (Array.isArray(question?.image_urls)) statics.push(...question.image_urls);
  if (question?.image_url) statics.push(question.image_url);
  return statics.some((u) => String(u || "").split("?")[0].split("/").pop() === ovKey);
};
// Box-mode image-fill renders answer boxes overlaid on the image (not as inline text blanks),
// so it must render via ImageOverlayFillInput, not the QuestionPaperLayout fill template.
const isBoxModeImageFill = (question) =>
  isImageFill(question)
  && question?.answer_data?.overlay_layout?.mode !== "inline"
  && Array.isArray(question?.answer_data?.overlay_layout?.blanks)
  && question.answer_data.overlay_layout.blanks.length > 0;
const isMultiSelectType = (question) => {
  const type = normalizeQuestionType(question);
  return type === "multiple-select" || type === "multiple_select";
};
const isOrderingType = (question) => normalizeQuestionType(question) === "ordering";

const orderingItems = (options) => (options || []).map((o) => o.option_text || o.text || "");

// Ordering answers are stored as an array of position numbers aligned to the options order:
// ranks[i] is the 1-based position the student assigned to options[i].
const orderingAnswerToText = (options, value) => {
  const items = orderingItems(options);
  const ranks = Array.isArray(value) ? value : [];
  const ranked = items
    .map((text, i) => ({ text, rank: parseInt(ranks[i], 10) }))
    .filter((p) => Number.isFinite(p.rank))
    .sort((a, b) => a.rank - b.rank);
  return ranked.map((p) => p.text).join(" | ").trim();
};

const OrderingInput = ({ options, value, onChange }) => {
  const items = orderingItems(options);
  const ranks = Array.isArray(value) ? value : [];
  const setRank = (idx, raw) => {
    const next = items.map((_, i) => ranks[i] ?? "");
    next[idx] = String(raw).replace(/[^0-9]/g, "").slice(0, 2);
    onChange(next);
  };
  const duplicate = (idx) => {
    const v = ranks[idx];
    if (!v) return false;
    return ranks.some((r, i) => i !== idx && r === v);
  };
  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.75 }}>
        Enter the order number (1–{items.length}) for each item.
      </Typography>
      {items.map((item, idx) => (
        <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1, p: 1, border: "1px solid #e0e0e0", borderRadius: 1, bgcolor: "#fafafa" }}>
          <TextField
            size="small"
            value={ranks[idx] ?? ""}
            onChange={(e) => setRank(idx, e.target.value)}
            error={duplicate(idx)}
            inputProps={{ inputMode: "numeric", "aria-label": `Order number for item ${idx + 1}`, style: { textAlign: "center" } }}
            placeholder="#"
            sx={{ width: 64 }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}><HtmlWithLatex html={String(item)} /></Box>
        </Box>
      ))}
    </Box>
  );
};

const isMatchingType = (question) => normalizeQuestionType(question) === "matching";

// Matching answers are an object keyed by the LEFT item's text: { "Oak Tree": "2390 cm", … } — the shape
// the backend's normalizeSubmittedMatching expects. The right-hand pool arrives as question.match_options
// (de-duplicated and shuffled server-side); the options themselves never carry their own match_text, or
// picking the answer would be a matter of reading them off in order.
const matchingAnswerOf = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : {};
const matchingPairCount = (value) =>
  Object.values(matchingAnswerOf(value)).filter((v) => String(v ?? "").trim() !== "").length;

const MatchingInput = ({ options, matchOptions, value, onChange }) => {
  const lefts = (options || []).map((o) => o.option_text || o.text || "");
  const rights = (matchOptions || []).filter((r) => String(r ?? "").trim() !== "");
  const selected = matchingAnswerOf(value);
  if (lefts.length === 0 || rights.length === 0) return null;
  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.75 }}>
        Choose the matching item for each one.
      </Typography>
      {lefts.map((left, idx) => (
        <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1, p: 1, border: "1px solid #e0e0e0", borderRadius: 1, bgcolor: "#fafafa", flexWrap: "wrap" }}>
          <Box sx={{ flex: 1, minWidth: 120 }}><HtmlWithLatex html={String(left)} /></Box>
          <Typography sx={{ color: "#6366f1", fontWeight: 700 }}>→</Typography>
          <Select
            size="small"
            displayEmpty
            value={selected[left] ?? ""}
            onChange={(e) => onChange({ ...selected, [left]: e.target.value })}
            sx={{ minWidth: 170, bgcolor: "#fff" }}
            renderValue={(v) => (v ? <HtmlWithLatex html={String(v)} /> : <em>Select…</em>)}
          >
            <MenuItem value=""><em>Select…</em></MenuItem>
            {rights.map((r) => (
              <MenuItem key={r} value={r}><HtmlWithLatex html={String(r)} /></MenuItem>
            ))}
          </Select>
        </Box>
      ))}
    </Box>
  );
};

const isPlotPointsQuestion = (question) => {
  if (!question) return false;
  const subtype = String(question?.question_subtype || question?.answer_data?.question_subtype || "").toLowerCase();
  if (subtype.includes("plot")) return true;
  const text = String(question?.question_text || "").toLowerCase();
  return text.includes("plot") && (text.includes("point") || text.includes("axes") || text.includes("coordinate"));
};
const collectQuestionImages = (question) => {
  if (!question) return [];
  // Images already embedded in question_text HTML are rendered by HtmlWithLatex — exclude them
  // from MeasurementWorkspace to prevent duplication.
  const htmlEmbedded = new Set();
  const html = String(question?.question_text || "");
  const imgRe = /<img[^>]*\ssrc=["']([^"']+)["']/gi;
  let m;
  while ((m = imgRe.exec(html)) !== null) htmlEmbedded.add(m[1]);

  const seen = new Set();
  const result = [];
  const add = (url) => {
    if (url && !seen.has(url) && !htmlEmbedded.has(url)) { seen.add(url); result.push(url); }
  };
  if (Array.isArray(question.image_urls)) question.image_urls.filter(Boolean).forEach(add);
  add(question.image_url);
  return result;
};

// Image-fill parts paint their answer boxes over ONE image. Drop just that image from the statics —
// a part that also carries a figure to read (the table a number machine produced, a clue scroll)
// must still show it. Suppressing every static whenever the overlay matched one of them hid those.
const imageFillContextImages = (question) => {
  const ov = question?.answer_data?.overlay_layout?.image_url;
  const ovKey = ov ? String(ov).split("?")[0].split("/").pop() : null;
  const images = collectQuestionImages(question);
  if (!ovKey) return images;
  return images.filter((src) => String(src || "").split("?")[0].split("/").pop() !== ovKey);
};

const collectAllQuestionImageEntries = (question) => {
  if (!question) return [];
  const entries = [];
  const seen = new Set();
  const push = (url, label) => { if (url && !seen.has(url)) { seen.add(url); entries.push({ url, label }); } };
  const qImgs = collectQuestionImages(question);
  qImgs.forEach((url, i) => push(url, i === 0 ? "Question diagram" : `Diagram ${i + 1}`));
  (question.sub_questions || []).forEach((sub, idx) => {
    const part = String.fromCharCode(65 + idx);
    collectQuestionImages(sub).forEach((url, i) =>
      push(url, i === 0 ? `Part ${part}` : `Part ${part} image ${i + 1}`)
    );
  });
  return entries;
};

const subMeasurementLayout = (parentQuestion, subQuestion) => {
  const mode = measurementModeForSub(parentQuestion, subQuestion);
  const imgs = collectQuestionImages(subQuestion);
  if (!mode || !imgs.length) return { mode: null, imgs: [], hideImage: false };
  return { mode, imgs, hideImage: true };
};
const isDiagramPlacementQuestion = (question) => {
  if (!question) return false;
  const text = String(question?.question_text || "").toLowerCase();
  const diagramVerbs = [
    "shade the",
    "complete the diagram",
    "draw the graph",
    "plot the graph",
    "mark on the",
    "venn",
    "number line",
    "mirror line",
    "reflect",
    "rotat",
    "translat",
    "enlarg",
    "transform",
    "construct",
  ];
  const hasVerb =
    diagramVerbs.some((v) => text.includes(v)) ||
    (text.includes("draw") && (text.includes("diagram") || text.includes("graph") || text.includes("grid"))) ||
    (text.includes("diagram") && (text.includes("shade") || text.includes("complete") || text.includes("label") || text.includes("mark")));
  if (collectQuestionImages(question).length === 0 && !hasVerb) return false;
  return (
    hasVerb ||
    (collectQuestionImages(question).length > 0 &&
      ((text.includes("place") && (text.includes("diagram") || text.includes("correct place"))) ||
        (text.includes("diagram") &&
          (text.includes("shade") || text.includes("complete") || text.includes("label") || text.includes("draw") || text.includes("mark")))))
  );
};

const stripHtmlTags = (s) => String(s ?? "").replace(/<[^>]*>/g, "").trim();

const formatCorrectAnswerForDisplay = (answer) => {
  if (answer == null) return "";
  let parsed = answer;
  if (typeof answer === "string" && answer.trim().startsWith("[")) {
    try { parsed = JSON.parse(answer); } catch {}
  }
  if (Array.isArray(parsed)) {
    const flat = parsed.flat(Infinity).map((v) => stripHtmlTags(String(v ?? ""))).filter(Boolean);
    return flat.join(" / ");
  }
  return stripHtmlTags(String(answer));
};

// Renders correct_answer with proper separation for essay questions (multiple topics).
// Only expands array items into numbered sections when isEssay=true; otherwise falls back to inline.
const renderCorrectAnswer = (answer, label, isEssay = false) => {
  let parsed = answer;
  if (typeof parsed === "string" && parsed.trim().startsWith("[")) {
    try { parsed = JSON.parse(parsed); } catch {}
  }
  if (isEssay && Array.isArray(parsed) && parsed.length > 1) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 0.25 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>{label}</Typography>
        {parsed.map((topic, i) => (
          <Box key={i} sx={{ borderLeft: "3px solid", borderColor: "inherit", pl: 1.5, py: 0.5, bgcolor: "rgba(0,0,0,0.04)", borderRadius: "0 4px 4px 0" }}>
            <Typography component="div" variant="caption" sx={{ fontWeight: 700, mb: 0.5, display: "block", textTransform: "uppercase", letterSpacing: 0.4 }}>
              Option {i + 1}
            </Typography>
            <HtmlWithLatex html={String(topic ?? "")} />
          </Box>
        ))}
      </Box>
    );
  }
  // A pure currency / plain-number answer ("$76.32", "$4.50") has no math to typeset, and a lone or
  // symmetric "$" makes MathJax emit a stray delimiter ("$76.32$"). Render it as plain text so the
  // "$" always shows literally, independent of MathJax config/caching.
  const plain = String(answer ?? "").replace(/<\/?p>/gi, "").trim();
  const currencyOnly = /^\$?\s?\d[\d.,]*\$?$/.test(plain);
  if (currencyOnly) {
    const clean = plain.replace(/\$$/, ""); // drop any trailing "$" MathJax-style wrapping
    return (
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>{label}</Typography>
        <Typography variant="body2">{clean}</Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>{label}</Typography>
      <HtmlWithLatex html={String(answer ?? "")} />
    </Box>
  );
};

// Renders the correct answer for a table question as the grid itself (fixed headers + pre-filled hints +
// the correct value in each editable cell) instead of a flat comma list.
const renderTableCorrectAnswer = (question, label = "Correct answer:") => {
  const grid = question?.answer_data?.table_layout?.grid;
  if (!Array.isArray(grid) || grid.length === 0) return null;
  const correctBlanks = question?.answer_data?.correct_blanks || [];
  const blankLabels = question?.answer_data?.blank_labels || [];
  const cellAnswer = (b) => {
    const v = correctBlanks[b];
    return Array.isArray(v) ? (v[0] ?? "") : (v ?? "");
  };
  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>{label}</Typography>
      <Box component="table" sx={{ borderCollapse: "collapse", "& td": { border: "1px solid #bbb", p: 1, textAlign: "center", verticalAlign: "middle", fontSize: "0.85rem" } }}>
        <tbody>
          {grid.map((row, ri) => (
            <tr key={ri}>
              {(row || []).map((cell, ci) => {
                const editable = cell && typeof cell.b === "number";
                if (editable) {
                  const hint = blankLabels[cell.b];
                  return (
                    <Box component="td" key={ci} sx={{ bgcolor: "#f0fdf4" }}>
                      {hint ? (
                        <Box sx={{ display: "block", color: "text.secondary", mb: 0.25, fontSize: "0.75rem" }}>
                          <HtmlWithLatex html={String(hint)} />
                        </Box>
                      ) : null}
                      <Box sx={{ color: "success.main", fontWeight: 700 }}>
                        <HtmlWithLatex html={String(cellAnswer(cell.b))} />
                      </Box>
                    </Box>
                  );
                }
                return (
                  <Box component="td" key={ci} sx={{ fontWeight: ri === 0 || ci === 0 ? 700 : 400, bgcolor: "#fafafa" }}>
                    <HtmlWithLatex html={String(cell?.f ?? "")} />
                  </Box>
                );
              })}
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  );
};

// Renders the correct answer for a box-mode image-fill question as the overlay image with each box
// filled by its correct value. Empty (intentionally unfilled) boxes still render as empty boxes, so
// the full answer matches the diagram instead of collapsing to a flat comma list that drops blanks.
const renderImageFillCorrectAnswer = (question, label = "Correct answer:", fallbackRaw = "") => {
  const overlay = question?.answer_data?.overlay_layout;
  if (!overlay || !Array.isArray(overlay.blanks) || overlay.blanks.length === 0) return null;
  const blanks = overlay.blanks;
  const cleanVal = (v) => {
    const raw = Array.isArray(v) ? (v[0] ?? "") : (v ?? "");
    return decodeHtmlEntities(stripHtmlTags(String(raw).replace(/&nbsp;/gi, " ")));
  };
  const correctBlanks = question?.answer_data?.correct_blanks;
  const hasBlanks = Array.isArray(correctBlanks)
    && correctBlanks.some((b) => (Array.isArray(b) ? b.length > 0 : String(b ?? "").trim() !== ""));
  let revealValues;
  if (hasBlanks) {
    revealValues = blanks.map((_, i) => cleanVal(correctBlanks[i]));
  } else {
    // The take payload withholds per-blank answers; fall back to the verify response's display
    // string. Single-box questions take it whole; multi-box split on | or , as a best effort.
    const rawStr = decodeHtmlEntities(stripHtmlTags(String(fallbackRaw ?? "").replace(/&nbsp;/gi, " ")));
    if (blanks.length === 1) {
      revealValues = [rawStr];
    } else {
      const parts = rawStr.split(/\s*[|,]\s*/);
      revealValues = blanks.map((_, i) => parts[i] ?? "");
    }
  }
  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>{label}</Typography>
      <ImageOverlayFillInput
        overlayLayout={overlay}
        values={revealValues}
        revealValues={revealValues}
        prefilled={question?.answer_data?.blank_labels}
        readOnly
      />
    </Box>
  );
};

const normalizeMathSymbols = (v) => String(v ?? '').replace(/÷/g, '/').replace(/×/g, '*');

// Evaluate a fraction / mixed number / decimal answer to a numeric value, or null if it isn't one.
// Handles \frac{a}{b}, "a/b", mixed "1 1/2", and a trailing % / £ / unit. A stray backslash ("34\15")
// is NOT a valid fraction slash — such an answer is rejected (null) so it is marked incorrect.
const numericAnswerValue = (raw) => {
  let s = normalizeMathSymbols(String(raw ?? '')).trim().toLowerCase();
  // A LaTeX mixed number "8\frac{3}{8}" must become "8 3/8" (with the separating space), not "83/8"
  // — otherwise it's misread as the simple fraction 83/8. Insert a space between a leading whole
  // number and a \frac that is glued to it before expanding the fraction.
  s = s.replace(/(\d)\s*\\frac/g, '$1 \\frac');
  s = s.replace(/\$/g, '').replace(/\\frac\s*\{\s*(-?\d+)\s*\}\s*\{\s*(\d+)\s*\}/g, '$1/$2');
  if (s.includes('\\')) return null; // e.g. "34\15" — wrong slash direction, not a valid fraction
  s = s.replace(/[£%]/g, '').trim();
  s = s.replace(/\s*\b(?:cm|mm|km|kg|mg|ml|m|g|l|p)\b\s*$/i, '').trim();
  let m = s.match(/^(-?\d+)\s+(\d+)\s*\/\s*(\d+)$/); // mixed number "1 1/2"
  if (m) { const d = +m[3]; return d ? (+m[1] + (+m[2]) / d) : null; }
  m = s.match(/^(-?\d+)\s*\/\s*(\d+)$/); // simple fraction "a/b"
  if (m) { const d = +m[2]; return d ? (+m[1]) / d : null; }
  if (/^-?\d+(?:\.\d+)?$/.test(s)) return parseFloat(s);
  return null;
};

// True when the mark scheme is a single bare fraction (e.g. "33/80" or "\frac{33}{80}"). Such answers
// are graded deterministically by value — AI Vision has wrongly accepted 66/80 for 33/80.
const isBareFractionAnswer = (correct) => {
  const c = normalizeMathSymbols(String(correct ?? '')).trim();
  if (!c || /\bor\b|,|;|=|\(or\)/i.test(c)) return false;
  const f = c.replace(/\$/g, '').replace(/\\frac\s*\{\s*(-?\d+)\s*\}\s*\{\s*(\d+)\s*\}/g, '$1/$2').replace(/\\/g, '/').trim();
  return /^-?\d+\s*\/\s*\d+$/.test(f);
};

const ARITHMETIC_VULGAR_FRACTIONS = {
  '½': ' 1/2 ', '⅓': ' 1/3 ', '⅔': ' 2/3 ', '¼': ' 1/4 ', '¾': ' 3/4 ',
  '⅕': ' 1/5 ', '⅖': ' 2/5 ', '⅗': ' 3/5 ', '⅘': ' 4/5 ', '⅙': ' 1/6 ',
  '⅚': ' 5/6 ', '⅐': ' 1/7 ', '⅛': ' 1/8 ', '⅜': ' 3/8 ', '⅝': ' 5/8 ',
  '⅞': ' 7/8 ', '⅑': ' 1/9 ', '⅒': ' 1/10 ',
};
const ARITHMETIC_SUPERSCRIPTS = { '⁰': '0', '¹': '1', '²': '2', '³': '3', '⁴': '4', '⁵': '5', '⁶': '6', '⁷': '7', '⁸': '8', '⁹': '9' };

// Put a written calculation into one comparable form: ÷ × → / *, ⅙ → 1/6, 4² → 4^2, \times → *,
// \frac{1}{6} → 1/6, an ellipsis gap ("+ ... +") dropped. Mark schemes and typed answers spell the
// same sum very differently, and the tokens are only meaningful once both sides use one alphabet.
const normalizeArithmeticText = (raw) => normalizeMathSymbols(String(raw ?? ''))
  .replace(/\\times/g, '*')
  .replace(/\\div/g, '/')
  .replace(/\\dots|\\ldots|…|\.\.\./g, ' ')
  .replace(/\\frac\s*\{\s*(-?\d+)\s*\}\s*\{\s*(\d+)\s*\}/g, ' $1/$2 ')
  .replace(/[½⅓⅔¼¾⅕⅖⅗⅘⅙⅚⅐⅛⅜⅝⅞⅑⅒]/g, (ch) => ARITHMETIC_VULGAR_FRACTIONS[ch] ?? ch)
  .replace(/[⁰¹²³⁴⁵⁶⁷⁸⁹]/g, (ch) => `^${ARITHMETIC_SUPERSCRIPTS[ch]}`)
  .replace(/[£$%]/g, '')
  .replace(/[−–—]/g, '-')
  .replace(/(?<=\d)\s*x\s*(?=\d)/gi, '*');

// A "what calculation did you do?" mark scheme is a pure arithmetic expression — only numbers,
// operators (× ÷ + − * / ^), parentheses, "=" and spaces. These are graded by structure, not AI Vision,
// which has accepted answers with an operator dropped ("(30 10) ÷ 50" for "(30 × 10) ÷ 50"). Returns
// the canonical { operators, operands } token lists, or null when the value isn't a clean expression.
// `requireCompound` gates the CORRECT side only — a submission is allowed to be operator-less, since a
// dropped operator ("1/6 4 5 9" for "⅙ × 4 × 5 × 9") is the very thing being caught.
const arithmeticExpressionTokens = (raw, { requireCompound = true } = {}) => {
  const s = normalizeArithmeticText(raw).trim();
  if (s === '') return null;
  if (!/^[\d\s()+\-*/.^=]+$/.test(s)) return null; // any letter/word means it's not a bare calculation
  // Scope strictly to COMPOUND calculations — a multiplication (× / *) or parentheses ("(30 × 10) ÷ 50").
  // Requiring one keeps a bare number, a negative ("-5"), a fraction ("1/2"), a subtraction/hyphen chain
  // and a value list out (matching those wrongly downgraded equivalents like "0.5" for "1/2").
  if (requireCompound && !/[*(]/.test(s)) return null;
  const operators = (s.match(/[+\-*/^=]/g) || []).slice().sort();
  const operands = (s.match(/\d+(?:\.\d+)?/g) || []).map((n) => String(parseFloat(n))).sort();
  return { operators, operands, hasOperator: /[+\-*/=]/.test(s) };
};

const sameArithmeticTokens = (a, b) => a.operators.join(' ') === b.operators.join(' ')
  && a.operands.join(' ') === b.operands.join(' ');

// Most mark schemes state the calculation inside a sentence ("The next line of the pattern is:
// 1² + 2² + 3² + 4² = 30 = ⅙ × 4 × 5 × 9."). Pull out the arithmetic runs so a student who writes the
// line out can still be checked operator-for-operator. Runs that aren't compound calculations are
// dropped, so a stray year or page number in the prose can never become a target.
const arithmeticRunsInProse = (raw) => (normalizeArithmeticText(raw).match(/[\d\s()+\-*/.^=]{6,}/g) || [])
  .map((run) => arithmeticExpressionTokens(run.replace(/[\s.]+$/, '')))
  .filter(Boolean);

// The submitted calculation is NOT the same calculation as the mark scheme — a different operator, a
// dropped operator ("30 10" for "30 × 10"), or different operands. Returns null (leave the verdict
// alone) whenever the mark scheme holds no compound calculation to compare against.
const arithmeticCalculationMismatch = (submitted, correct) => {
  const s = arithmeticExpressionTokens(submitted, { requireCompound: false });
  const c = arithmeticExpressionTokens(correct);
  if (c) {
    if (!s) return true; // correct is a calculation but the submission isn't a valid one
    return !sameArithmeticTokens(s, c);
  }
  // Prose mark scheme. Only meaningful once the student has written a calculation out: explanatory
  // working ("The mean is 7. The total is (5 × 4) + (6 × 5)…") quotes sums the answer itself — a short
  // value ("7") — is not expected to repeat, so anything without an operator is left alone.
  if (!s || !s.hasOperator) return null;
  const runs = arithmeticRunsInProse(correct);
  if (!runs.length) return null;
  if (runs.some((run) => sameArithmeticTokens(s, run))) return false;
  // Prose states the calculation but rarely IS the expected answer, so only the unambiguous signature
  // counts: every operand of a run reproduced, operators not. That is a dropped or swapped symbol
  // ("1/6 4 5 9" for "⅙ × 4 × 5 × 9"). Quoting a run in part ("6 × 4" of "6 × 4 = 24") differs in its
  // operands too — a partial working, not a wrong one — and is left to the existing verdict.
  const droppedOperator = runs.some((run) => run.operands.join(' ') === s.operands.join(' ')
    && run.operators.join(' ') !== s.operators.join(' '));
  return droppedOperator ? true : null;
};

// A mark scheme that is purely one or more colon-format clock times (e.g. "3:00", "3:00 or 9:00").
// Used to force strict, rule-based grading for time-of-day answers so dot/bare/o'clock forms
// (3.00, 3.0, 3, "3 o'clock") are not accepted.
const isPureClockTimeAnswer = (text) => {
  const t = String(text ?? '').trim();
  if (!t) return false;
  const parts = t.split(/\s+or\s+|\s*\/\s*|\s*,\s*/i).map((p) => p.trim()).filter(Boolean);
  // am/pm may be written with dots ("a.m", "p.m"), e.g. from an authored answer-unit suffix.
  return parts.length > 0 && parts.every((p) => /^\d{1,2}:\d{2}(\s*(a\.?\s*m\.?|p\.?\s*m\.?))?$/i.test(p));
};

// When the backend stores a verbose AI explanation as correct_answer (e.g. "Sam needs £79 more…"),
// extract whether the submitted short value appears as a standalone numeric token in that prose.
const isShortValueInProseAnswer = (submitted, prose) => {
  if (!submitted || !prose) return false;
  // A route / order of labelled points ("A E D B C G") is the answer itself, not prose explaining it:
  // every letter is required, so finding the first few of them ("A E") must not pass the whole route.
  if (correctIsLabelSequence(prose)) return false;
  if (prose.length <= submitted.length * 2) return false;
  // A date (e.g. "1st November 1998") is the answer itself, not explanatory prose — matching any one
  // of its tokens ("1st", "November", "1998") must not pass the whole answer.
  if (/\b(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\b/i.test(prose) && /\d/.test(prose)) return false;
  // A comma- or "and"-separated list of discrete values (e.g. "0.005, 0.024, 1.006, 1.01",
  // "5 and 13") is a structured mark-scheme answer set, not prose — matching only one of its
  // required values must not pass the whole answer ("give all possibilities" needs every value).
  // Only genuine sentences (a part with 2+ words) are treated as prose.
  const proseParts = prose.split(/,(?!\d{3}(?!\d))|\band\b/i);
  if (proseParts.length > 1 &&
      !proseParts.some((p) => p.trim().split(/\s+/).filter(Boolean).length >= 2)) {
    return false;
  }
  const normalized = normalizeMathSymbols(submitted.trim());
  const prosNorm = normalizeMathSymbols(prose);
  const checkPart = (val) => {
    if (!val) return false;
    const escaped = val.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`(?:^|[\\s=:£(,])${escaped}(?=$|[\\s.,)£,:])`, 'i').test(prosNorm);
  };
  if (checkPart(normalized)) return true;
  // For comma-separated multi-part answers ("5,4"), all parts must appear in prose.
  const parts = normalized.split(',').map((s) => s.trim()).filter(Boolean);
  if (parts.length > 1 && parts.every((p) => checkPart(p))) return true;
  return false;
};

const isTypedVerifyCorrect = (submitted, correct, question = null) => {
  const mode = question ? getMeasurementMode(question) : null;
  if (mode) {
    const measured = isMeasurementVerifyCorrect(submitted, correct, question, mode);
    if (measured !== null) return measured;
  }
  return isAnswerCorrect(submitted, correct, {
    ordered: requiresOrderedAnswer(question),
    questionText: question?.question_text ?? question?.text ?? question?.question ?? '',
  });
};

// compressDrawingForVerify / compositeWithContext imported from answerDrawingUtils

const getVerifyDisplayProps = (result) => {
  if (!result) return null;
  if (result.is_correct) {
    return {
      severity: "success",
      prefix: result.ai_evaluated ? "Correct! ✓ (AI Vision)" : "Correct! ✓",
      answerLabel: "Correct answer:",
      needsReview: false,
    };
  }
  // Only a genuine 2-model disagreement (both vision models ran) is "models disagreed". The
  // no-reference fallback also sets needs_review but never ran the models — handle it below as a
  // plain "compare with the correct answer" drawing result, not a conflicting-verdict warning.
  if (result.needs_review && result.ai_evaluated) {
    return {
      severity: "warning",
      prefix: "Needs review — AI models disagreed",
      answerLabel: "Correct answer:",
      aiEvaluated: true,
      needsReview: true,
    };
  }
  if (result.is_partial) {
    return {
      severity: "warning",
      prefix: "Partially correct",
      answerLabel: "Full answer:",
      needsReview: false,
    };
  }
  if (result.is_correct === null && result.was_answered) {
    return { severity: "info", prefix: null, answerLabel: "Correct answer:", needsReview: false };
  }
  if (!result.was_answered) {
    return { severity: "info", prefix: null, answerLabel: "Correct answer:", needsReview: false };
  }
  if (result.submitted_drawing) {
    if (!result.ai_evaluated) {
      return {
        severity: "info",
        prefix: "Drawing saved ✓",
        answerLabel: "How to do it:",
        needsReview: false,
      };
    }
    return {
      severity: "warning",
      prefix: "Incorrect (AI Vision)",
      answerLabel: "Correct answer:",
      needsReview: false,
    };
  }
  return { severity: "error", prefix: "Incorrect", answerLabel: "Correct answer:", needsReview: false };
};

const isQuestionAnswered = (question, answers) => {
  if (!question) return false;
  const value = answers[question.id];
  if (isGroupedQuestion(question)) {
    return (question.sub_questions || []).every((sub) => groupedAnswerHasContent((value || {})[sub.id]));
  }
  if (isTextQuestionType(question)) {
    return !isPastPaperTextAnswerEmpty(value);
  }
  if (isFillType(question)) {
    if (Array.isArray(value)) return value.some((v) => String(v ?? "").trim() !== "");
    return value != null && String(value).trim() !== "";
  }
  if (isInteractiveDrawing(question)) {
    return interactiveHasContent(value) || interactiveWrittenText(value).trim() !== "";
  }
  if (isMatchingType(question)) {
    // Answered only when EVERY item has been paired — an empty {} is truthy, so the default check below
    // would mark the question done the moment the widget rendered.
    const lefts = (question.options || []).map((o) => o.option_text || o.text || "");
    const selected = matchingAnswerOf(value);
    return lefts.length > 0 && lefts.every((l) => String(selected[l] ?? "").trim() !== "");
  }
  return value != null && value !== "";
};

const getBlankTemplate = (question) => {
  // Image-fill behaves like a fill question (image + □ blanks). Boxes mode binds each box to
  // correct_blanks[i] positionally; inline mode counts □ markers in the text.
  if (isImageFill(question)) {
    const cb = question.answer_data?.correct_blanks;
    if (Array.isArray(cb) && cb.length > 0) return cb;
    const boxes = question.answer_data?.overlay_layout?.blanks;
    if (Array.isArray(boxes) && boxes.length > 0) return boxes.map(() => [""]);
    const n = countBlanksInText(question.question_text);
    return Array.from({ length: Math.max(1, n) }, () => [""]);
  }
  if (!isFillType(question)) return [];
  const fromCorrect = question.answer_data?.correct_blanks;
  if (Array.isArray(fromCorrect) && fromCorrect.length > 0) return fromCorrect;
  const fromLegacy = question.blanks;
  if (Array.isArray(fromLegacy) && fromLegacy.length > 0) return fromLegacy;
  const fromLabels = question.answer_data?.blank_labels;
  if (Array.isArray(fromLabels) && fromLabels.length > 0) return fromLabels.map(() => [""]);
  if (normalizeQuestionType(question).includes("fill")) {
    const count = normalizeQuestionType(question).includes("multi")
      ? countBlanksInText(question.question_text)
      : 1;
    return Array.from({ length: Math.max(1, count) }, () => [""]);
  }
  // For table type: derive blank count from table_layout.grid editable cells
  const tableGrid = question.answer_data?.table_layout?.grid;
  if (Array.isArray(tableGrid) && tableGrid.length > 0) {
    let maxB = -1;
    tableGrid.forEach((row) => (row || []).forEach((cell) => {
      if (cell && typeof cell.b === 'number' && cell.b > maxB) maxB = cell.b;
    }));
    if (maxB >= 0) return Array.from({ length: maxB + 1 }, () => [""]);
  }
  return [];
};

const getQuestionPreview = (question, answers) => {
  const value = answers[question.id];
  if (isGroupedQuestion(question)) {
    const answered = (question.sub_questions || []).filter((sub) => groupedAnswerHasContent((value || {})[sub.id])).length;
    return `${answered}/${question.sub_questions?.length || 0} parts answered`;
  }
  if (value == null || value === "") return "—";
  if (Array.isArray(value)) {
    const filled = value.filter((item) => String(item ?? "").trim() !== "");
    return filled.length ? filled.join(" | ").slice(0, 40) : "—";
  }
  if (typeof value === "object" && value !== null) {
    const parts = [];
    if (value.text && String(value.text).trim()) {
      const text = String(value.text).trim();
      parts.push(text.length > 36 ? `${text.slice(0, 36)}…` : text);
    }
    if (value.drawing) parts.push("Drawing");
    return parts.length ? parts.join(" · ") : "—";
  }
  if (typeof value === "string" && value.length > 40) return `${value.slice(0, 40)}…`;
  return String(value);
};

const GroupedQuestion = ({
  question,
  answerMap,
  onSubAnswerChange,
  questionIdx,
  scratchDrawingsRef,
  activeSubIdx,
  onSubIdxChange,
  isPracticeMode,
  onVerify,
  verifyLoading,
  passageOpen,
  isComprehensionMode,
  verifyResult,
  verifyQuestionId,
  pendingFloatImage,
  onFloatImageConsumed,
  pendingFloatSubId,
  showMathTools = true,
  showAnswerUnit = true,
  isEnglishPaper = false,
}) => {
  const subQuestions = question.sub_questions || [];
  const safeAnswers = answerMap && typeof answerMap === "object" && !Array.isArray(answerMap) ? answerMap : {};
  const compoundBlankRefs = useRef({});
  const [activeCompoundIdx, setActiveCompoundIdx] = useState(0);
  // Which part the one shared grid is currently accepting taps for (see sharedGridDrawingSubs).
  const [activePartId, setActivePartId] = useState(null);
  const stemText = cleanStemText(question.question_text || question.group_title || "");
  const stemVisibleText = stemText.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim();
  const hasStem = stemVisibleText.length > 0 || collectQuestionImages(question).length > 0;
  // Plotting / drawing groups that share one diagram across parts are shown with every part expanded on
  // its own grid (extended as a single question) instead of a/b tabs — controlled by the admin
  // "Show all parts extended" flag (answer_data.extend_parts).
  const extendFlag = !!(question.answer_data?.extend_parts);
  // Parts that all plot on ONE background image are answered on a single shared canvas (below), which
  // replaces both tab-nav and the stacked one-canvas-per-part expanded layout.
  const sharedDraws = sharedGridDrawingSubs(subQuestions);
  const useTabNav = subQuestions.length > 1 && hasStem && !extendFlag && sharedDraws.length === 0;
  const safeSubIdx = Math.min(activeSubIdx, Math.max(0, subQuestions.length - 1));
  const currentSub = subQuestions[safeSubIdx];
  const currentSubAnswer = currentSub ? safeAnswers[currentSub.id] : undefined;
  const currentLabel = toSubLabel(safeSubIdx, currentSub);
  const currentClean = currentSub ? { ...currentSub, question_text: extractSubCore(currentSub.question_text, currentLabel) } : null;

  const subMarksLabel = (() => {
    const count = subQuestions.length;
    if (count < 2) return null;
    const toFracLabel = (total, n) => {
      const dec = Math.round((total / n) * 100) / 100;
      return `${total}/${n} ≈ ${dec} marks`;
    };
    const parentAd = question.answer_data || {};
    if (parentAd.marks_mode === 'fixed') {
      const total = parentAd.marks_total ?? question.default_marks ?? question.marks ?? 1;
      return toFracLabel(total, count);
    }
    const parentTotal = Number(question.default_marks ?? question.marks ?? 0);
    const subMarks = subQuestions.map((s) => Number(s.marks ?? s.default_marks ?? 1));
    const allEqual = subMarks.every((m) => m === subMarks[0]);
    const subSum = subMarks.reduce((a, b) => a + b, 0);
    if (parentTotal > 0 && allEqual && subSum > parentTotal && parentTotal !== subMarks[0]) return toFracLabel(parentTotal, count);
    return null;
  })();

  const renderVerifyResult = (subId) => {
    if (!isPracticeMode || !verifyResult || verifyQuestionId !== subId) return null;
    const props = getVerifyDisplayProps(verifyResult);
    if (!props) return null;
    const resultSub = subQuestions.find((s) => s.id === subId) || currentSub;
    return (
      <Alert severity={props.severity} sx={{ mt: 1.5 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography variant="body2">{verifyResult.hint || props.prefix || ""}</Typography>
          {normalizeQuestionType(resultSub) === 'table'
            ? renderTableCorrectAnswer(resultSub, props.answerLabel || "Correct answer:")
            : isBoxModeImageFill(resultSub)
            ? renderImageFillCorrectAnswer(resultSub, props.answerLabel || "Correct answer:", verifyResult.correct_answer)
            : (verifyResult.correct_answer ? renderCorrectAnswer(verifyResult.correct_answer, props.answerLabel || "Correct answer:", resultSub?.type === 'essay') : null)}
          {verifyResult.correct_option_image ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 0.25 }}>
              {!verifyResult.correct_answer && (
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{props.answerLabel || "Correct answer:"}</Typography>
              )}
              <Box component="img" src={verifyResult.correct_option_image} alt="Correct option" onError={(e) => { e.target.style.display = "none"; }} sx={{ maxWidth: "100%", maxHeight: 120, objectFit: "contain", borderRadius: 1, border: "1px solid", borderColor: "divider", bgcolor: "white", alignSelf: "flex-start" }} />
            </Box>
          ) : null}
          {verifyResult.needs_review && verifyResult.ai_evaluated ? (
            <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "warning.dark", fontStyle: "italic" }}>
              ⚠ AI models gave conflicting verdicts — ask your tutor to review
            </Typography>
          ) : null}
          {verifyResult.submitted_drawing ? (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                {verifyResult.ai_evaluated ? "Your drawing (as seen by AI):" : "Your drawing:"}
              </Typography>
              <Box component="img" src={verifyResult.submitted_drawing} alt="Your submitted drawing" sx={{ maxWidth: "100%", maxHeight: 300, borderRadius: 1, border: "1px solid", borderColor: "divider", bgcolor: "white" }} />
              {verifyResult.ai_evaluated && !verifyResult.needs_review && (
                <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "text.secondary", fontStyle: "italic" }}>
                  ✦ Evaluated by AI Vision
                </Typography>
              )}
            </Box>
          ) : null}
          {verifyResult.correct_drawing && !verifyResult.correct_interactive ? (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>Correct solution:</Typography>
              <Box component="img" src={verifyResult.correct_drawing} alt="Correct solution" sx={{ maxWidth: "100%", maxHeight: 300, borderRadius: 1, border: "1px solid", borderColor: "divider", bgcolor: "white" }} />
            </Box>
          ) : null}
          {verifyResult.correct_interactive ? (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>Correct solution:</Typography>
              <InteractiveDiagramCanvas
                spec={verifyResult.correct_interactive}
                imageUrl={verifyResult.correct_interactive.image_url || interactiveImageOf(resultSub)}
                maxWidth={480}
                readOnly
                revealValue={revealValueOf(verifyResult.correct_interactive)}
              />
              {interactiveWrittenOf(verifyResult.correct_interactive) ? (
                <Typography variant="body2" sx={{ mt: 0.75 }}>
                  <strong>Written answer:</strong> {verifyResult.correct_interactive.written.answer}
                </Typography>
              ) : null}
            </Box>
          ) : null}
        </Box>
      </Alert>
    );
  };

  const renderSubInput = (sub, subAnswer, subQuestionIdx, opts = {}) => {
    if (isInteractiveDrawing(sub)) {
      // hideCanvas: the group already paints ONE shared canvas above the part list, so this part
      // contributes only its written box / Check answer, never a second grid.
      if (opts.hideCanvas) {
        return (
          <>
            {interactiveWrittenOf(sub.answer_data.interactive) ? (
              <Box sx={{ mt: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  label={sub.answer_data.interactive.written.prompt || "Write your answer"}
                  value={interactiveWrittenText(subAnswer)}
                  onChange={(e) => onSubAnswerChange(sub.id, {
                    interactive: interactiveValueOf(subAnswer),
                    text: e.target.value,
                  })}
                />
              </Box>
            ) : null}
            {isPracticeMode ? (
              <Box sx={{ mt: 1 }}>
                <Button variant="outlined" size="small" startIcon={<FactCheckIcon />} onClick={() => onVerify(sub)} disabled={verifyLoading} sx={{ textTransform: "none", fontWeight: 700 }}>
                  {verifyLoading ? "Checking…" : "Check answer"}
                </Button>
              </Box>
            ) : null}
            {renderVerifyResult(sub.id)}
          </>
        );
      }
      // The sub is rendered with hideImage, so QuestionPaperLayout paints none of its images; the
      // canvas below shows the one interactive figure. Suppress only the image the canvas itself
      // paints — every OTHER image is genuine context the student needs (e.g. the data table they read
      // to complete the chart) and must still be shown. Dropping them all whenever the spec carried an
      // authored image_url hid those tables entirely.
      const interactiveKey = imgFileKey(interactiveImageOf(sub));
      const contextImages = collectQuestionImages(sub).filter((src) => imgFileKey(src) !== interactiveKey);
      // Carry the points the student placed on earlier drawing parts sharing this grid onto this one,
      // so a multi-part figure (plot A/B/C, then reflect ABC) reads as a single continuous grid.
      const carried = sharedGridContextPoints(subQuestions, safeAnswers, sub);
      const drawSpec = carried.length
        ? { ...sub.answer_data.interactive, context_points: [...(sub.answer_data.interactive.context_points || []), ...carried] }
        : sub.answer_data.interactive;
      return (
        <>
          {contextImages.length > 0 ? (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
              {contextImages.map((src, i) => (
                <Box key={i} component="img" src={src} alt="" onError={(e) => { e.target.style.display = "none"; }} sx={{ width: "auto", maxWidth: "min(100%, 640px)", maxHeight: 360, objectFit: "contain", borderRadius: 1 }} />
              ))}
            </Box>
          ) : null}
          {interactiveHint(sub.answer_data.interactive) ? (
            <Typography variant="caption" color="primary.main" sx={{ display: "block", mb: 0.75, fontWeight: 600 }}>
              {interactiveHint(sub.answer_data.interactive)}
            </Typography>
          ) : null}
          <InteractiveDiagramCanvas
            spec={drawSpec}
            imageUrl={interactiveImageOf(sub)}
            maxWidth={480}
            value={interactiveValueOf(subAnswer)}
            onChange={(v) => onSubAnswerChange(
              sub.id,
              interactiveWrittenOf(sub.answer_data.interactive)
                ? { interactive: v, text: interactiveWrittenText(subAnswer) }
                : v
            )}
          />
          {interactiveWrittenOf(sub.answer_data.interactive) ? (
            <Box sx={{ mt: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                label={sub.answer_data.interactive.written.prompt || "Write your answer"}
                value={interactiveWrittenText(subAnswer)}
                onChange={(e) => onSubAnswerChange(sub.id, {
                  // interactiveValueOf: typing here must not drop the drawn outline ({points, edges}).
                  interactive: interactiveValueOf(subAnswer),
                  text: e.target.value,
                })}
              />
            </Box>
          ) : null}
          {isPracticeMode ? (
            <Box sx={{ mt: 1 }}>
              <Button variant="outlined" size="small" startIcon={<FactCheckIcon />} onClick={() => onVerify(sub)} disabled={verifyLoading} sx={{ textTransform: "none", fontWeight: 700 }}>
                {verifyLoading ? "Checking…" : "Check answer"}
              </Button>
            </Box>
          ) : null}
          {renderVerifyResult(sub.id)}
        </>
      );
    }
    const fillTemplate = getBlankTemplate(sub);
    if (fillTemplate.length > 0 && !isBoxModeImageFill(sub)) {
      // A multi-fill part can additionally carry a drawing/shade grid (answer_data.interactive), e.g.
      // "sketch the rectangle AND write its perimeter/area". The sub answer then travels as an object
      // { blanks, interactive } so both surfaces are graded independently and their marks summed.
      const fillSpec = sub.answer_data?.interactive || null;
      const rawFillBlanks = fillSpec
        ? (subAnswer && typeof subAnswer === "object" && !Array.isArray(subAnswer) ? subAnswer.blanks : subAnswer)
        : subAnswer;
      // interactiveValueOf, not interactiveArrayOf: the canvas re-reads its drawn outline out of
      // value.edges, so handing it the bare point array wipes every edge as soon as it's drawn.
      const fillInteractive = fillSpec
        ? interactiveValueOf(subAnswer && typeof subAnswer === "object" && !Array.isArray(subAnswer) ? subAnswer.interactive : [])
        : [];
      const values = Array.isArray(rawFillBlanks)
        ? rawFillBlanks
        : fillTemplate.length === 1
        ? [rawFillBlanks ?? ""]
        : Array.from({ length: fillTemplate.length }, () => "");
      return (
        <>
          <QuestionPaperLayout
            hideBlankMarks
            imageMaxHeight={300}
            imageMaxWidth="min(100%, 540px)"
            question={{ ...sub, question_text: extractSubCore(sub.question_text, toSubLabel(subQuestionIdx, sub)) }}
            questionNumber={subQuestionIdx + 1}
            showAnswers={false}
            subLabel={toSubLabel(subQuestionIdx, sub)}
            marksLabel={subMarksLabel}
            inputValues={values}
            showMathTools={showMathTools}
            onInputChange={(inputIdx, newVal) => {
              const len = fillTemplate.length;
              onSubAnswerChange(sub.id, (stored) => {
                const storedBlanks = fillSpec && stored && typeof stored === "object" && !Array.isArray(stored) ? stored.blanks : stored;
                const cur = Array.isArray(storedBlanks)
                  ? (storedBlanks.length >= len ? storedBlanks : [...storedBlanks, ...Array(len - storedBlanks.length).fill("")])
                  : len === 1 ? [storedBlanks ?? ""]
                  : Array.from({ length: len }, () => "");
                const next = cur.map((v, idx) => (idx === inputIdx ? newVal : v ?? ""));
                if (fillSpec) {
                  // interactiveValueOf: editing a text box must not drop the drawn outline.
                  const keepInteractive = interactiveValueOf(stored && typeof stored === "object" && !Array.isArray(stored) ? stored.interactive : []);
                  return { blanks: next, interactive: keepInteractive };
                }
                return len === 1 ? next[0] : next;
              });
            }}
          />
          {fillSpec ? (
            <Box sx={{ mt: 2 }}>
              {interactiveHint(fillSpec) ? (
                <Typography variant="caption" color="primary.main" sx={{ display: "block", mb: 0.75, fontWeight: 600 }}>
                  {interactiveHint(fillSpec)}
                </Typography>
              ) : null}
              <InteractiveDiagramCanvas
                spec={fillSpec}
                imageUrl={fillSpec.image_url || interactiveImageOf(sub)}
                maxWidth={480}
                value={fillInteractive}
                onChange={(v) => onSubAnswerChange(sub.id, { blanks: values, interactive: v })}
              />
            </Box>
          ) : null}
          {isPracticeMode ? (
            <Box sx={{ mt: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<FactCheckIcon />}
                onClick={() => onVerify(sub)}
                disabled={verifyLoading}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                {verifyLoading ? "Checking…" : "Check answer"}
              </Button>
            </Box>
          ) : null}
          {renderVerifyResult(sub.id)}
        </>
      );
    }

    if (isTextQuestionType(sub)) {
      const compoundUnits = showAnswerUnit ? (sub.answer_data?.answer_compound_units ?? null) : null;

      if (Array.isArray(compoundUnits) && compoundUnits.length > 1) {
        const parts = compoundUnits.map((unit) => {
          const m = String(subAnswer ?? '').match(new RegExp(`(\\d+)\\s+${unit}`, 'i'));
          return m ? m[1] : '';
        });
        const combineAnswer = (vals) => vals.map((v, i) => `${v} ${compoundUnits[i]}`).join(' ');
        return (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                {compoundUnits.map((unit, i) => (
                  <Box key={unit} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <TextField
                      size="small"
                      sx={{ width: '7ch' }}
                      inputProps={{ inputMode: 'numeric', style: { textAlign: 'center' } }}
                      inputRef={(el) => { compoundBlankRefs.current[i] = el; }}
                      value={parts[i] ?? ''}
                      onFocus={() => setActiveCompoundIdx(i)}
                      onChange={(e) => {
                        const next = [...parts];
                        next[i] = e.target.value;
                        onSubAnswerChange(sub.id, combineAnswer(next));
                      }}
                    />
                    <Typography sx={{ fontWeight: 700, fontSize: '1rem', userSelect: 'none' }}>
                      {unit}
                    </Typography>
                  </Box>
                ))}
              </Box>
              {isPracticeMode && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FactCheckIcon />}
                  onClick={() => onVerify(sub)}
                  disabled={verifyLoading}
                  sx={{ textTransform: 'none', fontWeight: 700, flexShrink: 0 }}
                >
                  {verifyLoading ? 'Checking…' : 'Check answer'}
                </Button>
              )}
            </Box>
            {showMathTools ? (
              <MathInputTools
                getInput={() => compoundBlankRefs.current[activeCompoundIdx] ?? null}
                getValue={() => parts[activeCompoundIdx] ?? ''}
                onChange={(newVal) => {
                  const next = [...parts];
                  next[activeCompoundIdx] = newVal;
                  onSubAnswerChange(sub.id, combineAnswer(next));
                }}
              />
            ) : null}
            {renderVerifyResult(sub.id)}
          </>
        );
      }

      const answerUnit = showAnswerUnit ? (sub.answer_data?.answer_unit || undefined) : undefined;
      const isUnitPrefix = sub.answer_data?.answer_unit_prefix === true;
      // English papers: short-answer responses are written prose, so drop the compact answer-length
      // cap to give a full-width single-line box (maths keeps the compact sized numeric box).
      const englishRoomy = isEnglishPaper && !isEssayType(sub);
      return (
        <>
          <TypeDrawInput
            key={sub.id}
            answer={subAnswer ?? ""}
            onChange={(value) => onSubAnswerChange(sub.id, value)}
            multiline={isEssayType(sub)}
            rows={isEssayType(sub) ? 6 : 3}
            label={isEssayType(sub) ? "Your essay" : "Your answer"}
            questionIdx={questionIdx}
            marks={Number(sub.marks ?? sub.default_marks ?? 1) || 1}
            allowDraw={allowsDrawAnswer(sub)}
            defaultTab={isDrawingType(sub) ? 1 : 0}
            measurementMode={showMathTools ? measurementModeForSub(question, sub) : null}
            showMathTools={showMathTools}
            pendingFloatImage={sub.id === pendingFloatSubId ? pendingFloatImage : undefined}
            onFloatImageConsumed={sub.id === pendingFloatSubId ? onFloatImageConsumed : undefined}
            questionImages={collectAllQuestionImageEntries({ ...question, sub_questions: [sub] })}
            unitSuffix={answerUnit && !isUnitPrefix ? answerUnit : undefined}
            unitPrefix={answerUnit && isUnitPrefix ? answerUnit : undefined}
            answerLength={(isEssayType(sub) || englishRoomy) ? undefined : (Number(sub.answer_data?.answer_length) || undefined)}
            checkAnswerButton={
              isPracticeMode ? (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FactCheckIcon />}
                  onClick={() => onVerify(sub)}
                  disabled={verifyLoading}
                  sx={{ textTransform: "none", fontWeight: 700 }}
                >
                  {verifyLoading ? "Checking…" : "Check answer"}
                </Button>
              ) : null
            }
          />
          {renderVerifyResult(sub.id)}
        </>
      );
    }

    if (isMatchingType(sub)) {
      return (
        <>
          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend">Match each item to its pair</FormLabel>
            <MatchingInput
              options={sub.options || []}
              matchOptions={sub.match_options || []}
              value={subAnswer}
              onChange={(next) => onSubAnswerChange(sub.id, next)}
            />
            {isPracticeMode ? (
              <Box sx={{ mt: 1 }}>
                <Button variant="outlined" startIcon={<FactCheckIcon />} onClick={() => onVerify(sub)} disabled={verifyLoading} sx={{ textTransform: "none", fontWeight: 700 }}>
                  {verifyLoading ? "Checking…" : "Check answer"}
                </Button>
              </Box>
            ) : null}
          </FormControl>
          {renderVerifyResult(sub.id)}
        </>
      );
    }

    if (isOrderingType(sub)) {
      return (
        <>
          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend">Put the items in the correct order</FormLabel>
            <OrderingInput
              options={sub.options || []}
              value={subAnswer}
              onChange={(next) => onSubAnswerChange(sub.id, next)}
            />
            {isPracticeMode ? (
              <Box sx={{ mt: 1 }}>
                <Button variant="outlined" startIcon={<FactCheckIcon />} onClick={() => onVerify(sub)} disabled={verifyLoading} sx={{ textTransform: "none", fontWeight: 700 }}>
                  {verifyLoading ? "Checking…" : "Check answer"}
                </Button>
              </Box>
            ) : null}
          </FormControl>
          {renderVerifyResult(sub.id)}
        </>
      );
    }

    if (isMultiSelectType(sub) && sub.options?.length > 0) {
      const selected = Array.isArray(subAnswer) ? subAnswer : [];
      return (
        <>
          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend">Select your answer(s)</FormLabel>
            <FormGroup sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))", gap: 0.5 }}>
              {sub.options.map((option, oi) => {
                const optVal = option.id ?? `q${sub.id}_o${oi}`;
                const checked = selected.includes(optVal);
                return (
                  <FormControlLabel
                    key={optVal}
                    control={<Checkbox checked={checked} onChange={() => onSubAnswerChange(sub.id, (stored) => {
                      const cur = Array.isArray(stored) ? stored : [];
                      return cur.includes(optVal) ? cur.filter((v) => v !== optVal) : [...cur, optVal];
                    })} />}
                    label={
                      <Box sx={{ display: "flex", flexDirection: option.image_url ? "column" : "row", alignItems: "flex-start", gap: 1 }}>
                        {option.option_text && <HtmlWithLatex html={String(option.option_text)} style={optionRichHtmlSx} />}
                        {option.image_url && (
                          <img
                            src={option.image_url}
                            alt={option.option_text || `Option ${oi + 1}`}
                            style={{ maxWidth: "100%", maxHeight: 110, objectFit: "contain", borderRadius: 4 }}
                            onError={(e) => { e.target.style.display = "none"; }}
                          />
                        )}
                      </Box>
                    }
                    sx={{ p: 0.75, borderRadius: 1, m: 0, width: "100%", alignItems: "center", border: "1px solid", borderColor: checked ? "primary.main" : "divider", bgcolor: checked ? "action.selected" : "transparent", "& .MuiFormControlLabel-label": { width: "100%", "& p": { m: 0 } }, "&:hover": { backgroundColor: "action.hover" } }}
                  />
                );
              })}
            </FormGroup>
            {isPracticeMode ? (
              <Box sx={{ mt: 1 }}>
                <Button variant="outlined" startIcon={<FactCheckIcon />} onClick={() => onVerify(sub)} disabled={verifyLoading} sx={{ textTransform: "none", fontWeight: 700 }}>
                  {verifyLoading ? "Checking…" : "Check answer"}
                </Button>
              </Box>
            ) : null}
          </FormControl>
          {renderVerifyResult(sub.id)}
        </>
      );
    }

    if (sub.options?.length > 0) {
      return (
        <>
          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend">Select your answer</FormLabel>
            <RadioGroup value={subAnswer || ""} onChange={(e) => onSubAnswerChange(sub.id, e.target.value)}>
              {sub.options.map((option, oi) => (
                <FormControlLabel
                  key={option.id ?? `q${sub.id}_o${oi}`}
                  value={option.id ?? `q${sub.id}_o${oi}`}
                  control={<Radio />}
                  label={
                    <Box sx={{ display: "flex", flexDirection: option.image_url ? "column" : "row", alignItems: "flex-start", gap: 1 }}>
                      {option.option_text && <HtmlWithLatex html={String(option.option_text)} style={optionRichHtmlSx} />}
                      {option.image_url && (
                        <img
                          src={option.image_url}
                          alt={option.option_text || `Option ${oi + 1}`}
                          style={{ maxWidth: "100%", maxHeight: 110, objectFit: "contain", borderRadius: 4 }}
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                      )}
                    </Box>
                  }
                  sx={{ mb: 1, p: 1, borderRadius: 1, m: 0, width: "100%", alignItems: "center", "& .MuiFormControlLabel-label": { width: "100%", "& p": { m: 0 } }, "&:hover": { backgroundColor: "action.hover" } }}
                />
              ))}
            </RadioGroup>
            {isPracticeMode ? (
              <Box sx={{ mt: 1 }}>
                <Button variant="outlined" startIcon={<FactCheckIcon />} onClick={() => onVerify(sub)} disabled={verifyLoading} sx={{ textTransform: "none", fontWeight: 700 }}>
                  {verifyLoading ? "Checking…" : "Check answer"}
                </Button>
              </Box>
            ) : null}
          </FormControl>
          {renderVerifyResult(sub.id)}
        </>
      );
    }

    if (isImageFill(sub)) {
      const overlay = sub.answer_data?.overlay_layout;
      const boxCount = Array.isArray(overlay?.blanks) ? overlay.blanks.length : 0;
      // Combined part: typed boxes on one image PLUS a drawing/shade answer on a second image (e.g.
      // fill the table cells AND shade the bar for the missing match). The sub answer is then an
      // object { blanks: [...], interactive: [...] } so both surfaces travel together.
      const combinedSpec = sub.answer_data?.interactive || null;
      const rawBlanks = combinedSpec
        ? (subAnswer && typeof subAnswer === "object" && !Array.isArray(subAnswer) ? subAnswer.blanks : subAnswer)
        : subAnswer;
      const combinedInteractive = combinedSpec
        ? interactiveValueOf(subAnswer && typeof subAnswer === "object" && !Array.isArray(subAnswer) ? subAnswer.interactive : [])
        : [];
      const values = Array.isArray(rawBlanks)
        ? rawBlanks
        : boxCount === 1 ? [rawBlanks ?? ""]
        : Array.from({ length: boxCount }, () => "");
      const setBlanks = (next) => onSubAnswerChange(sub.id, combinedSpec
        ? { blanks: next, interactive: combinedInteractive }
        : (boxCount === 1 ? next[0] : next));
      const setInteractive = (v) => onSubAnswerChange(sub.id, { blanks: values, interactive: v });
      const subStem = String(sub.question_text || "").replace(/<[^>]*>/g, "").replace(/&nbsp;/gi, " ").trim();
      const subMarksForDisplay = subMarksLabel || (() => {
        const m = Number(sub.marks ?? sub.default_marks ?? 1) || 1;
        return `${m} mark${m !== 1 ? "s" : ""}`;
      })();
      return (
        <>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: subStem ? 0.5 : 1.5 }}>
            <Typography
              component="span"
              variant="body2"
              sx={{ color: "text.secondary", fontWeight: 700, fontSize: "0.8rem", bgcolor: "grey.100", px: 0.75, py: 0.25, borderRadius: 0.5 }}
            >
              ({subMarksForDisplay})
            </Typography>
          </Box>
          {subStem ? (
            <Box sx={{ mb: 1.5 }}>
              <HtmlWithLatex html={String(sub.question_text)} style={questionRichHtmlSx} />
            </Box>
          ) : null}
          {(() => {
            // Image-fill subs skip QuestionPaperLayout (getBlankTemplate is always non-empty), so any
            // attached figure a part reads from (a map, a clue-scroll image) would never show. Render
            // them here as context, minus the overlay figure itself when the overlay IS that figure.
            const ctx = imageFillContextImages(sub);
            return ctx.length > 0 ? (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
                {ctx.map((src, i) => (
                  <Box key={i} component="img" src={src} alt="" onError={(e) => { e.target.style.display = "none"; }} sx={{ width: "auto", maxWidth: "min(100%, 640px)", maxHeight: 360, objectFit: "contain", borderRadius: 1 }} />
                ))}
              </Box>
            ) : null;
          })()}
          <ImageOverlayFillInput
            overlayLayout={overlay}
            values={values}
            prefilled={sub.answer_data?.blank_labels}
            showMathTools={showMathTools}
            onChange={setBlanks}
          />
          {combinedSpec ? (
            <Box sx={{ mt: 2 }}>
              {interactiveHint(combinedSpec) ? (
                <Typography variant="caption" color="primary.main" sx={{ display: "block", mb: 0.75, fontWeight: 600 }}>
                  {interactiveHint(combinedSpec)}
                </Typography>
              ) : null}
              <InteractiveDiagramCanvas
                spec={combinedSpec}
                imageUrl={combinedSpec.image_url || interactiveImageOf(sub)}
                maxWidth={480}
                value={combinedInteractive}
                onChange={setInteractive}
              />
            </Box>
          ) : null}
          {isPracticeMode ? (
            <Box sx={{ mt: 1 }}>
              <Button variant="outlined" startIcon={<FactCheckIcon />} onClick={() => onVerify(sub)} disabled={verifyLoading} sx={{ textTransform: "none", fontWeight: 700 }}>
                {verifyLoading ? "Checking…" : "Check answer"}
              </Button>
            </Box>
          ) : null}
          {renderVerifyResult(sub.id)}
        </>
      );
    }

    // No options and type not recognized — render a text answer input as fallback
    return (
      <>
        <TypeDrawInput
          key={sub.id}
          answer={subAnswer ?? ""}
          onChange={(value) => onSubAnswerChange(sub.id, value)}
          multiline={false}
          rows={3}
          label="Your answer"
          answerLength={Number(sub.answer_data?.answer_length) || undefined}
          questionIdx={questionIdx}
          marks={Number(sub.marks ?? sub.default_marks ?? 1) || 1}
          allowDraw={allowsDrawAnswer(sub)}
          defaultTab={isDrawingType(sub) ? 1 : 0}
          measurementMode={showMathTools ? measurementModeForSub(question, sub) : null}
          showMathTools={showMathTools}
          pendingFloatImage={sub.id === pendingFloatSubId ? pendingFloatImage : undefined}
          onFloatImageConsumed={sub.id === pendingFloatSubId ? onFloatImageConsumed : undefined}
          questionImages={collectAllQuestionImageEntries({ ...question, sub_questions: [sub] })}
          checkAnswerButton={
            isPracticeMode ? (
              <Button variant="outlined" size="small" startIcon={<FactCheckIcon />}
                onClick={() => onVerify(sub)} disabled={verifyLoading}
                sx={{ textTransform: "none", fontWeight: 700 }}>
                {verifyLoading ? "Checking…" : "Check answer"}
              </Button>
            ) : null
          }
        />
        {renderVerifyResult(sub.id)}
      </>
    );
  };

  // When parts are expanded and each carries the shared diagram as its interactive grid, drop that
  // same image from the parent stem so the grid isn't painted twice (once as stem, once per canvas).
  // Expanded layout stacks every part, so a drawing part's canvas above a text part still shows the
  // figure — same approach as StudentMockExamTake.
  //
  // Only the image the canvas itself paints is dropped. The shared stem figure is usually a DIFFERENT
  // picture the part is answered FROM (the printed tile to rotate, the chart to read), so hiding every
  // stem image whenever the current tab is a drawing left those parts unanswerable. A part whose canvas
  // really is a re-upload of the stem figure under another filename suppresses it with the authored
  // per-sub hide_shared_context flag.
  const imgFileKey = (u) => String(u || "").split("?")[0].split("/").pop();
  const drawingImgKeys = new Set(
    subQuestions.filter((s) => isInteractiveDrawing(s)).map((s) => imgFileKey(interactiveImageOf(s))).filter(Boolean)
  );
  const currentInteractiveKey = useTabNav && currentSub && isInteractiveDrawing(currentSub)
    ? imgFileKey(interactiveImageOf(currentSub))
    : null;
  const subInteractiveImgKeys = new Set(useTabNav ? [] : drawingImgKeys);
  if (currentInteractiveKey) subInteractiveImgKeys.add(currentInteractiveKey);
  const stemImages = collectQuestionImages(question).filter((src) => !subInteractiveImgKeys.has(imgFileKey(src)));
  const stemMeasureMode = showMathTools ? getMeasurementMode(question) : null;

  // For comprehension questions, passage is never shown inline — only in the side panel when passageOpen=true
  const hideInlineStem = isComprehensionMode;

  const stemBlock = !hideInlineStem && (stemVisibleText.length > 0 || stemImages.length > 0) ? (
    <Box sx={{ p: 2, bgcolor: "grey.50", borderRadius: 1, border: "1px solid", borderColor: "grey.300" }}>
      {stemText ? (
        <Box sx={{ fontSize: "1rem", lineHeight: 1.7 }}>
          <HtmlWithLatex html={stemText} style={questionRichHtmlSx} />
        </Box>
      ) : null}
      {stemImages.length > 0 && stemMeasureMode ? (
        <Box sx={{ mt: stemText ? 1 : 0 }}>
          <MeasurementWorkspace images={stemImages} measurementMode={stemMeasureMode} pxPerCm={getPxPerCm(question)} />
        </Box>
      ) : stemImages.length > 0 ? (
        <Box sx={{ mt: stemText ? 1 : 0, display: "flex", flexWrap: "wrap", gap: 1 }}>
          {stemImages.map((src, i) => (
            <Box key={i} component="img" src={src} alt="" onError={(e) => { e.target.style.display = "none"; }} sx={{ width: "auto", maxWidth: "min(100%, 640px)", maxHeight: 360, objectFit: "contain", borderRadius: 1 }} />
          ))}
        </Box>
      ) : null}
    </Box>
  ) : null;

  // ONE grid for the whole group: the canvas takes the selected part's tool (plot / join / mark / lines)
  // while every other part's committed work stays on the same grid in that part's own colour, so each
  // sub-question's marks stay identifiable. Answers and "Check answer" remain strictly per part.
  if (!isComprehensionMode && sharedDraws.length > 0) {
    const colorOf = (subId) => {
      const i = sharedDraws.findIndex((s) => s.id === subId);
      return SHARED_GRID_COLORS[(i < 0 ? 0 : i) % SHARED_GRID_COLORS.length];
    };
    const labelOfSub = (sub) => toSubLabel(subQuestions.findIndex((s) => s.id === sub.id), sub);
    const activeSub = sharedDraws.find((s) => s.id === activePartId) || sharedDraws[0];
    const baseSpec = interactiveSpecOf(activeSub);
    const carried = sharedGridOtherPoints(sharedDraws, safeAnswers, activeSub, colorOf);
    const activeSpec = carried.length
      ? { ...baseSpec, context_points: [...(baseSpec.context_points || []), ...carried] }
      : baseSpec;
    const contextLayers = sharedDraws
      .filter((s) => s.id !== activeSub.id)
      .map((s) => sharedGridLayer(s, safeAnswers[s.id], colorOf(s.id), labelOfSub(s)))
      .filter(Boolean);
    const activeColor = colorOf(activeSub.id);
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {stemBlock}
        <Paper sx={{ p: 2, borderRadius: 2, border: "1px solid #e0e0e0", bgcolor: "#fafafa", overflow: "hidden", maxWidth: "100%", minWidth: 0 }}>
          <Typography variant="caption" sx={{ display: "block", mb: 0.75, fontWeight: 700, color: activeColor }}>
            Drawing part ({labelOfSub(activeSub)}) — tap a part below to switch
          </Typography>
          {interactiveHint(activeSpec) ? (
            <Typography variant="caption" color="primary.main" sx={{ display: "block", mb: 0.75, fontWeight: 600 }}>
              {interactiveHint(activeSpec)}
            </Typography>
          ) : null}
          <InteractiveDiagramCanvas
            key={activeSub.id}
            spec={activeSpec}
            imageUrl={interactiveImageOf(activeSub)}
            maxWidth={520}
            value={interactiveValueOf(safeAnswers[activeSub.id])}
            contextLayers={contextLayers}
            onChange={(v) => onSubAnswerChange(
              activeSub.id,
              interactiveWrittenOf(activeSpec)
                ? { interactive: v, text: interactiveWrittenText(safeAnswers[activeSub.id]) }
                : v
            )}
          />
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 1 }}>
            {sharedDraws.map((s) => (
              <Box key={s.id} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: colorOf(s.id) }} />
                <Typography variant="caption" sx={{ fontWeight: s.id === activeSub.id ? 700 : 500 }}>
                  ({labelOfSub(s)})
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
        {subQuestions.map((sub, idx) => {
          const isDraw = sharedDraws.some((s) => s.id === sub.id);
          const isActive = isDraw && sub.id === activeSub.id;
          return (
            <Paper
              key={sub.id}
              onClick={isDraw ? () => setActivePartId(sub.id) : undefined}
              sx={{
                p: 2, borderRadius: 2, bgcolor: "#fafafa", overflow: "hidden", maxWidth: "100%", minWidth: 0,
                border: "1px solid", borderColor: isActive ? colorOf(sub.id) : "#e0e0e0",
                borderLeft: isDraw ? `4px solid ${colorOf(sub.id)}` : "1px solid #e0e0e0",
                cursor: isDraw && !isActive ? "pointer" : "default",
              }}
            >
              {!getBlankTemplate(sub).length && (
                <QuestionPaperLayout
                  hideBlankMarks
                  hideOptions
                  imageMaxHeight={300}
                  imageMaxWidth="min(100%, 540px)"
                  question={{ ...sub, question_text: extractSubCore(sub.question_text, toSubLabel(idx, sub)) }}
                  questionNumber={idx + 1}
                  showAnswers={false}
                  subLabel={toSubLabel(idx, sub)}
                  hideImage={isDraw || (isImageFill(sub) && overlayReplacesFigure(sub))}
                  marksLabel={subMarksLabel}
                />
              )}
              {isDraw && !isActive ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                  Tap to draw this part on the grid above.
                </Typography>
              ) : null}
              <Box sx={{ mt: getBlankTemplate(sub).length ? 0 : 2 }}>
                {renderSubInput(sub, safeAnswers[sub.id], idx, { hideCanvas: isDraw })}
              </Box>
            </Paper>
          );
        })}
        <ScratchPad questionIdx={questionIdx} scratchDrawingsRef={scratchDrawingsRef} marks={Number(question.marks ?? 3) || 3} />
      </Box>
    );
  }

  if (!isComprehensionMode && !useTabNav) {
    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {stemBlock}
          {subQuestions.map((sub, idx) => {
            const subM = showMathTools ? subMeasurementLayout(question, sub) : { mode: null, imgs: [], hideImage: false };
            return (
                <Paper key={sub.id} sx={{ p: 2, borderRadius: 2, border: "1px solid #e0e0e0", bgcolor: "#fafafa", overflow: "hidden", maxWidth: "100%", minWidth: 0 }}>
                  {subM.imgs.length > 0 && subM.mode ? (
                    <MeasurementWorkspace images={subM.imgs} measurementMode={subM.mode} pxPerCm={getPxPerCm(sub, question)} />
                  ) : null}
                  {!getBlankTemplate(sub).length && (
                    <QuestionPaperLayout
                      hideBlankMarks
                      hideOptions
                      imageMaxHeight={300}
                      imageMaxWidth="min(100%, 540px)"
                      question={{ ...sub, question_text: extractSubCore(sub.question_text, toSubLabel(idx, sub)) }}
                      questionNumber={idx + 1}
                      showAnswers={false}
                      subLabel={toSubLabel(idx, sub)}
                      hideImage={subM.hideImage || isInteractiveDrawing(sub) || (isImageFill(sub) && overlayReplacesFigure(sub))}
                      marksLabel={subMarksLabel}
                    />
                  )}
                  <Box sx={{ mt: getBlankTemplate(sub).length ? 0 : 2 }}>{renderSubInput(sub, safeAnswers[sub.id], idx)}</Box>
                </Paper>
            );
          })}
          <ScratchPad questionIdx={questionIdx} scratchDrawingsRef={scratchDrawingsRef} marks={Number(question.marks ?? 3) || 3} />
        </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {!currentSub?.hide_shared_context && stemBlock}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, alignItems: "center" }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mr: 0.5 }}>
          Part:
        </Typography>
        {subQuestions.map((sub, idx) => {
          const answered = groupedAnswerHasContent(safeAnswers[sub.id]);
          const isCurrent = idx === safeSubIdx;
          return (
            <Box
              key={sub.id}
              onClick={() => onSubIdxChange(idx)}
              sx={{
                width: 32,
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "50%",
                cursor: "pointer",
                fontWeight: 700,
                fontSize: "0.78rem",
                bgcolor: answered ? "#4caf50" : "#ef5350",
                color: "white",
                border: isCurrent ? "3px solid #1a1a1a" : "2px solid transparent",
              }}
            >
              {toSubLabel(idx, sub)}
            </Box>
          );
        })}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
          {subQuestions.filter((sub) => groupedAnswerHasContent(safeAnswers[sub.id])).length} / {subQuestions.length} answered
        </Typography>
      </Box>
      {currentClean ? (() => {
        const subM = showMathTools ? subMeasurementLayout(question, currentSub) : { mode: null, imgs: [], hideImage: false };
        return (
        <Paper sx={{ p: 2, borderRadius: 2, border: "2px solid", borderColor: "primary.light", bgcolor: "#fafafa", overflow: "hidden", maxWidth: "100%", minWidth: 0 }}>
          {subM.imgs.length > 0 && subM.mode ? (
            <MeasurementWorkspace images={subM.imgs} measurementMode={subM.mode} pxPerCm={getPxPerCm(currentSub, question)} />
          ) : null}
          {!getBlankTemplate(currentSub).length && (
            <QuestionPaperLayout
              hideBlankMarks
              hideOptions
              imageMaxHeight={300}
              imageMaxWidth="min(100%, 540px)"
              question={currentClean}
              questionNumber={safeSubIdx + 1}
              showAnswers={false}
              subLabel={currentLabel}
              hideImage={subM.hideImage || isInteractiveDrawing(currentSub) || (isImageFill(currentSub) && overlayReplacesFigure(currentSub))}
              marksLabel={subMarksLabel}
            />
          )}
          <Box sx={{ mt: getBlankTemplate(currentSub).length ? 0 : 2 }}>{renderSubInput(currentSub, currentSubAnswer, safeSubIdx)}</Box>
        </Paper>
        );
      })() : null}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => onSubIdxChange(Math.max(0, safeSubIdx - 1))}
          disabled={safeSubIdx === 0}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Prev part
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          Part {safeSubIdx + 1} of {subQuestions.length}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          endIcon={<ArrowForwardIcon />}
          onClick={() => onSubIdxChange(Math.min(subQuestions.length - 1, safeSubIdx + 1))}
          disabled={safeSubIdx === subQuestions.length - 1}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Next part
        </Button>
      </Box>
      <ScratchPad questionIdx={questionIdx} scratchDrawingsRef={scratchDrawingsRef} marks={Number(question.marks ?? 3) || 3} />
    </Box>
  );
};

const StudentPaperTake = () => {
  const { purchaseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const draftKey = `paper_draft_${purchaseId}`;
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [paperMeta, setPaperMeta] = useState(null);
  const [answers, setAnswers] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [incompleteNextDialog, setIncompleteNextDialog] = useState({ open: false, remaining: 0, firstUnansweredSub: 0 });
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [fetchError, setFetchError] = useState(null);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifyQuestionId, setVerifyQuestionId] = useState(null);
  const submitFnRef = useRef(async () => {});
  const [aiScoring, setAiScoring] = useState({ active: false, resultId: null, progress: 0 });
  const aiPollRef = useRef(null);
  const aiProgressRef = useRef(null);
  const [isPracticeMode, setIsPracticeMode] = useState(false);
  const [paused, setPaused] = useState(false);
  const [annotating, setAnnotating] = useState(false);
  const [annotationFloatImage, setAnnotationFloatImage] = useState(null);
  const [annotationFloatSubId, setAnnotationFloatSubId] = useState(null);
  const [showProtractor, setShowProtractor] = useState(false);
  const [showRuler, setShowRuler] = useState(false);
  const [showSetSquare, setShowSetSquare] = useState(false);
  const [showCompass, setShowCompass] = useState(false);
  const [passageOpen, setPassageOpen] = useState(false);
  const [passageFontSize, setPassageFontSize] = useState(1.0);
  const [supplementaryOpen, setSupplementaryOpen] = useState(false);
  const scratchDrawingsRef = useRef({});
  const questionDisplayRef = useRef(null);
  const [questionContextSnapshot, setQuestionContextSnapshot] = useState(null);
  const [statuses, setStatuses] = useState({});
  const [groupedSubIdx, setGroupedSubIdx] = useState(0);
  const [paletteOpen, setPaletteOpen] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth >= 1200 : true
  );
  const serverDraftRef = useRef(null);
  const pendingNavRef = useRef(null);
  const isLeavingRef = useRef(false);
  const hasSubmittedRef = useRef(false); // once true, the history/leave guard never re-arms itself
  const dummyHistoryPushedRef = useRef(false); // the leave-guard pushes one extra "buffer" history entry — collapse it when we leave for real
  const questionsInitializedRef = useRef(false); // prevents re-initialization when question data updates (edit saves)
  const prevCurrentQuestionIndexRef = useRef(-1); // tracks previous index to distinguish navigation from question-data updates
  const [flaggingInProgress, setFlaggingInProgress] = useState({});
  const EMPTY_FLAG_DIALOG = { open: false, reason: '', issue_question: false, issue_answer: false, issue_image: false };
  const isTester = localStorage.getItem("role") === "tester";
  const canVerifyQuestion = isTester && testerHasPermission("verify-question");
  const canVerifyPaper = isTester && testerHasPermission("verify-paper");
  const { loadVerifications, submitVerification, getItemData, submitting: verifySubmitting } = useContentVerification();
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [verifyQuestionFull, setVerifyQuestionFull] = useState(null);
  const [verifyQuestionLoading, setVerifyQuestionLoading] = useState(false);
  const [editFromVerifyOpen, setEditFromVerifyOpen] = useState(false);
  const [editFromVerifyQuestion, setEditFromVerifyQuestion] = useState(null);
  const [editFromVerifySaving, setEditFromVerifySaving] = useState(false);
  const [flagReasonDialog, setFlagReasonDialog] = useState(EMPTY_FLAG_DIALOG);
  const [verifyPaperDialogOpen, setVerifyPaperDialogOpen] = useState(false);
  const [verifyPaperSubmitting, setVerifyPaperSubmitting] = useState(false);
  const [showAnswerUnit, setShowAnswerUnit] = useState(true);
  const [mcqNoticeDialog, setMcqNoticeDialog] = useState({ open: false, items: [], message: "" });

  // Re-pull the questions after a tester edits one, so the change (improved stem, rewritten options,
  // new explanation) shows immediately. Only the questions array is swapped — re-running the full
  // loader would reset the timer and the draft-restore logic mid-exam.
  const refreshPaperQuestions = useCallback(async () => {
    try {
      const res = await api.get(`/student/paper/${purchaseId}/questions`);
      const fresh = res.data?.data?.questions;
      if (Array.isArray(fresh)) setQuestions(fresh);
    } catch {
      // Non-critical — the edit is saved; the student sees it on next load.
    }
  }, [purchaseId]);

  useEffect(() => {
    const handleExamModeChanged = () => window.location.reload();
    window.addEventListener("exam-mode-changed", handleExamModeChanged);
    return () => window.removeEventListener("exam-mode-changed", handleExamModeChanged);
  }, []);

  useEffect(() => {
    api.get('/student/settings')
      .then((res) => setShowAnswerUnit(res.data?.data?.show_answer_unit !== false))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!purchaseId) return;
    setPaused(sessionStorage.getItem(pauseStorageKey(purchaseId)) === "1");
  }, [purchaseId]);

  useEffect(() => {
    if (!purchaseId) return;
    sessionStorage.setItem(pauseStorageKey(purchaseId), paused ? "1" : "0");
  }, [paused, purchaseId]);

  useEffect(() => {
    const fetchPaperData = async () => {
      if (!purchaseId) return;
      setFetchError(null);
      try {
        setLoading(true);
        const response = await api.get(`/student/paper/${purchaseId}/questions`);
        if (response.data?.success && response.data?.data) {
          const { duration_minutes, total_marks, questions: qList, is_practice_mode, paper_name, paper_id, draft_answers, current_question_index: serverQIndex } = response.data.data;
          const duration = duration_minutes ?? 120;
          const practice = Boolean(is_practice_mode);
          setIsPracticeMode(practice);
          setTimeRemaining(practice ? 0 : duration * 60);
          setPaperMeta({ duration_minutes: duration, total_marks: total_marks ?? 0, paper_name: paper_name || "", paper_id: paper_id ?? null });
          serverDraftRef.current = draft_answers && Object.keys(draft_answers).length > 0
            ? { answers: draft_answers, currentQuestionIndex: serverQIndex ?? 0 }
            : null;
          const qs = Array.isArray(qList) ? qList : [];
          setQuestions(qs);

          if (response.data.data.first_open) {
            const flaggedForMcq = [];
            qs.forEach((q, idx) => {
              const qNum = String(q.paper_number || idx + 1).trim();
              if (q.mcq_recommended) flaggedForMcq.push({ label: `Q${qNum}`, note: q.mcq_recommended_note });
              (q.sub_questions || []).forEach((sub, subIdx) => {
                if (!sub.mcq_recommended) return;
                // toSubLabel yields "c)" (or the authored paper_number, which may repeat the
                // parent number as in "6c") — reduce it to the bare part letter for the notice.
                let part = toSubLabel(subIdx, sub).replace(/[).\s]+$/, "");
                if (part.startsWith(qNum)) part = part.slice(qNum.length).replace(/^[).\s]+/, "");
                flaggedForMcq.push({
                  label: part ? `Q${qNum} — part ${part}` : `Q${qNum}`,
                  note: sub.mcq_recommended_note,
                });
              });
            });
            if (flaggedForMcq.length > 0) {
              setMcqNoticeDialog({ open: true, items: flaggedForMcq, message: response.data.data.mcq_notice_message || "" });
            }
          }

          // Restore server-persisted flagged questions so palette shows correct colours on resume
          try {
            const flagRes = await api.get(`/student/paper/${purchaseId}/flagged-questions`);
            const rawIds = flagRes.data?.data?.flagged_question_ids || [];
            const toNumeric = (id) => String(id).replace(/^bank_/, '');
            const flaggedNums = new Set(rawIds.map(toNumeric));
            setStatuses((prev) => {
              const next = { ...prev };
              qs.forEach((q, idx) => {
                if (flaggedNums.has(toNumeric(q.id))) {
                  next[idx] = STATUS.FLAGGED;
                }
              });
              return next;
            });
          } catch {
            // Silently ignore — flagged state is non-critical
          }
        } else {
          setQuestions([]);
          setTimeRemaining(120 * 60);
          setPaperMeta({ duration_minutes: 120, paper_name: "" });
        }
      } catch (err) {
        const msg = err.response?.data?.message || "Failed to load exam questions.";
        // Reached this take page via browser back/forward (or a stale link) after the paper was
        // already submitted — send the student to their result instead of letting them see a
        // dead-end exam screen (or worse, retry submitting).
        if (err.response?.status === 400 && /already (completed|submitted)/i.test(msg)) {
          navigate(`/${STUDENT_BASE}/past-paper/${purchaseId}/result`, {
            replace: true,
            state: { alreadySubmitted: true },
          });
          return;
        }
        setFetchError(msg);
        setSnackbar({ open: true, message: msg, severity: "error" });
        setQuestions([]);
      } finally {
        setLoading(false);
      }
    };
    fetchPaperData();
  }, [purchaseId, navigate]);

  useEffect(() => {
    if (!questions.length) {
      setStatuses({});
      questionsInitializedRef.current = false;
      return;
    }
    // Skip re-initialization when question data is updated in-place (e.g. tester edits a question).
    // Only run on the initial load when questions first become available.
    if (questionsInitializedRef.current) return;
    questionsInitializedRef.current = true;

    const next = {};
    questions.forEach((_, index) => {
      next[index] = STATUS.NOT_VISITED;
    });

    const startFresh = location.state?.startFresh;
    if (startFresh) {
      localStorage.removeItem(draftKey);
      window.history.replaceState({}, "");
    }

    const localDraft = startFresh ? null : (() => {
      try { return JSON.parse(localStorage.getItem(draftKey)); } catch { return null; }
    })();
    const hasLocalDraft = localDraft?.answers && Object.keys(localDraft.answers).length > 0;
    const serverDraft = !startFresh && !hasLocalDraft ? serverDraftRef.current : null;
    const draft = hasLocalDraft ? localDraft : (serverDraft ?? null);

    if (draft?.answers && Object.keys(draft.answers).length > 0) {
      setAnswers(draft.answers);
      const resumeIndex = draft.currentQuestionIndex ?? 0;
      setCurrentQuestionIndex(Math.min(resumeIndex, questions.length - 1));
      questions.forEach((q, index) => {
        const ans = draft.answers[q.id];
        if (ans == null) return; // never visited — keep NOT_VISITED
        const hasAnswer = (() => {
          if (ans === '') return false;
          if (isGroupedQuestion(q) && q.sub_questions?.length) {
            if (typeof ans !== 'object' || Array.isArray(ans)) return false;
            return Object.values(ans).some((v) => v != null && v !== '');
          }
          if (typeof ans === 'object' && !Array.isArray(ans)) {
            return String(ans.text ?? '').trim() !== '' || !!ans.drawing
              || (Array.isArray(ans.interactive) && ans.interactive.length > 0);
          }
          if (Array.isArray(ans)) return ans.some((a) => a != null && String(a).trim() !== '');
          return String(ans).trim() !== '';
        })();
        next[index] = hasAnswer ? STATUS.ANSWERED : STATUS.UNANSWERED;
      });
      setStatuses(next);
    } else {
      setStatuses(next);
      setCurrentQuestionIndex(0);
    }
    // Deep link from the Verification Dashboard's unverified-question chips.
    const jumpId = location.state?.jumpToQuestionId;
    if (jumpId) {
      const target = questions.findIndex((q) => Number(q.qb_id ?? q.id) === Number(jumpId));
      if (target >= 0) setCurrentQuestionIndex(target);
      window.history.replaceState({}, "");
    }
    setGroupedSubIdx(0);
  }, [questions]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const currentQ = questions[currentQuestionIndex];
    setPassageOpen(false);
    setSupplementaryOpen(false);
    if (prevCurrentQuestionIndexRef.current !== currentQuestionIndex) {
      setGroupedSubIdx(0);
      prevCurrentQuestionIndexRef.current = currentQuestionIndex;
    }
    setVerifyResult(null);
    setVerifyQuestionId(null);
    setAnnotationFloatImage(null);
    setAnnotationFloatSubId(null);
  }, [currentQuestionIndex, questions, paperMeta?.paper_name]);

  useEffect(() => {
    setQuestionContextSnapshot(null);
    const el = questionDisplayRef.current;
    if (!el) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const { default: html2canvas } = await import('html2canvas');
        const canvas = await html2canvas(el, { scale: 1, useCORS: true, logging: false, backgroundColor: '#ffffff' });
        if (!cancelled) setQuestionContextSnapshot(canvas.toDataURL('image/jpeg', 0.9));
      } catch (_) { /* capture failed — drawing pad will save strokes only */ }
    }, 150);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [currentQuestionIndex]);

  useEffect(() => {
    // The convert-to-MCQ notice blocks the paper until acknowledged, so the clock must not run
    // behind it — the student hasn't started reading the paper yet.
    if (isPracticeMode || loading || paused || mcqNoticeDialog.open) return undefined;
    const id = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 0) return 0;
        if (prev === 1) {
          setSnackbar({ open: true, message: "Time is up! Submitting your exam...", severity: "warning" });
          setTimeout(() => submitFnRef.current?.(), 2000);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [loading, isPracticeMode, paused, mcqNoticeDialog.open]);

  // Poll server every 30s to sync admin-cleared flags to the student's exam session
  useEffect(() => {
    if (loading || submitting) return undefined;
    const id = setInterval(async () => {
      try {
        const res = await api.get(`/student/paper/${purchaseId}/flagged-questions`);
        const _toNum = (id) => String(id).replace(/^bank_/, '');
        const serverIds = new Set((res.data?.data?.flagged_question_ids || []).map(_toNum));
        setStatuses((prev) => {
          const next = { ...prev };
          questions.forEach((q, idx) => {
            if (next[idx] === STATUS.FLAGGED && !serverIds.has(_toNum(q.id))) {
              const ans = answers[q.id];
              const hasAnswer = ans != null && ans !== '' && !(Array.isArray(ans) && ans.every((v) => !v));
              next[idx] = hasAnswer ? STATUS.ANSWERED : STATUS.UNANSWERED;
            }
          });
          return next;
        });
      } catch { /* non-critical */ }
    }, 30_000);
    return () => clearInterval(id);
  }, [loading, submitting, purchaseId, questions, answers]);

  // Autosave answers to the server every 30s so resume works across devices/browsers
  useEffect(() => {
    if (loading || submitting || !purchaseId) return undefined;
    const id = setInterval(async () => {
      try {
        const sanitized = {};
        Object.entries(answers).forEach(([qId, val]) => {
          if (val && typeof val === "object" && !Array.isArray(val)) {
            // Strip base64 drawings to keep payload small; text portion still restores MC/short answers
            const { drawing, ...rest } = val;
            sanitized[qId] = rest;
          } else {
            sanitized[qId] = val;
          }
        });
        await api.post(`/student/paper/${purchaseId}/autosave`, {
          answers: sanitized,
          current_question_index: currentQuestionIndex,
        });
      } catch {} // non-critical
    }, 30_000);
    return () => clearInterval(id);
  }, [loading, submitting, purchaseId, answers, currentQuestionIndex]);

  // Navigation interception — block sidebar/back navigation during exam
  useEffect(() => {
    if (loading || submitting || aiScoring.active || hasSubmittedRef.current) return undefined;

    const origPushState = window.history.pushState.bind(window.history);

    window.history.pushState = (state, title, url) => {
      if (isLeavingRef.current) {
        isLeavingRef.current = false;
        return origPushState(state, title, url);
      }
      if (!url) return origPushState(state, title, url);

      let newPath;
      try { newPath = new URL(String(url), window.location.origin).pathname; }
      catch { newPath = String(url); }

      if (newPath === window.location.pathname) return origPushState(state, title, url);

      pendingNavRef.current = newPath;
      setLeaveDialogOpen(true);
    };

    origPushState(null, "", window.location.href);
    dummyHistoryPushedRef.current = true;
    const handlePopState = () => {
      origPushState(null, "", window.location.href);
      pendingNavRef.current = null;
      setLeaveDialogOpen(true);
    };
    window.addEventListener("popstate", handlePopState);

    const guard = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", guard);

    return () => {
      window.history.pushState = origPushState;
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("beforeunload", guard);
    };
  }, [aiScoring.active, loading, submitting]);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) return `${hours}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  };

  const currentQuestion = questions[currentQuestionIndex];
  const currentStatus = statuses[currentQuestionIndex] || STATUS.NOT_VISITED;

  // Close verify dialog when navigating to a different question
  useEffect(() => {
    if (verifyDialogOpen) {
      setVerifyDialogOpen(false);
      setVerifyQuestionFull(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestion?.id, currentQuestion?.qb_id]);

  // Preload verification state for the current question so the Verify button
  // can reflect any tester's rejection without the tester having to open it.
  useEffect(() => {
    if (!canVerifyQuestion || !currentQuestion) return;
    const bankId = currentQuestion.qb_id || currentQuestion.id;
    if (!bankId) return;
    if (getItemData("question", bankId)) return;
    loadVerifications("question", bankId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canVerifyQuestion, currentQuestion?.id, currentQuestion?.qb_id]);

  const activeMeasurementMode = (() => {
    if (!currentQuestion) return null;
    if (isGroupedQuestion(currentQuestion)) {
      const sub = (currentQuestion.sub_questions || [])[groupedSubIdx];
      if (sub) return measurementModeForSub(currentQuestion, sub);
    }
    return getMeasurementMode(currentQuestion);
  })();

  const isMathsPaper = /math/i.test(paperMeta?.paper_name || "");
  const effectiveMeasurementMode = isMathsPaper ? activeMeasurementMode : null;

  const activePxPerCm = (() => {
    if (!currentQuestion) return undefined;
    if (isGroupedQuestion(currentQuestion)) {
      const sub = (currentQuestion.sub_questions || [])[groupedSubIdx];
      return getPxPerCm(sub, currentQuestion);
    }
    return getPxPerCm(currentQuestion);
  })();

  useEffect(() => {
    setShowProtractor(false);
    setShowRuler(false);
    setShowSetSquare(false);
    setShowCompass(false);
  }, [effectiveMeasurementMode]);

  const showProtractorBtn =
    effectiveMeasurementMode === MEASUREMENT_MODES.ANGLE ||
    effectiveMeasurementMode === MEASUREMENT_MODES.CONSTRUCT;
  const showRulerBtn =
    effectiveMeasurementMode === MEASUREMENT_MODES.LENGTH ||
    effectiveMeasurementMode === MEASUREMENT_MODES.CONSTRUCT;
  const showSetSquareBtn = effectiveMeasurementMode === MEASUREMENT_MODES.CONSTRUCT;
  const showCompassBtn = effectiveMeasurementMode === MEASUREMENT_MODES.CONSTRUCT;

  // Booklet-accurate position calculator — handles comprehension grouped question ranges
  const examPositionByIndex = (() => {
    const positions = [];
    let running = 1;
    questions.forEach((q, i) => {
      if (isGroupedQuestion(q) && isComprehensionPassage(q)) {
        const len = q.sub_questions?.length || 1;
        positions[i] = { start: running, end: running + len - 1 };
        running += len;
      } else {
        positions[i] = { start: running, end: running };
        running += 1;
      }
    });
    return { positions, total: running - 1 };
  })();

  const getQuestionLabel = (question, index) => {
    const pn = question?.paper_number;
    if (pn != null && String(pn).trim() !== "" && Number(pn) !== 0) return String(pn).trim();
    const pos = examPositionByIndex.positions[index] || { start: index + 1, end: index + 1 };
    return pos.start === pos.end ? `${pos.start}` : `${pos.start}–${pos.end}`;
  };

  const updateStatusForQuestion = useCallback(
    (questionId, nextValue) => {
      const index = questions.findIndex((question) => question.id === questionId);
      if (index < 0) return;
      const question = questions[index];
      const answered = isGroupedQuestion(question)
        ? isQuestionAnswered(question, { ...answers, [questionId]: nextValue })
        : isTextQuestionType(question)
        ? !isPastPaperTextAnswerEmpty(nextValue)
        : isFillType(question)
        ? (Array.isArray(nextValue) ? nextValue.some((v) => String(v ?? "").trim() !== "") : String(nextValue ?? "").trim() !== "")
        : isMultiSelectType(question)
        ? Array.isArray(nextValue) && nextValue.length > 0
        : isMatchingType(question)
        ? isQuestionAnswered(question, { ...answers, [questionId]: nextValue })
        : nextValue != null && nextValue !== "";

      setStatuses((prev) => {
        const cur = prev[index];
        if (cur === STATUS.FLAGGED || cur === STATUS.ANSWER_LATER) return prev;
        return { ...prev, [index]: answered ? STATUS.ANSWERED : STATUS.UNANSWERED };
      });
    },
    [answers, questions]
  );

  const handleAnswerChange = (questionId, value, multiSelect = false) => {
    let nextVal;
    if (multiSelect) {
      const cur = Array.isArray(answers[questionId]) ? answers[questionId] : [];
      nextVal = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
    } else {
      nextVal = value;
    }
    setAnswers((prev) => {
      const next = { ...prev, [questionId]: nextVal };
      try { localStorage.setItem(draftKey, JSON.stringify({ answers: next, currentQuestionIndex })); } catch {}
      return next;
    });
    updateStatusForQuestion(questionId, nextVal);
    setVerifyResult(null);
    setVerifyQuestionId(null);
  };

  const handleAnnotationCapture = useCallback((base64Jpeg) => {
    if (!currentQuestion) return;
    setAnnotationFloatImage(base64Jpeg);
    setAnnotating(false);
    setSnackbar({ open: true, message: "Captured — added to your drawing answer. You can mark up on top.", severity: "info" });
  }, [currentQuestion]);

  // For grouped questions: routes the captured screenshot to the active sub-question's drawing pad as a floating image.
  const handleGroupedAnnotationCapture = useCallback((base64Jpeg) => {
    if (!currentQuestion) return;
    const sub = (currentQuestion.sub_questions || [])[groupedSubIdx];
    if (!sub) return;
    setAnnotationFloatImage(base64Jpeg);
    setAnnotationFloatSubId(sub.id);
    setAnnotating(false);
    setSnackbar({ open: true, message: "Captured — added to your drawing answer. You can mark up on top.", severity: "info" });
  }, [currentQuestion, groupedSubIdx]);

  const handleGroupedSubAnswer = (subId, valueOrUpdater) => {
    const groupId = currentQuestion?.id;
    if (!groupId) return;
    setAnswers((prev) => {
      const stored = (prev[groupId] || {})[subId];
      const nextSubVal = typeof valueOrUpdater === 'function' ? valueOrUpdater(stored) : valueOrUpdater;
      const nextGroup = { ...(prev[groupId] || {}), [subId]: nextSubVal };
      const next = { ...prev, [groupId]: nextGroup };
      try { localStorage.setItem(draftKey, JSON.stringify({ answers: next, currentQuestionIndex })); } catch {}
      updateStatusForQuestion(groupId, nextGroup);
      return next;
    });
    setVerifyResult(null);
    setVerifyQuestionId(null);
  };

  const handleVerify = async (questionOverride = null) => {
    const question = questionOverride || currentQuestion;
    if (!question) return;
    setVerifyLoading(true);
    setVerifyResult(null);
    setVerifyQuestionId(question.id);
    try {
      const payload = { question_id: question.id };
      const value = questionOverride
        ? (answers[currentQuestion?.id] || {})[question.id]
        : answers[question.id];

      if (isInteractiveDrawing(question)) {
        payload.answer_interactive = interactiveValueOf(value);
        if (interactiveWrittenOf(interactiveSpecOf(question))) {
          payload.answer_text = interactiveWrittenText(value);
        }
      } else if (isTextQuestionType(question)) {
        if (typeof value === "string") payload.answer_text = normalizeMathSymbols(value.trim());
        else if (value && typeof value === "object" && !Array.isArray(value)) {
          payload.answer_text = normalizeMathSymbols(typeof value.text === "string" ? value.text.trim() : "");
          const rawDrawing = value.drawing && String(value.drawing).startsWith("data:image/") ? value.drawing : null;
          if (rawDrawing) {
            const withContext = (!value.diagramOnCanvas && questionContextSnapshot && !skipContextForDrawing(question))
              ? await compositeWithContext(rawDrawing, questionContextSnapshot)
              : rawDrawing;
            payload.answer_drawing = await compressDrawingForVerify(withContext, 1200);
          }
        }
        else payload.answer_text = "";
      } else if (isFillType(question) || isImageFill(question)) {
        if (value && typeof value === "object" && !Array.isArray(value) && "interactive" in value) {
          // Combined image-fill + drawing part: typed boxes AND a shaded diagram.
          const blanks = Array.isArray(value.blanks) ? value.blanks : [];
          payload.answer_text = blanks.join(" | ").trim();
          // interactiveValueOf: the grader reads the drawn outline from submitted.edges, so the bare
          // point array would submit a closed shape with no outline and mark it wrong.
          payload.answer_interactive = interactiveValueOf(value.interactive);
        } else if (Array.isArray(value)) {
          payload.answer_text = value.join(" | ").trim();
        } else if (value && typeof value === "object" && !Array.isArray(value)) {
          // TypeDrawInput object — happens when blank template was missing so the question
          // fell through to free-text rendering instead of inline blank boxes.
          payload.answer_text = typeof value.text === "string" ? value.text.trim() : "";
          const rawDrawing = value.drawing && String(value.drawing).startsWith("data:image/") ? value.drawing : null;
          if (rawDrawing) {
            // Skip context composite for fill-type — the snapshot includes "choose from list"
            // number banks that confuse AI Vision into misreading the typed answer.
            payload.answer_drawing = await compressDrawingForVerify(rawDrawing, 800);
          }
        } else {
          payload.answer_text = String(value ?? "").trim();
        }
      } else {
        if (value && typeof value === "object" && !Array.isArray(value) && (value.drawing != null || value.text != null)) {
          payload.answer_text = typeof value.text === "string" ? value.text.trim() : "";
          const rawDrawing = value.drawing && String(value.drawing).startsWith("data:image/") ? value.drawing : null;
          if (rawDrawing) {
            const withContext = (!value.diagramOnCanvas && questionContextSnapshot && !skipContextForDrawing(question))
              ? await compositeWithContext(rawDrawing, questionContextSnapshot)
              : rawDrawing;
            payload.answer_drawing = await compressDrawingForVerify(withContext, 1200);
          }
        } else if (isMatchingType(question)) {
          payload.answer_matching = matchingAnswerOf(value);
        } else if (isOrderingType(question)) {
          payload.answer_text = orderingAnswerToText(question.options, value);
        } else if (isMultiSelectType(question) && Array.isArray(value) && value.length > 0) {
          payload.option_ids = value;
        } else {
          payload.option_id = value || null;
        }
      }

      const response = await api.post(`/student/paper/${purchaseId}/verify-answers`, payload);
      const data = response.data?.data ?? response.data;
      const submittedAnswer = String(data?.submitted_answer ?? payload.answer_text ?? "").trim();
      const submittedDrawing = data?.submitted_drawing || payload.answer_drawing || null;
      const wasAnswered = !!submittedDrawing || submittedAnswer !== "" || !!payload.option_id || (Array.isArray(payload.option_ids) && payload.option_ids.length > 0) || interactiveHasContent(payload.answer_interactive);
      if (data?.is_correct === null) {
        const hasDrawing = !!submittedDrawing;
        const correctText = String(data?.correct_answer ?? "").trim();
        // For drawings where AI evaluation was not performed, never resolve as correct client-side —
        // the final score comes from the AI pass at submit time and may differ.
        const normSub = normalizeMathSymbols(submittedAnswer);
        const typedCorrect = !hasDrawing && submittedAnswer !== "" && correctText !== ""
          && !submissionIsDeterministicallyWrong(normSub, correctText)
          && (isTypedVerifyCorrect(normSub, correctText, question) || isShortValueInProseAnswer(normSub, correctText));
        setVerifyResult({
          question_id: question.id,
          is_correct: hasDrawing ? null : (typedCorrect ? true : false),
          is_partial: false,
          was_answered: wasAnswered,
          hint: hasDrawing
            ? "Drawing saved — will be submitted with your answer."
            : typedCorrect
            ? "Correct! ✓"
            : (!hasDrawing && submittedAnswer !== "" && correctText !== "")
            ? "Incorrect — see correct answer below."
            : (!hasDrawing && correctText !== "")
            ? (data?.hint || "See the correct answer below.")
            : (data?.hint || "Written answers are not auto-verified in practice."),
          correct_answer: data?.correct_answer || null,
          correct_option_image: data?.correct_option_image || null,
          submitted_answer: submittedAnswer || null,
          submitted_drawing: submittedDrawing || null,
          correct_drawing: data?.correct_drawing || data?.correct_solution_image_url || null,
          correct_interactive: data?.correct_interactive || null,
          ai_evaluated: !!data?.ai_evaluated,
        });
      } else {
        // For fill-type questions: if the backend says correct but the student left blanks empty,
        // downgrade to partially correct (backend may only store one blank's answer).
        let isCorrectFinal = !!data?.is_correct;
        let isPartialFinal = !!data?.is_partial;
        // True when this downgrade fired, so the backend's "Correct! ✓" hint isn't printed over an
        // amber "partially correct" panel.
        let downgradedFromCorrect = false;
        if (isCorrectFinal && (isFillType(question) || isImageFill(question))) {
          const blankTemplate = getBlankTemplate(question);
          // Box-mode image-fill can have intentionally-empty boxes (correct_blanks[i] = []), e.g. a Venn
          // diagram where only two regions take a number. Only boxes that carry a correct value are
          // required, so compare against that count — not the total box count.
          // A "must stay empty" box is sometimes authored as an &nbsp; / markup placeholder rather than an
          // empty list, and the raw string "&nbsp;" is not blank — so every box counted as required and a
          // fully-correct answer was downgraded to "partially correct". Decode before deciding, and prefer
          // the count the server sends (required_blank_count), which applies the same rule as the grader.
          const isBlankRequired = (cb) => {
            const clean = (v) => decodeHtmlEntities(stripHtmlTags(String(v ?? "").replace(/&nbsp;/gi, " "))).trim();
            return Array.isArray(cb) ? cb.some((v) => clean(v) !== "") : clean(cb) !== "";
          };
          const serverRequired = Number(question?.answer_data?.required_blank_count);
          const requiredCount = Number.isFinite(serverRequired) && serverRequired > 0
            ? serverRequired
            : (blankTemplate.filter(isBlankRequired).length || blankTemplate.length);
          // A combined fill+drawing part stores { blanks, interactive } — the typed answers live in
          // value.blanks, not value itself. Pull those out so the filled-count check works.
          const blankValues = value && typeof value === "object" && !Array.isArray(value) && Array.isArray(value.blanks)
            ? value.blanks
            : value;
          const filledBlanks = Array.isArray(blankValues) ? blankValues.filter((v) => String(v ?? "").trim() !== "") : [blankValues].filter((v) => String(v ?? "").trim() !== "");
          if (requiredCount > 1 && filledBlanks.length < requiredCount) {
            isCorrectFinal = false;
            isPartialFinal = true;
            downgradedFromCorrect = true;
          }
        }
        // Drawing-only answers must not show "Correct!" without AI Vision.
        // A rule-based "correct" when both submitted text and correct_answer_text are empty
        // is a false positive — downgrade so the student is shown "compare your drawing."
        const hasDrawing = !!submittedDrawing;
        if (isCorrectFinal && !data?.ai_evaluated && hasDrawing && submittedAnswer === '') {
          isCorrectFinal = false;
        }
        // Multi-blank fills are graded positionally by the backend (blank i ↔ correct[i]).
        // The client-side fallback below uses order-independent multi-value matching, which would
        // wrongly accept swapped answers (e.g. First/Second Pattern entered in the wrong boxes) —
        // so trust the backend's positional decision for these.
        const isMultiBlankFill = (isFillType(question) || isImageFill(question)) && getBlankTemplate(question).length > 1;
        // Client-side fallback: backend may return false when correct_answer is verbose AI prose
        // and the student's short typed answer is the value embedded within it (e.g. "£79" in
        // "Sam needs £79 more…"). Also handles ÷ vs / symbol mismatch after normalization.
        // Ordering is excluded: the backend grades it position-by-position, and the client's
        // order-independent matching would wrongly upgrade a right-set-wrong-order submission.
        if (!isCorrectFinal && !isInteractiveDrawing(question) && !isOrderingType(question) && !isMultiBlankFill && !isMultiSelectType(question) && !data?.ai_evaluated && !hasDrawing && submittedAnswer !== '' && data?.correct_answer) {
          const normSub = normalizeMathSymbols(submittedAnswer);
          const correctStr = String(data.correct_answer);
          // isShortValueInProseAnswer matches any token-run inside the mark-scheme sentence, so an
          // unfinished answer ("4 and") is found verbatim in "between 4 and 8 …" and upgraded over the
          // backend's correct "incorrect". Deterministically-wrong submissions never get the upgrade.
          if (!submissionIsDeterministicallyWrong(normSub, correctStr)
            && (isTypedVerifyCorrect(normSub, correctStr, question) || isShortValueInProseAnswer(normSub, correctStr))) {
            isCorrectFinal = true;
            isPartialFinal = false;
          }
        }
        // Clock-time questions are graded strictly rule-based: a pure time mark scheme (e.g.
        // "3:00 or 9:00") must match the colon format exactly. The AI grader is inconsistent on
        // equivalent forms (accepting 3.00 but not 3.0), so for these we override its verdict —
        // only an exact time match (3:00 / 9:00) is correct; 3.00, 3.0, 3, "3 o'clock" are not.
        // Multi-blank fills stay positional here too — otherwise a swapped pair of times (blank i's
        // answer in blank j) would match order-independently and override the backend's correct
        // "incorrect" verdict.
        if (!isInteractiveDrawing(question) && !hasDrawing && submittedAnswer !== '' && isPureClockTimeAnswer(data?.correct_answer)) {
          isCorrectFinal = isAnswerCorrect(normalizeMathSymbols(submittedAnswer), String(data.correct_answer), { ordered: isMultiBlankFill });
          isPartialFinal = false;
        }
        // Fraction mark schemes are graded deterministically by value — AI Vision is unreliable here
        // (it accepted 66/80 for 33/80). Override the AI verdict: correct only when the submitted value
        // equals the fraction (so an equivalent fraction like 66/160 = 33/80 still passes, but 66/80
        // does not). A submission that isn't a number/fraction is incorrect.
        // Exception: when the question asks for the SIMPLEST form, reducing the fraction is the task —
        // an unreduced equivalent (6/8 for 3/4) has the right value but is the wrong answer, so this
        // value-equality tier must not upgrade it over the backend's "incorrect" verdict. Nor is a
        // DECIMAL (0.75) the fraction in its simplest form: fractionIsInSimplestForm passes anything
        // that isn't a fraction at all, so the submitted form is checked against the mark scheme's.
        // Same for "What fraction of …?": writing it AS a fraction is the task, so the equal-valued
        // decimal ("0.25" for "1/4") is the wrong form — this tier compares by VALUE, so without the
        // guard it re-accepts it here no matter what the server decided.
        if (!isInteractiveDrawing(question) && !hasDrawing && submittedAnswer !== '' && isBareFractionAnswer(data?.correct_answer)) {
          const cv = numericAnswerValue(data?.correct_answer);
          const sv = numericAnswerValue(submittedAnswer);
          if (cv !== null) {
            const valueMatches = sv !== null && Math.abs(sv - cv) <= 1e-6 * Math.max(1, Math.abs(cv));
            const wantsSimplest = questionAsksForSimplestForm(question);
            const wantsFraction = questionAsksForFractionForm(question);
            isCorrectFinal = valueMatches
              && !(wantsSimplest && !fractionIsInSimplestForm(submittedAnswer))
              && !(wantsSimplest && !valueIsFraction(submittedAnswer))
              && !(wantsFraction && !valueIsFraction(submittedAnswer));
            isPartialFinal = false;
          }
        }
        // "What calculation did you do?" mark schemes are arithmetic expressions ("(30 × 10) ÷ 50").
        // AI Vision accepts a dropped operator ("(30 10) ÷ 50") as correct — it doesn't check the full
        // answer. Override deterministically: correct only when the submitted expression has the same
        // operators and operands. Never upgrades — a matching structure just leaves the verdict alone.
        if (!isInteractiveDrawing(question) && !isOrderingType(question) && !isMultiSelectType(question) && !hasDrawing && (isCorrectFinal || isPartialFinal)) {
          const arithmeticCorrectBlanks = Array.isArray(question?.answer_data?.correct_blanks)
            ? question.answer_data.correct_blanks
            : null;
          const arithmeticBlankValues = value && typeof value === "object" && !Array.isArray(value) && Array.isArray(value.blanks)
            ? value.blanks
            : value;
          if (isMultiBlankFill && arithmeticCorrectBlanks && Array.isArray(arithmeticBlankValues)) {
            // Pooled blanks ("the rows may be in any order" / interchangeable boxes) are NOT bound to a
            // position, so blank i must be checked against EVERY blank's alternatives — comparing it to
            // correct_blanks[i] alone would fail a right answer written in a different order.
            const arithmeticOverlay = question?.answer_data?.overlay_layout;
            const arithmeticPooled = !!(arithmeticOverlay?.blanks_interchangeable
              || arithmeticOverlay?.rows_interchangeable
              || (Array.isArray(arithmeticOverlay?.interchangeable_row_indices) && arithmeticOverlay.interchangeable_row_indices.length));
            const altsOf = (cb) => (Array.isArray(cb) ? cb : [cb]).map((a) => String(a ?? "").trim()).filter(Boolean);
            const pooledAlts = arithmeticPooled ? arithmeticCorrectBlanks.flatMap(altsOf) : null;
            // A fill's `correct_answer` is a DISPLAY string, not a value list, so the whole-answer compare
            // below would read one blank's calculation against another's. Each blank carries its own, and
            // is graded against its own accepted alternatives — a blank is only wrong when EVERY
            // alternative mismatches. One bad blank costs the "correct" verdict, not the whole answer.
            const blankMismatched = arithmeticCorrectBlanks.some((cb, i) => {
              const alts = pooledAlts || altsOf(cb);
              const sub = String(arithmeticBlankValues[i] ?? "").trim();
              if (!alts.length || sub === '') return false;
              return alts.every((alt) => arithmeticCalculationMismatch(sub, alt) === true);
            });
            if (blankMismatched) {
              isCorrectFinal = false;
              isPartialFinal = true;
              downgradedFromCorrect = true;
            }
          } else if (!isMultiBlankFill && submittedAnswer !== '') {
            const mismatch = arithmeticCalculationMismatch(submittedAnswer, data?.correct_answer);
            if (mismatch === true) {
              isCorrectFinal = false;
              isPartialFinal = false;
            }
          }
        }
        // Power mark schemes ("1³+2³+3³+4³") require the powers in the written form. Value equality
        // alone is not enough: 1³ = 1, so "1+2³+3³+4³" must not stay "Correct!".
        if (!isInteractiveDrawing(question) && !hasDrawing && submittedAnswer !== '' && isCorrectFinal) {
          const correctBlanks = Array.isArray(question?.answer_data?.correct_blanks)
            ? question.answer_data.correct_blanks
            : null;
          const blankValues = value && typeof value === "object" && !Array.isArray(value) && Array.isArray(value.blanks)
            ? value.blanks
            : value;
          if (isMultiBlankFill && correctBlanks && Array.isArray(blankValues) && correctBlanks.length > 1) {
            let powerRequired = 0;
            let powerMatched = 0;
            correctBlanks.forEach((cb, i) => {
              const alts = (Array.isArray(cb) ? cb : [cb]).map((a) => String(a ?? "").trim()).filter(Boolean);
              if (!alts.length || !isNumericPowerExpression(alts[0])) return;
              powerRequired++;
              const sub = String(blankValues[i] ?? "").trim();
              if (alts.some((alt) => matchesNumericPower(sub, alt))) powerMatched++;
            });
            if (powerRequired > 0 && powerMatched < powerRequired) {
              isCorrectFinal = false;
              isPartialFinal = true;
              downgradedFromCorrect = true;
            }
          } else if (!isMultiBlankFill && !isMultiSelectType(question) && !isOrderingType(question)
            && isNumericPowerExpression(data?.correct_answer)
            && !matchesNumericPower(submittedAnswer, String(data.correct_answer))) {
            isCorrectFinal = false;
            isPartialFinal = false;
            downgradedFromCorrect = true;
          }
        }
        // Ordered-sequence questions (e.g. "arrange in order of size") must be graded
        // position-sensitively — the backend's order-independent multi-value match wrongly
        // accepts the right values in the wrong order. Right values, wrong order → partial.
        // Multi-blank fills are excluded for the same reason as the tiers above: each blank is
        // already graded against its own position, and their `correct_answer` is a DISPLAY string,
        // not a value list — with alternative answers it reads "5, 32, … or 5, 32, …", which splits
        // into one long pseudo-sequence (the " or " even glues "0 or 5" into a single token). An
        // 11-blank table then scored "11 of 31 in the correct order" and a fully correct answer was
        // downgraded to partial. requiresOrderedAnswer also fires on narrative prose — Q6's stem
        // merely describes a board listing competitors "in order of their total score".
        let orderOverrideHint = null;
        if (!isInteractiveDrawing(question) && !hasDrawing && !isMultiBlankFill && submittedAnswer !== '' && data?.correct_answer && requiresOrderedAnswer(question)) {
          const orderedCorrect = splitMultiValueAnswer(String(data.correct_answer));
          if (orderedCorrect.length > 1) {
            let orderedSub = splitMultiValueAnswer(normalizeMathSymbols(submittedAnswer));
            // Numeric sequences are often typed with spaces ("798 879 897") or bare commas
            // ("798,879,897"), which splitMultiValueAnswer doesn't break — re-tokenize on
            // whitespace/comma so a correct space-separated list isn't scored as "wrong order".
            const correctAllNumeric = orderedCorrect.every((p) => /^-?\d+(?:\.\d+)?$/.test(String(p).trim()));
            if (correctAllNumeric && orderedSub.length < orderedCorrect.length) {
              const reSplit = normalizeMathSymbols(submittedAnswer).trim().split(/[\s,]+/).map((p) => p.trim()).filter(Boolean);
              if (reSplit.length > orderedSub.length) orderedSub = reSplit;
            }
            let si = 0;
            for (const cp of orderedCorrect) {
              if (si < orderedSub.length && singleValueMatches(orderedSub[si], cp)) si++;
            }
            const inOrder = si === orderedSub.length && orderedSub.length > 0;
            if (inOrder && orderedSub.length === orderedCorrect.length) {
              isCorrectFinal = true;
              isPartialFinal = false;
            } else if (inOrder) {
              // Fewer values but still in the correct order → partial.
              isCorrectFinal = false;
              isPartialFinal = true;
              orderOverrideHint = `Partially correct — ${orderedSub.length} of ${orderedCorrect.length} in the correct order. See the full answer below.`;
            } else if (isSameValueMultiset(orderedSub, orderedCorrect)) {
              // Right values, wrong sequence — the backend hint may say "Correct! ✓"; never inherit it.
              isCorrectFinal = false;
              isPartialFinal = false;
              orderOverrideHint = "Incorrect — wrong order. See the correct answer below.";
            } else {
              // The greedy scan above only counts matches, so a wrong or unmatched VALUE also lands
              // here — reporting that as "wrong order" is a lie. Leave the backend's verdict alone.
              orderOverrideHint = null;
            }
          }
        }
        const aiEvaluated = !!data?.ai_evaluated;
        const partialRatio = typeof data?.partial_ratio === "number" ? data.partial_ratio : null;
        setVerifyResult({
          question_id: question.id,
          is_correct: isCorrectFinal,
          is_partial: isPartialFinal,
          was_answered: wasAnswered,
          hint: orderOverrideHint
            ? orderOverrideHint
            : isCorrectFinal
            ? (aiEvaluated ? "Correct! ✓ (AI Vision)" : "Correct! ✓")
            : isPartialFinal
            // downgradedFromCorrect: the server graded this CORRECT and its hint says so — printing that
            // hint over an amber "partially correct" panel is how a wrong answer came to read "Correct! ✓".
            ? ((!downgradedFromCorrect && data?.hint) || (aiEvaluated ? "Partially correct (AI Vision) — see correct answer below." : "Partially correct — see full answer below."))
            : (submittedDrawing && !aiEvaluated)
            ? "Drawing submitted — compare with the correct answer below."
            : (submittedDrawing && aiEvaluated)
            ? (data?.hint || "Incorrect (AI Vision) — see correct answer below. AI evaluation of drawings may not be perfect; final marks are awarded after full submission.")
            : wasAnswered
            ? "Incorrect — see correct answer below."
            : "No answer entered yet. See the correct answer below.",
          correct_answer: data?.correct_answer || null,
          correct_option_image: data?.correct_option_image || null,
          submitted_answer: submittedAnswer || null,
          submitted_drawing: submittedDrawing || null,
          correct_drawing: data?.correct_drawing || data?.correct_solution_image_url || null,
          correct_interactive: data?.correct_interactive || null,
          ai_evaluated: aiEvaluated,
          partial_ratio: partialRatio,
          needs_review: !!(data?.needs_review),
          confidence: data?.confidence || null,
        });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not verify answer");
    } finally {
      setVerifyLoading(false);
    }
  };

  const buildAnswersPayload = useCallback(async () => {
    const payload = [];
    for (const question of questions) {
      const value = answers[question.id];
      if (isGroupedQuestion(question)) {
        for (const sub of (question.sub_questions || [])) {
          const subValue = (value || {})[sub.id];
          // QB-added grouped parts carry a synthetic string id ("extract_X_qbsub_Y") — pass it
          // through; parseInt would turn it into NaN and 422 the whole submit.
          const numericSubId = parseInt(sub.id, 10);
          const row = { question_id: Number.isFinite(numericSubId) && String(numericSubId) === String(sub.id) ? numericSubId : String(sub.id) };
          if (isInteractiveDrawing(sub)) {
            const arr = interactiveArrayOf(subValue);
            const written = interactiveWrittenOf(interactiveSpecOf(sub));
            const writtenText = written ? interactiveWrittenText(subValue) : "";
            if (arr.length === 0 && !writtenText.trim()) continue;
            row.answer_interactive = interactiveValueOf(subValue); // preserve { points, edges } for graded outline
            if (written) row.answer_text = writtenText;
            payload.push(row);
            continue;
          }
          if (isTextQuestionType(sub)) {
            if (subValue != null && typeof subValue === "object" && !Array.isArray(subValue)) {
              row.answer_text = typeof subValue.text === "string" ? subValue.text.trim() : "";
              const rawDrawing = subValue.drawing && String(subValue.drawing).startsWith("data:image/") ? subValue.drawing : null;
              row.answer_drawing = rawDrawing ? await compressDrawingForVerify(rawDrawing, 1200) : null;
            } else {
              row.answer_text = typeof subValue === "string" ? subValue.trim() : "";
              row.answer_drawing = null;
            }
            if (!row.answer_text && !row.answer_drawing) continue;
          } else if (isFillType(sub) || isImageFill(sub)) {
            if (subValue && typeof subValue === "object" && !Array.isArray(subValue) && "interactive" in subValue) {
              const blanks = Array.isArray(subValue.blanks) ? subValue.blanks : [];
              row.answer_text = blanks.join(" | ").trim();
              // interactiveValueOf: keep {points, edges} — the grader reads the outline from edges. The
              // emptiness check goes through interactiveHasContent, since the value may now be an object.
              row.answer_interactive = interactiveValueOf(subValue.interactive);
              row.answer_drawing = null;
              if (!row.answer_text && !interactiveHasContent(row.answer_interactive)) continue;
              payload.push(row);
              continue;
            }
            if (Array.isArray(subValue)) {
              row.answer_text = subValue.join(" | ").trim();
              row.answer_drawing = null;
            } else if (subValue && typeof subValue === "object") {
              row.answer_text = typeof subValue.text === "string" ? subValue.text.trim() : "";
              const rawDrawing = subValue.drawing && String(subValue.drawing).startsWith("data:image/") ? subValue.drawing : null;
              row.answer_drawing = rawDrawing ? await compressDrawingForVerify(rawDrawing, 800) : null;
            } else {
              row.answer_text = String(subValue ?? "").trim();
              row.answer_drawing = null;
            }
            if (!row.answer_text && !row.answer_drawing) continue;
          } else if (isMatchingType(sub)) {
            if (matchingPairCount(subValue) === 0) continue;
            row.answer_matching = matchingAnswerOf(subValue);
          } else if (isOrderingType(sub)) {
            const orderText = orderingAnswerToText(sub.options, subValue);
            if (orderText === "") continue;
            row.answer_text = orderText;
            row.answer_drawing = null;
          } else if (isMultiSelectType(sub)) {
            const ids = Array.isArray(subValue) ? subValue.filter(Boolean) : [];
            if (ids.length === 0) continue;
            row.option_ids = ids;
          } else {
            if (subValue == null || subValue === "") continue;
            row.option_id = subValue;
          }
          payload.push(row);
        }
        continue;
      }

      const row = { question_id: parseInt(question.id, 10) };
      if (isInteractiveDrawing(question)) {
        const arr = interactiveArrayOf(value);
        const written = interactiveWrittenOf(interactiveSpecOf(question));
        const writtenText = written ? interactiveWrittenText(value) : "";
        if (arr.length === 0 && !writtenText.trim()) continue;
        row.answer_interactive = interactiveValueOf(value); // preserve { points, edges } for graded outline
        if (written) row.answer_text = writtenText;
        payload.push(row);
        continue;
      }
      if (isTextQuestionType(question)) {
        if (value != null && typeof value === "object" && !Array.isArray(value)) {
          row.answer_text = typeof value.text === "string" ? value.text.trim() : "";
          const rawDrawing = value.drawing && String(value.drawing).startsWith("data:image/") ? value.drawing : null;
          row.answer_drawing = rawDrawing ? await compressDrawingForVerify(rawDrawing, 1200) : null;
        } else {
          row.answer_text = typeof value === "string" ? value.trim() : "";
          row.answer_drawing = null;
        }
        if (!row.answer_text && !row.answer_drawing) continue;
      } else if (isFillType(question) || isImageFill(question)) {
        if (Array.isArray(value)) {
          row.answer_text = value.join(" | ").trim();
          row.answer_drawing = null;
        } else if (value && typeof value === "object") {
          row.answer_text = typeof value.text === "string" ? value.text.trim() : "";
          const rawDrawing = value.drawing && String(value.drawing).startsWith("data:image/") ? value.drawing : null;
          row.answer_drawing = rawDrawing ? await compressDrawingForVerify(rawDrawing, 800) : null;
        } else {
          row.answer_text = String(value ?? "").trim();
          row.answer_drawing = null;
        }
        if (!row.answer_text && !row.answer_drawing) continue;
      } else if (isMatchingType(question)) {
        if (matchingPairCount(value) === 0) continue;
        row.answer_matching = matchingAnswerOf(value);
      } else if (isOrderingType(question)) {
        const orderText = orderingAnswerToText(question.options, value);
        if (orderText === "") continue;
        row.answer_text = orderText;
        row.answer_drawing = null;
      } else {
        if (isMultiSelectType(question)) {
          const ids = Array.isArray(value) ? value.filter(Boolean) : [];
          if (ids.length === 0) continue;
          row.option_ids = ids;
        } else {
          if (value == null || value === "") continue;
          row.option_id = value;
        }
      }
      payload.push(row);
    }
    return payload;
  }, [answers, questions]);

  // Once the paper is submitted, jump straight past the exam screen (and the leave-guard's
  // extra buffer history entry) so a single browser Back press from the result page lands the
  // student on the papers list, not back on the take page.
  const goToResultCollapsingHistory = useCallback((path, state) => {
    if (dummyHistoryPushedRef.current) {
      dummyHistoryPushedRef.current = false;
      const onPop = () => {
        window.removeEventListener("popstate", onPop);
        navigate(path, { replace: true, state });
      };
      window.addEventListener("popstate", onPop);
      window.history.back();
    } else {
      navigate(path, { replace: true, state });
    }
  }, [navigate]);

  const handleSubmit = useCallback(async () => {
    setShowSubmitDialog(false);
    setSubmitting(true);
    try {
      const response = await api.post(`/student/paper/${purchaseId}/submit`, { answers: await buildAnswersPayload() });
      if (response.data.success) {
        hasSubmittedRef.current = true;
        if (!isPracticeMode) { try { localStorage.removeItem(draftKey); } catch {} }
        const data = response.data.data || {};
        const paperName = paperMeta?.paper_name || "";
        if (data.practice || data.recorded === false) {
          const resultId = data.result_id ?? purchaseId;
          if (data.ai_feedback_status === "pending" && resultId != null) {
            toast.success("Finished! AI is now analysing your practice answers...");
            setAiScoring({ active: true, resultId, progress: 5 });
          } else {
            goToResultCollapsingHistory(`/${STUDENT_BASE}/past-paper/${purchaseId}/result`, {
              results: { ...data, paper_name: paperName }, isPractice: true,
            });
          }
        } else {
          const resultId = data.result_id ?? purchaseId;
          toast.success("Paper submitted! AI is now scoring your answers...");
          if (resultId != null) {
            setAiScoring({ active: true, resultId, progress: 5 });
          } else {
            goToResultCollapsingHistory(`/${STUDENT_BASE}/past-paper/${purchaseId}/result`, {
              results: { ...data, paper_name: paperName },
            });
          }
        }
      } else {
        throw new Error(response.data.message || "Failed to submit paper");
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || "Failed to submit paper", severity: "error" });
    } finally {
      setSubmitting(false);
    }
  }, [answers, buildAnswersPayload, draftKey, goToResultCollapsingHistory, isPracticeMode, paperMeta?.paper_name, purchaseId, questions]);

  submitFnRef.current = handleSubmit;

  useEffect(() => {
    if (!aiScoring.active || !aiScoring.resultId) return undefined;

    const MAX_WAIT_MS = 150_000;
    const POLL_MS = 2_500;
    let elapsed = 0;

    aiProgressRef.current = setInterval(() => {
      setAiScoring((prev) => ({ ...prev, progress: Math.min(prev.progress + 0.7, 88) }));
    }, 1_000);

    aiPollRef.current = setInterval(async () => {
      elapsed += POLL_MS;
      try {
        const res = await api.get(`/student/paper/${aiScoring.resultId}/ai-feedback`);
        const status = res.data?.data?.status;
        if (status === "completed") {
          clearInterval(aiPollRef.current);
          clearInterval(aiProgressRef.current);
          setAiScoring((prev) => ({ ...prev, progress: 100 }));
          setTimeout(() => {
            goToResultCollapsingHistory(`/${STUDENT_BASE}/past-paper/${aiScoring.resultId}/result`, {
              aiCompleted: true, aiScore: res.data?.data?.ai_score,
            });
          }, 700);
        } else if (status === "failed" || elapsed >= MAX_WAIT_MS) {
          clearInterval(aiPollRef.current);
          clearInterval(aiProgressRef.current);
          if (status === "failed") toast.warning("AI scoring could not be completed. Showing your saved score.");
          else toast.info("AI scoring is taking longer than expected. Showing your score now.");
          goToResultCollapsingHistory(`/${STUDENT_BASE}/past-paper/${aiScoring.resultId}/result`);
        }
      } catch {
      }
    }, POLL_MS);

    return () => {
      clearInterval(aiPollRef.current);
      clearInterval(aiProgressRef.current);
    };
  }, [aiScoring.active, aiScoring.resultId, goToResultCollapsingHistory]);

  const handleCloseSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  const requestLeavePastPapers = () => {
    const hasProgress = questions.some((question) => isQuestionAnswered(question, answers));
    if (hasProgress && !submitting) {
      setShowLeaveDialog(true);
      return;
    }
    navigate(`/${STUDENT_BASE}/past-papers`);
  };

  const goToQuestion = (idx) => {
    if (idx < 0 || idx >= questions.length) return;
    setStatuses((prev) => ({
      ...prev,
      [currentQuestionIndex]: prev[currentQuestionIndex] === STATUS.NOT_VISITED ? STATUS.UNANSWERED : prev[currentQuestionIndex],
    }));
    setCurrentQuestionIndex(idx);
    try {
      const raw = localStorage.getItem(draftKey);
      const draft = raw ? JSON.parse(raw) : {};
      localStorage.setItem(draftKey, JSON.stringify({ ...draft, currentQuestionIndex: idx }));
    } catch {}
  };
  const goToQuestionEntry = (idx, subIdx = null) => {
    goToQuestion(idx);
    if (typeof subIdx === "number" && subIdx >= 0) {
      setGroupedSubIdx(subIdx);
    } else {
      setGroupedSubIdx(0);
    }
  };

  const toggleFlag = async (dialogPayload = null) => {
    const question = questions[currentQuestionIndex];
    if (!question) return;
    const isCurrentlyFlagged = statuses[currentQuestionIndex] === STATUS.FLAGGED;

    if (!isCurrentlyFlagged && dialogPayload === null) {
      setFlagReasonDialog({ ...EMPTY_FLAG_DIALOG, open: true });
      return;
    }

    setFlaggingInProgress((prev) => ({ ...prev, [currentQuestionIndex]: true }));
    try {
      if (isCurrentlyFlagged) {
        await api.delete(`/student/paper/${purchaseId}/questions/${question.id}/flag`);
      } else {
        await api.post(`/student/paper/${purchaseId}/questions/${question.id}/flag`, {
          reason: dialogPayload?.reason || '',
          issue_question: dialogPayload?.issue_question || false,
          issue_answer: dialogPayload?.issue_answer || false,
          issue_image: dialogPayload?.issue_image || false,
        });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to flag question');
      if (!isCurrentlyFlagged) setFlagReasonDialog(EMPTY_FLAG_DIALOG);
      setFlaggingInProgress((prev) => ({ ...prev, [currentQuestionIndex]: false }));
      return;
    } finally {
      if (!isCurrentlyFlagged) setFlagReasonDialog(EMPTY_FLAG_DIALOG);
      setFlaggingInProgress((prev) => ({ ...prev, [currentQuestionIndex]: false }));
    }

    setStatuses((prev) => {
      const willBeFlagged = prev[currentQuestionIndex] !== STATUS.FLAGGED;
      return {
        ...prev,
        [currentQuestionIndex]: willBeFlagged
          ? STATUS.FLAGGED
          : isQuestionAnswered(question, answers) ? STATUS.ANSWERED : STATUS.UNANSWERED,
      };
    });
  };

  const handleFlagReasonSave = () => {
    toggleFlag({
      reason: flagReasonDialog.reason || '',
      issue_question: flagReasonDialog.issue_question,
      issue_answer: flagReasonDialog.issue_answer,
      issue_image: flagReasonDialog.issue_image,
    });
  };

  const markAnswerLater = () => {
    setStatuses((prev) => ({ ...prev, [currentQuestionIndex]: STATUS.ANSWER_LATER }));
    if (currentQuestionIndex < questions.length - 1) setCurrentQuestionIndex((prev) => prev + 1);
  };

  const handleNext = () => {
    if (currentQuestionIndex >= questions.length - 1) return;
    const q = currentQuestion;
    if (isGroupedQuestion(q)) {
      const subs = q.sub_questions || [];
      const value = answers[q.id] || {};
      const firstUnansweredSub = subs.findIndex((sub) => !groupedAnswerHasContent(value[sub.id]));
      const remaining = subs.filter((sub) => !groupedAnswerHasContent(value[sub.id])).length;
      if (remaining > 0) {
        setIncompleteNextDialog({ open: true, remaining, firstUnansweredSub: firstUnansweredSub < 0 ? 0 : firstUnansweredSub });
        return;
      }
    }
    goToQuestion(currentQuestionIndex + 1);
  };
  const handlePrevious = () => {
    if (currentQuestionIndex > 0) goToQuestion(currentQuestionIndex - 1);
  };

  const answeredCount = questions.filter((question) => isQuestionAnswered(question, answers)).length;
  const partialCount = questions.filter((question) =>
    isGroupedQuestion(question)
    && !isQuestionAnswered(question, answers)
    && (question.sub_questions || []).some((sub) => groupedAnswerHasContent((answers[question.id] || {})[sub.id]))
  ).length;
  const unansweredCount = questions.length - answeredCount - partialCount;
  const flaggedCount = Object.values(statuses).filter((value) => value === STATUS.FLAGGED).length;
  const answerLaterCount = Object.values(statuses).filter((value) => value === STATUS.ANSWER_LATER).length;
  const notVisitedCount = questions.reduce((sum, _, index) => sum + ((statuses[index] || STATUS.NOT_VISITED) === STATUS.NOT_VISITED ? 1 : 0), 0);
  const questionDisplayLabel = getQuestionLabel(currentQuestion, currentQuestionIndex);
  const displayTitle = paperMeta?.paper_name?.trim() || "Past paper";
  const prevSectionTitle = currentQuestionIndex > 0 ? questions[currentQuestionIndex - 1]?.section_title : null;
  const isNewSection = !!currentQuestion?.section_title && currentQuestion.section_title !== prevSectionTitle;
  const isEnglishPaper = /english/i.test(paperMeta?.paper_name || "");
  const isExplicitComprehension = (q) => isGroupedQuestion(q) && q?.passage_type === "comprehension";
  const isCurrentComprehension = isGroupedQuestion(currentQuestion) &&
    (isExplicitComprehension(currentQuestion) || (isComprehensionPassage(currentQuestion) && isEnglishPaper));
  const hasSupplementaryBooklet = !!currentQuestion?.supplementary_booklet_url;
  const currentPartLabels = isCurrentComprehension && Array.isArray(currentQuestion?.sub_questions)
    ? currentQuestion.sub_questions.map((_, idx) => String.fromCharCode(97 + idx)).join(", ")
    : "";
  const displayTotalQuestions = questions.length;
  const questionNavSections = (() => {
    const sections = [];
    questions.forEach((question, index) => {
      const title = question.section_title || null;
      if (sections.length === 0 || title !== sections[sections.length - 1].title) sections.push({ title, items: [] });
      const isComp = isGroupedQuestion(question) && isComprehensionPassage(question);
      const qPaperLabel = getQuestionLabel(question, index);
      if (isComp && (question.sub_questions || []).length > 0) {
        const subItems = (question.sub_questions || []).map((sub, subIdx) => ({
          label: String.fromCharCode(97 + subIdx),
          answered: groupedAnswerHasContent((answers[question.id] || {})[sub.id]),
          id: sub.id,
          subIdx,
          isActive: index === currentQuestionIndex && groupedSubIdx === subIdx,
        }));
        const allAnswered = subItems.length > 0 && subItems.every((sub) => sub.answered);
        const anyAnswered = subItems.some((sub) => sub.answered);
        sections[sections.length - 1].items.push({
          key: `q_${index}_comp`,
          index,
          subIdx: null,
          label: qPaperLabel,
          status: allAnswered ? STATUS.ANSWERED : anyAnswered ? STATUS.PARTIAL : (statuses[index] || STATUS.NOT_VISITED),
          isActive: index === currentQuestionIndex,
          childLabels: subItems,
        });
      } else {
        sections[sections.length - 1].items.push({
          key: `q_${index}`,
          index,
          subIdx: null,
          label: qPaperLabel,
          status: (() => {
            const s = statuses[index] || STATUS.NOT_VISITED;
            if (s === STATUS.FLAGGED || s === STATUS.ANSWER_LATER || s === STATUS.NOT_VISITED) return s;
            if (isQuestionAnswered(question, answers)) return STATUS.ANSWERED;
            if (isGroupedQuestion(question)
              && (question.sub_questions || []).some((sub) => groupedAnswerHasContent((answers[question.id] || {})[sub.id]))) {
              return STATUS.PARTIAL;
            }
            return STATUS.UNANSWERED;
          })(),
          isActive: index === currentQuestionIndex,
          childLabels: !isComp && isGroupedQuestion(question) ? (question.sub_questions || []).map((sub, subIdx) => {
            const subLabel = sub.paper_number != null && Number(sub.paper_number) !== 0 && String(sub.paper_number).trim() !== ""
              ? String(sub.paper_number)
              : String.fromCharCode(97 + subIdx);
            const answered = groupedAnswerHasContent((answers[question.id] || {})[sub.id]);
            return { label: subLabel, answered, id: sub.id, subIdx };
          }) : [],
        });
      }
    });
    return sections;
  })();

  // Comprehension passage content for the side panel
  const rawPassage = isCurrentComprehension ? (currentQuestion?.question_text || currentQuestion?.group_title || "") : "";
  const stemText = isCurrentComprehension ? cleanStemText(rawPassage, { preserveLineMarkers: true }) : null;
  const stemImages = isCurrentComprehension ? collectQuestionImages(currentQuestion) : [];
  const stemHasTable = stemText ? (stemText.includes("|") || stemText.includes("<table")) : false;
  const stemHasLatex = stemText ? stemText.includes("\\(") || stemText.includes("$$") : false;
  const stemHasHtml = stemText ? /<[a-zA-Z]/.test(stemText) : false;
  const stemHasBold = stemText ? stemText.includes("**") : false;

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", bgcolor: "#f5f5f5" }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (fetchError) {
    return (
      <Box sx={{ p: 3, textAlign: "center", bgcolor: "#f5f5f5", minHeight: "100vh" }}>
        <Typography color="error" sx={{ mb: 2 }}>
          {fetchError}
        </Typography>
        <Button variant="contained" onClick={() => navigate(`/${STUDENT_BASE}/past-papers`)}>
          Back to Past Papers
        </Button>
      </Box>
    );
  }

  if (questions.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: "center", bgcolor: "#f5f5f5", minHeight: "100vh" }}>
        <Typography sx={{ mb: 2 }}>No questions found for this paper.</Typography>
        <Button variant="contained" onClick={() => navigate(`/${STUDENT_BASE}/past-papers`)}>
          Back
        </Button>
      </Box>
    );
  }

  return (
    <AnnotateProvider annotating={annotating} onToggle={() => setAnnotating((prev) => !prev)}>
    <MeasurementToolsProvider
      measurementMode={effectiveMeasurementMode}
      showProtractor={showProtractor}
      showRuler={showRuler}
      onToggleProtractor={() => setShowProtractor((v) => !v)}
      onToggleRuler={() => setShowRuler((v) => !v)}
    >
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", bgcolor: "#f5f5f5", overflow: "hidden" }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: { xs: 1.5, sm: 3 }, py: { xs: 1, sm: 1.5 }, bgcolor: "white", boxShadow: "0 2px 8px rgba(0,0,0,0.1)", position: "sticky", top: 0, zIndex: 100, flexWrap: "wrap", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", flex: 1, minWidth: 0 }}>
          <Button variant="text" startIcon={<ArrowBackIcon />} onClick={requestLeavePastPapers} sx={{ textTransform: "none", fontWeight: 600, color: "text.primary", fontSize: { xs: "0.85rem", sm: "0.875rem" }, px: { xs: 0.5, sm: 1 }, minWidth: 0 }}>
            Back
          </Button>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#1a1a1a", fontSize: { xs: "0.9rem", sm: "1.1rem" }, lineHeight: 1.3, flex: 1, minWidth: 0 }}>
            {displayTitle}
          </Typography>
          {isPracticeMode ? (
            <Alert severity="info" sx={{ py: 0, px: 1, fontSize: "0.7rem", fontWeight: 700, "& .MuiAlert-icon": { fontSize: 14, mr: 0.5 }, display: { xs: "none", sm: "flex" } }}>
              PRACTICE MODE
            </Alert>
          ) : null}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.75, sm: 1 }, flexWrap: "nowrap" }}>
          {!isPracticeMode ? (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, px: { xs: 1, sm: 2 }, py: 0.75, borderRadius: 2, bgcolor: timeRemaining < 300 ? "#fff3e0" : "#f5f5f5", border: "1px solid", borderColor: timeRemaining < 300 ? "warning.main" : "#e0e0e0" }}>
                <AccessTimeIcon sx={{ fontSize: { xs: 16, sm: 20 }, color: timeRemaining < 300 ? "warning.main" : "text.secondary" }} />
                <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: "monospace", color: timeRemaining < 300 ? "warning.main" : "text.primary", fontSize: { xs: "0.95rem", sm: "1.1rem" } }}>
                  {formatTime(timeRemaining)}
                </Typography>
              </Box>
              <Button variant="outlined" size="small" startIcon={paused ? <PlayCircleIcon /> : <PauseCircleIcon />} onClick={() => setPaused((prev) => !prev)} disabled={submitting || aiScoring.active || timeRemaining <= 0} sx={{ textTransform: "none", fontWeight: 700, display: { xs: "none", sm: "flex" } }}>
                {paused ? "Resume" : "Pause"}
              </Button>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, fontSize: { xs: "0.8rem", sm: "0.875rem" }, display: { xs: "none", sm: "block" } }}>
              Untimed practice
            </Typography>
          )}
          <Button variant="contained" onClick={() => setShowSubmitDialog(true)} disabled={submitting || aiScoring.active} sx={{ bgcolor: "#ef5350", color: "white", fontWeight: 700, px: { xs: 1.5, sm: 3 }, py: { xs: 0.75, sm: 1 }, textTransform: "none", fontSize: { xs: "0.85rem", sm: "0.875rem" }, "&:hover": { bgcolor: "#c62828" }, "&:disabled": { bgcolor: "#e0e0e0", color: "#9e9e9e" } }}>
            {submitting ? <CircularProgress size={18} color="inherit" /> : isPracticeMode ? "Finish practice" : "Submit paper"}
          </Button>
        </Box>
      </Box>

      {isPracticeMode ? (
        <Alert severity="info" sx={{ borderRadius: 0, py: 0.5, px: { xs: 1.5, sm: 3 }, "& .MuiAlert-message": { display: "flex", alignItems: "center", width: "100%" } }}>
          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: { xs: "0.85rem", sm: "0.8rem" } }}>
            PRACTICE MODE — Answers are not recorded on your record. Use &quot;Check answer&quot; on each question when available.
          </Typography>
        </Alert>
      ) : null}

      {!isPracticeMode && paused ? (
        <Box sx={{ px: 3, py: 1, bgcolor: "#e3f2fd", borderBottom: "1px solid #90caf9", textAlign: "center" }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            Timer paused — click Resume when you are ready to continue.
          </Typography>
        </Box>
      ) : null}

      <Box sx={{ display: "flex", flex: 1, overflow: "hidden", flexDirection: { xs: "column", lg: "row" }, minWidth: 0 }}>
        {/* Comprehension passage panel */}
        {isCurrentComprehension && passageOpen ? (
          <Box sx={{
            width: { xs: "100%", lg: supplementaryOpen ? "35%" : "55%" },
            minWidth: { xs: 0, lg: 280 },
            maxWidth: { xs: "100%", lg: supplementaryOpen ? 460 : 600 },
            overflow: "auto",
            bgcolor: "#fafafa",
            borderRight: { xs: "none", lg: "2px solid #e0e0e0" },
            borderBottom: { xs: "2px solid #e0e0e0", lg: "none" },
            boxShadow: "2px 0 8px rgba(0,0,0,0.08)",
            position: { xs: "relative", lg: "sticky" },
            top: 0,
            height: { xs: "auto", lg: "calc(100vh - 64px)" },
            maxHeight: { xs: "40vh", lg: "none" },
            flexShrink: 0,
          }}>
            <Box sx={{ px: 2, pt: 1.5, pb: 1, borderBottom: "1px solid #e0e0e0", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "#1565c0", flexShrink: 0 }}>
                Reading Passage
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Button size="small" variant="outlined" onClick={() => setPassageFontSize((s) => Math.max(0.75, +(s - 0.1).toFixed(1)))} sx={{ minWidth: 0, px: 0.75, py: 0.25, fontSize: "0.8rem", lineHeight: 1, borderColor: "#c5cae9", color: "#3949ab" }}>A−</Button>
                <Button size="small" variant="outlined" onClick={() => setPassageFontSize((s) => Math.min(1.6, +(s + 0.1).toFixed(1)))} sx={{ minWidth: 0, px: 0.75, py: 0.25, fontSize: "0.8rem", lineHeight: 1, borderColor: "#c5cae9", color: "#3949ab" }}>A+</Button>
                <Button size="small" variant="text" onClick={() => setPassageOpen(false)} sx={{ textTransform: "none", fontSize: "0.7rem", color: "text.secondary", minWidth: 0, px: 1 }}>
                  Hide ✕
                </Button>
              </Box>
            </Box>
            <Box sx={{ px: 2, pt: 2, pb: 6 }}>
              {stemText ? (
                stemHasTable || stemHasLatex || stemHasHtml || stemHasBold ? (
                  <Box sx={{ "& img": { maxWidth: "100%", maxHeight: `${Math.round(320 * passageFontSize)}px`, objectFit: "contain", borderRadius: 1 } }}>
                    <HtmlWithLatex html={stemText} style={{ fontSize: `${passageFontSize}rem`, lineHeight: 1.75 }} />
                  </Box>
                ) : (
                  <PassageWithLineNumbers
                    text={stemText}
                    wrapWidth={computePassageWrapWidth(rawPassage, currentQuestion?.sub_questions || [])}
                    fontSize={passageFontSize}
                  />
                )
              ) : null}
              {stemImages.length > 0 ? (
                <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
                  {stemImages.map((src, i) => (
                    <Box key={i} component="img" src={resolveQuestionHtmlAssetUrl(src) || src} alt="" onError={(e) => { e.target.style.display = "none"; }} sx={{ maxWidth: "100%", maxHeight: Math.round(320 * passageFontSize), objectFit: "contain", borderRadius: 1 }} />
                  ))}
                </Box>
              ) : null}
            </Box>
          </Box>
        ) : null}

        {/* Supplementary booklet panel */}
        {supplementaryOpen && currentQuestion?.supplementary_booklet_url ? (
          <Box sx={{
            width: { xs: "100%", lg: isCurrentComprehension && passageOpen ? "35%" : "55%" },
            minWidth: { xs: 0, lg: 280 },
            maxWidth: { xs: "100%", lg: isCurrentComprehension && passageOpen ? 460 : 600 },
            overflow: "hidden",
            bgcolor: "#faf5ff",
            borderRight: { xs: "none", lg: "2px solid #c4b5fd" },
            borderBottom: { xs: "2px solid #c4b5fd", lg: "none" },
            boxShadow: "2px 0 8px rgba(92,53,176,0.10)",
            position: { xs: "relative", lg: "sticky" },
            top: 0,
            height: { xs: "auto", lg: "calc(100vh - 64px)" },
            maxHeight: { xs: "40vh", lg: "none" },
            minHeight: { xs: 200, lg: 0 },
            display: "flex",
            flexDirection: "column",
            flexShrink: 0,
          }}>
            <Box sx={{ px: 2, pt: 1.5, pb: 1, borderBottom: "1px solid #ddd6fe", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "#6a1b9a" }}>
                Sources / Booklet
              </Typography>
              <Button size="small" variant="text" onClick={() => setSupplementaryOpen(false)} sx={{ textTransform: "none", fontSize: "0.7rem", color: "text.secondary", minWidth: 0, px: 1 }}>
                Hide ✕
              </Button>
            </Box>
            <Box sx={{ flex: 1, overflow: "hidden" }}>
              <iframe
                src={currentQuestion.supplementary_booklet_url}
                title="Supplementary booklet"
                style={{ width: "100%", height: "100%", border: "none", display: "block" }}
              />
            </Box>
          </Box>
        ) : null}

        {/* Question area */}
        <Box sx={{ flex: 1, minWidth: 0, px: { xs: 1.5, sm: 3 }, pb: 3, pt: 0, overflow: "auto" }}>
          {/* Sticky question header bar */}
          <Box sx={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            position: "sticky", top: 0, zIndex: 10, bgcolor: "#f5f5f5",
            mx: { xs: -1.5, sm: -3 }, px: { xs: 1.5, sm: 3 }, pt: { xs: 1.5, sm: 2 }, pb: { xs: 1, sm: 1.5 }, mb: 1, borderBottom: "1px solid #e8e8e8",
            flexWrap: "wrap", gap: 0.75,
          }}>
            <Typography variant="body1" sx={{ fontWeight: 700, color: "text.secondary", fontSize: { xs: "1rem", sm: "1rem" } }}>
              {isCurrentComprehension
                ? `Question ${currentQuestionIndex + 1}${currentPartLabels ? ` (parts ${currentPartLabels})` : ""}`
                : `Question ${questionDisplayLabel} of ${displayTotalQuestions}`}
              {(() => {
                let m = currentQuestion?.marks;
                if (!isGroupedQuestion(currentQuestion) && isFillQuestionType(currentQuestion)) {
                  const fillTotal = getDisplayFillMarksTotal(currentQuestion);
                  if (fillTotal > 0) m = fillTotal;
                }
                if (isGroupedQuestion(currentQuestion) && (currentQuestion?.sub_questions || []).length > 0) {
                  const parentAd = currentQuestion.answer_data || {};
                  if (parentAd.marks_mode === 'fixed') {
                    m = parentAd.marks_total ?? currentQuestion.default_marks ?? m;
                  } else {
                    const subTotal = (currentQuestion.sub_questions || []).reduce((sum, s) => {
                      if (isFillQuestionType(s)) {
                        const total = getDisplayFillMarksTotal(s);
                        if (total > 0) return sum + total;
                      }
                      return sum + (Number(s.default_marks ?? s.marks) || 0);
                    }, 0);
                    if (subTotal > 0) m = subTotal;
                  }
                }
                return m != null && Number(m) > 0 ? ` · ${m} mark${m !== 1 ? "s" : ""}` : "";
              })()}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {isCurrentComprehension ? (
                <Tooltip title={passageOpen ? "Close reading passage" : "Show reading passage"}>
                  <Button size="small" variant={passageOpen ? "contained" : "outlined"} startIcon={<MenuBookIcon fontSize="small" />} onClick={() => setPassageOpen((prev) => !prev)} sx={{ textTransform: "none", fontWeight: 700, color: passageOpen ? "white" : "#1565c0", borderColor: "#1565c0", bgcolor: passageOpen ? "#1565c0" : "transparent", "&:hover": { bgcolor: passageOpen ? "#0d47a1" : "rgba(21,101,192,0.06)" } }}>
                    {passageOpen ? "Close Passage" : "Show Passage"}
                  </Button>
                </Tooltip>
              ) : null}
              {hasSupplementaryBooklet ? (
                <Tooltip title={supplementaryOpen ? "Close supplementary booklet" : "View sources / supplementary booklet"}>
                  <Button size="small" variant={supplementaryOpen ? "contained" : "outlined"} onClick={() => setSupplementaryOpen((prev) => !prev)} sx={{ textTransform: "none", fontWeight: 700, color: supplementaryOpen ? "white" : "#6a1b9a", borderColor: "#6a1b9a", bgcolor: supplementaryOpen ? "#6a1b9a" : "transparent", "&:hover": { bgcolor: supplementaryOpen ? "#4a148c" : "rgba(106,27,154,0.06)" } }}>
                    {supplementaryOpen ? "Close Sources" : "View Sources"}
                  </Button>
                </Tooltip>
              ) : null}
              <Tooltip title={currentStatus === STATUS.FLAGGED ? "Unflag this question" : "Flag for review"}>
                <Button size="small" variant={currentStatus === STATUS.FLAGGED ? "contained" : "outlined"} startIcon={currentStatus === STATUS.FLAGGED ? <FlagIcon fontSize="small" /> : <FlagOutlinedIcon fontSize="small" />} onClick={() => toggleFlag()} sx={{ textTransform: "none", fontWeight: 700, color: currentStatus === STATUS.FLAGGED ? "white" : "warning.main", borderColor: "warning.main", bgcolor: currentStatus === STATUS.FLAGGED ? "warning.main" : "transparent", "&:hover": { bgcolor: currentStatus === STATUS.FLAGGED ? "warning.dark" : "warning.50" } }}>
                  {currentStatus === STATUS.FLAGGED ? "Flagged" : "Flag"}
                </Button>
              </Tooltip>
              {canVerifyQuestion && currentQuestion && (() => {
                const bankId = currentQuestion.qb_id || currentQuestion.id;
                const itemData = getItemData("question", bankId);
                const myVerdicts = itemData?.my_verdicts;
                const aggregate = itemData?.aggregate;
                const btnColors = getVerifierButtonColors(myVerdicts, aggregate);
                const isRejected = (myVerdicts?.web === "rejected" || myVerdicts?.mobile === "rejected" || (aggregate?.rejected ?? 0) > 0);
                return (
                <Tooltip title={isRejected ? "This question was rejected — review and re-approve (Tester mode)" : "Verify this question (Tester mode)"}>
                  <Button
                    size="small"
                    variant={btnColors.variant}
                    startIcon={<VerifiedUserIcon fontSize="small" />}
                    onClick={() => {
                      loadVerifications("question", bankId);
                      setVerifyQuestionFull(null);
                      setVerifyQuestionLoading(true);
                      api.get(`/admin/question-bank/${bankId}`)
                        .then((r) => setVerifyQuestionFull(r.data?.data ?? null))
                        .catch(() => {})
                        .finally(() => setVerifyQuestionLoading(false));
                      setVerifyDialogOpen(true);
                    }}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      color: btnColors.color,
                      borderColor: "info.main",
                      bgcolor: btnColors.bgcolor,
                      "&:hover": { bgcolor: btnColors.variant === "contained" ? btnColors.bgcolor : "info.50" },
                    }}
                  >
                    {formatVerifierButtonLabel(myVerdicts, aggregate)}
                  </Button>
                </Tooltip>
                );
              })()}
              {showProtractorBtn ? (
                <Tooltip title={showProtractor ? "Hide protractor" : "Show protractor on diagram"}>
                  <Button size="small" variant={showProtractor ? "contained" : "outlined"} onClick={() => setShowProtractor((v) => !v)} sx={{ minWidth: 0, px: 1, color: showProtractor ? "white" : "#1565c0", borderColor: "#1565c0", bgcolor: showProtractor ? "#1565c0" : "transparent", "&:hover": { bgcolor: showProtractor ? "#0d47a1" : "rgba(21,101,192,0.06)" } }}>
                    <ProtractorIcon fontSize="small" />
                  </Button>
                </Tooltip>
              ) : null}
              {showRulerBtn ? (
                <Tooltip title={showRuler ? "Hide ruler" : "Show ruler on diagram"}>
                  <Button size="small" variant={showRuler ? "contained" : "outlined"} onClick={() => setShowRuler((v) => !v)} sx={{ minWidth: 0, px: 1, color: showRuler ? "white" : "success.main", borderColor: "success.main", bgcolor: showRuler ? "success.main" : "transparent", "&:hover": { bgcolor: showRuler ? "success.dark" : "rgba(46,125,50,0.06)" } }}>
                    <StraightenIcon fontSize="small" />
                  </Button>
                </Tooltip>
              ) : null}
              {showSetSquareBtn ? (
                <Tooltip title={showSetSquare ? "Hide set square" : "Show set square"}>
                  <Button size="small" variant={showSetSquare ? "contained" : "outlined"} onClick={() => setShowSetSquare((v) => !v)} sx={{ minWidth: 0, px: 1, color: showSetSquare ? "white" : "#1565c0", borderColor: "#1565c0", bgcolor: showSetSquare ? "#1565c0" : "transparent", "&:hover": { bgcolor: showSetSquare ? "#0d47a1" : "rgba(21,101,192,0.06)" } }}>
                    <SetSquareIcon fontSize="small" />
                  </Button>
                </Tooltip>
              ) : null}
              {showCompassBtn ? (
                <Tooltip title={showCompass ? "Hide compass" : "Show compass"}>
                  <Button size="small" variant={showCompass ? "contained" : "outlined"} onClick={() => setShowCompass((v) => !v)} sx={{ minWidth: 0, px: 1, color: showCompass ? "white" : "#6a1b9a", borderColor: "#6a1b9a", bgcolor: showCompass ? "#6a1b9a" : "transparent", "&:hover": { bgcolor: showCompass ? "#4a148c" : "rgba(106,27,154,0.06)" } }}>
                    <AdjustIcon fontSize="small" />
                  </Button>
                </Tooltip>
              ) : null}
              <Tooltip title={annotating ? "Hide annotation tools" : "Annotate / circle on the question"}>
                <Button size="small" variant={annotating ? "contained" : "outlined"} onClick={() => setAnnotating((prev) => !prev)} sx={{ minWidth: 0, px: 1, color: annotating ? "white" : "text.secondary", borderColor: annotating ? "warning.main" : "#e0e0e0", bgcolor: annotating ? "warning.main" : "transparent", "&:hover": { bgcolor: annotating ? "warning.dark" : "action.hover" } }}>
                  <EditIcon fontSize="small" />
                </Button>
              </Tooltip>
              <Tooltip title={paletteOpen ? "Hide question palette" : `Show question palette (${answeredCount}/${displayTotalQuestions} answered)`}>
                <Button
                  size="small"
                  variant={paletteOpen ? "contained" : "outlined"}
                  startIcon={<GridViewIcon fontSize="small" />}
                  onClick={() => setPaletteOpen((v) => !v)}
                  sx={{ textTransform: "none", fontWeight: 700, display: { xs: "flex", lg: "none" }, color: paletteOpen ? "white" : "#1565c0", borderColor: "#1565c0", bgcolor: paletteOpen ? "#1565c0" : "transparent", "&:hover": { bgcolor: paletteOpen ? "#0d47a1" : "rgba(21,101,192,0.06)" } }}
                >
                  {paletteOpen ? "Hide" : `${answeredCount}/${displayTotalQuestions}`}
                </Button>
              </Tooltip>
            </Box>
          </Box>

          {/* Section title / instructions */}
          {currentQuestion?.section_title ? (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ px: 1.5, py: 1, borderRadius: "10px 10px 0 0", bgcolor: isNewSection ? "#1565c0" : "#e3f2fd", color: isNewSection ? "white" : "#0d3c70", borderBottom: isNewSection ? "none" : "1px solid #bbdefb" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, letterSpacing: 0.3 }}>
                  {currentQuestion.section_title}
                </Typography>
              </Box>
              {currentQuestion.section_instructions ? (
                <Box sx={{ px: 1.5, py: 1.25, bgcolor: "#f4f9ff", border: "1px solid #bbdefb", borderTop: "none", borderRadius: "0 0 10px 10px" }}>
                  <FormatInstructions text={currentQuestion.section_instructions} typographySx={{ color: "#0d3c70" }} />
                </Box>
              ) : null}
            </Box>
          ) : null}

          {/* Question content */}
          <Box sx={{ bgcolor: "white", borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.06)", p: { xs: 2, sm: 2.5 }, fontSize: { xs: "1rem", sm: "1rem" }, maxWidth: "100%", minWidth: 0 }}>
            {isGroupedQuestion(currentQuestion) ? (
              <QuestionAnnotationCanvas questionIdx={currentQuestionIndex} visible={annotating} onCapture={handleGroupedAnnotationCapture}>
                <GroupedQuestion
                  question={currentQuestion}
                  answerMap={answers[currentQuestion?.id] || {}}
                  onSubAnswerChange={handleGroupedSubAnswer}
                  questionIdx={currentQuestionIndex}
                  scratchDrawingsRef={scratchDrawingsRef}
                  activeSubIdx={groupedSubIdx}
                  onSubIdxChange={setGroupedSubIdx}
                  isPracticeMode={isPracticeMode}
                  onVerify={handleVerify}
                  verifyLoading={verifyLoading}
                  passageOpen={passageOpen}
                  isComprehensionMode={isCurrentComprehension}
                  verifyResult={verifyResult}
                  verifyQuestionId={verifyQuestionId}
                  pendingFloatImage={annotationFloatImage}
                  onFloatImageConsumed={() => { setAnnotationFloatImage(null); setAnnotationFloatSubId(null); }}
                  pendingFloatSubId={annotationFloatSubId}
                  showMathTools={isMathsPaper}
                  showAnswerUnit={showAnswerUnit}
                  isEnglishPaper={isEnglishPaper}
                />
              </QuestionAnnotationCanvas>
            ) : (
              (() => {
                if (!currentQuestion) return null;
                const fillTemplate = getBlankTemplate(currentQuestion);
                const isFill = fillTemplate.length > 0 && !isBoxModeImageFill(currentQuestion);

                if (isFill) {
                  const values = Array.isArray(answers[currentQuestion.id])
                    ? answers[currentQuestion.id]
                    : fillTemplate.length === 1
                    ? [answers[currentQuestion.id] ?? ""]
                    : Array.from({ length: fillTemplate.length }, () => "");
                  return (
                    <>
                      <QuestionPaperLayout
                        hideBlankMarks
                        imageMaxHeight={300}
                        imageMaxWidth="min(100%, 540px)"
                        question={currentQuestion}
                        questionNumber={currentQuestionIndex + 1}
                        showAnswers={false}
                        showMathTools={isMathsPaper}
                        inputValues={values}
                        onInputChange={(inputIdx, newVal) => {
                          const qId = currentQuestion.id;
                          const len = fillTemplate.length;
                          setAnswers((prev) => {
                            const stored = prev[qId];
                            const cur = Array.isArray(stored)
                              ? (stored.length >= len ? stored : [...stored, ...Array(len - stored.length).fill("")])
                              : len === 1 ? [stored ?? ""]
                              : Array.from({ length: len }, () => "");
                            const next = cur.map((v, idx) => (idx === inputIdx ? newVal : v ?? ""));
                            const nextVal = len === 1 ? next[0] : next;
                            try { localStorage.setItem(draftKey, JSON.stringify({ answers: { ...prev, [qId]: nextVal }, currentQuestionIndex })); } catch {}
                            return { ...prev, [qId]: nextVal };
                          });
                          updateStatusForQuestion(qId, Array.from({ length: len }, (_, idx) => idx === inputIdx ? newVal : values[idx] ?? ""));
                          setVerifyResult(null);
                          setVerifyQuestionId(null);
                        }}
                      />
                      {isPracticeMode ? (
                        <Box sx={{ mt: 2 }}>
                          <Button variant="outlined" startIcon={<FactCheckIcon />} onClick={() => handleVerify()} disabled={verifyLoading}>
                            {verifyLoading ? "Checking…" : "Check answer"}
                          </Button>
                        </Box>
                      ) : null}
                      <ScratchPad questionIdx={currentQuestionIndex} scratchDrawingsRef={scratchDrawingsRef} marks={Number(currentQuestion.marks ?? 3) || 3} />
                    </>
                  );
                }

                if (isImageFill(currentQuestion)) {
                  const overlay = currentQuestion.answer_data?.overlay_layout;
                  const boxCount = Array.isArray(overlay?.blanks) ? overlay.blanks.length : 0;
                  const stored = answers[currentQuestion.id];
                  const values = Array.isArray(stored)
                    ? stored
                    : boxCount === 1 ? [stored ?? ""]
                    : Array.from({ length: boxCount }, () => "");
                  const contextImages = imageFillContextImages(currentQuestion);
                  const questionMarksForDisplay = (() => {
                    const m = Number(currentQuestion.marks ?? currentQuestion.default_marks ?? 1) || 1;
                    return `${m} mark${m !== 1 ? "s" : ""}`;
                  })();
                  return (
                    <>
                      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 0.5 }}>
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{ color: "text.secondary", fontWeight: 700, fontSize: "0.8rem", bgcolor: "grey.100", px: 0.75, py: 0.25, borderRadius: 0.5 }}
                        >
                          ({questionMarksForDisplay})
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <HtmlWithLatex html={currentQuestion?.question_text || `Question ${currentQuestionIndex + 1}`} style={questionRichHtmlSx} />
                      </Box>
                      {contextImages.length > 0 ? (
                        <Box sx={{ mb: 2 }}>
                          {contextImages.map((src, i) => (
                            <Box key={src} sx={{ mb: 1, textAlign: "center" }}>
                              <img
                                src={src}
                                alt={`Diagram ${i + 1}`}
                                style={{ maxWidth: "100%", height: "auto", borderRadius: 4 }}
                                onError={(e) => { e.target.style.display = "none"; }}
                              />
                            </Box>
                          ))}
                        </Box>
                      ) : null}
                      <ImageOverlayFillInput
                        overlayLayout={overlay}
                        values={values}
                        prefilled={currentQuestion.answer_data?.blank_labels}
                        showMathTools={isMathsPaper}
                        onChange={(next) => handleAnswerChange(currentQuestion.id, boxCount === 1 ? next[0] : next)}
                      />
                      {isPracticeMode ? (
                        <Box sx={{ mt: 2 }}>
                          <Button variant="outlined" startIcon={<FactCheckIcon />} onClick={() => handleVerify()} disabled={verifyLoading}>
                            {verifyLoading ? "Checking…" : "Check answer"}
                          </Button>
                        </Box>
                      ) : null}
                      <ScratchPad questionIdx={currentQuestionIndex} scratchDrawingsRef={scratchDrawingsRef} marks={Number(currentQuestion.marks ?? 3) || 3} />
                    </>
                  );
                }


                const questionMarks = Number(currentQuestion?.marks ?? currentQuestion?.default_marks ?? 1) || 1;
                const questionDisplay = (
                  <Box ref={questionDisplayRef} sx={{ maxWidth: "100%", minWidth: 0, overflow: "hidden", "& table": { display: "block", maxWidth: "100%", overflowX: "auto" }, "& img": { maxWidth: "100%", height: "auto" }, "& pre": { whiteSpace: "pre-wrap", wordBreak: "break-word" } }}>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                      <Typography component="span" variant="body2" sx={{ color: "text.secondary", fontWeight: 700, fontSize: "0.8rem", bgcolor: "grey.100", px: 0.75, py: 0.25, borderRadius: 0.5 }}>
                        ({questionMarks} mark{questionMarks !== 1 ? "s" : ""})
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <HtmlWithLatex html={currentQuestion?.question_text || `Question ${currentQuestionIndex + 1}`} style={questionRichHtmlSx} />
                    </Box>
                    {collectQuestionImages(currentQuestion).length > 0 && !isInteractiveDrawing(currentQuestion) ? (
                      <MeasurementWorkspace
                        images={collectQuestionImages(currentQuestion)}
                        measurementMode={isMathsPaper ? getMeasurementMode(currentQuestion) : null}
                        caption={isPlotPointsQuestion(currentQuestion) ? "Plot on this diagram" : undefined}
                        pxPerCm={getPxPerCm(currentQuestion)}
                      />
                    ) : null}
                    {currentQuestion?.answer_data?.geometry_config ? (
                      <GeometryMarkSegment
                        config={currentQuestion.answer_data.geometry_config}
                        value={answers[currentQuestion.id]}
                        onChange={(value) => handleAnswerChange(currentQuestion.id, value)}
                      />
                    ) : null}
                  </Box>
                );

                const answerInput = isInteractiveDrawing(currentQuestion) ? (
                  <Box>
                    {interactiveHint(currentQuestion.answer_data.interactive) ? (
                      <Typography variant="caption" color="primary.main" sx={{ display: "block", mb: 0.75, fontWeight: 600 }}>
                        {interactiveHint(currentQuestion.answer_data.interactive)}
                      </Typography>
                    ) : null}
                    <InteractiveDiagramCanvas
                      spec={currentQuestion.answer_data.interactive}
                      imageUrl={interactiveImageOf(currentQuestion)}
                      maxWidth={480}
                      value={interactiveValueOf(answers[currentQuestion.id])}
                      onChange={(v) => handleAnswerChange(
                        currentQuestion.id,
                        interactiveWrittenOf(currentQuestion.answer_data.interactive)
                          ? { interactive: v, text: interactiveWrittenText(answers[currentQuestion.id]) }
                          : v
                      )}
                    />
                    {interactiveWrittenOf(currentQuestion.answer_data.interactive) ? (
                      <Box sx={{ mt: 1.5 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label={currentQuestion.answer_data.interactive.written.prompt || "Write your answer"}
                          value={interactiveWrittenText(answers[currentQuestion.id])}
                          onChange={(e) => handleAnswerChange(currentQuestion.id, {
                            // interactiveValueOf: typing here must not drop the drawn outline.
                            interactive: interactiveValueOf(answers[currentQuestion.id]),
                            text: e.target.value,
                          })}
                        />
                      </Box>
                    ) : null}
                    {isPracticeMode ? (
                      <Box sx={{ mt: 2 }}>
                        <Button variant="outlined" startIcon={<FactCheckIcon />} onClick={() => handleVerify()} disabled={verifyLoading} sx={{ textTransform: "none", fontWeight: 700 }}>
                          {verifyLoading ? "Checking…" : "Check answer"}
                        </Button>
                      </Box>
                    ) : null}
                  </Box>
                ) : !isTextQuestionType(currentQuestion) ? (
                  (isPlotPointsQuestion(currentQuestion) || isDiagramPlacementQuestion(currentQuestion)) && (currentQuestion?.options || []).length === 0 ? (
                    <TypeDrawInput
                      key={currentQuestion.id}
                      answer={answers[currentQuestion.id] ?? ""}
                      onChange={(value) => handleAnswerChange(currentQuestion.id, value)}
                      multiline={false}
                      rows={3}
                      label="Your answer"
                      questionIdx={currentQuestionIndex}
                      marks={Number(currentQuestion.marks ?? 3) || 3}
                      allowDraw
                      defaultTab={1}
                      measurementMode={isMathsPaper ? getMeasurementMode(currentQuestion) : null}
                      showMathTools={isMathsPaper}
                      contextImageUrl={questionContextSnapshot}
                      pendingFloatImage={annotationFloatImage}
                      onFloatImageConsumed={() => setAnnotationFloatImage(null)}
                      questionImages={collectAllQuestionImageEntries(currentQuestion)}
                      checkAnswerButton={
                        isPracticeMode ? (
                          <Button variant="outlined" size="small" startIcon={<FactCheckIcon />} onClick={() => handleVerify()} disabled={verifyLoading} sx={{ textTransform: "none", fontWeight: 700, fontSize: { xs: "0.9rem", sm: "0.8rem" }, py: { xs: 0.875, sm: 0.5 } }}>
                            {verifyLoading ? "Checking…" : "Check answer"}
                          </Button>
                        ) : null
                      }
                    />
                  ) : isErrorFindingStandalone(currentQuestion) || isErrorFindingContext(currentQuestion) ? (
                    <>
                      <EnglishErrorFindingLayout
                        question={currentQuestion}
                        selected={answers[currentQuestion.id] || ""}
                        onSelect={(val) => handleAnswerChange(currentQuestion.id, val)}
                        showAnswers={false}
                        mode="take"
                      />
                      <ScratchPad questionIdx={currentQuestionIndex} scratchDrawingsRef={scratchDrawingsRef} marks={Number(currentQuestion.marks ?? 3) || 3} />
                      {isPracticeMode ? (
                        <Box sx={{ mt: 2 }}>
                          <Button variant="outlined" startIcon={<FactCheckIcon />} onClick={() => handleVerify()} disabled={verifyLoading}>
                            {verifyLoading ? "Checking…" : "Check answer"}
                          </Button>
                        </Box>
                      ) : null}
                    </>
                  ) : isClozeWordChoice(currentQuestion) ? (
                    <>
                      <EnglishClozePassageLayout
                        question={currentQuestion}
                        selected={answers[currentQuestion.id] || ""}
                        onSelect={(val) => handleAnswerChange(currentQuestion.id, val)}
                        showAnswers={false}
                        mode="take"
                      />
                      <ScratchPad questionIdx={currentQuestionIndex} scratchDrawingsRef={scratchDrawingsRef} marks={Number(currentQuestion.marks ?? 3) || 3} />
                      {isPracticeMode ? (
                        <Box sx={{ mt: 2 }}>
                          <Button variant="outlined" startIcon={<FactCheckIcon />} onClick={() => handleVerify()} disabled={verifyLoading}>
                            {verifyLoading ? "Checking…" : "Check answer"}
                          </Button>
                        </Box>
                      ) : null}
                    </>
                  ) : (currentQuestion?.options || []).length === 0 ? (
                    <>
                      <TypeDrawInput
                        key={currentQuestion.id}
                        answer={answers[currentQuestion.id] ?? ""}
                        onChange={(value) => handleAnswerChange(currentQuestion.id, value)}
                        multiline={false}
                        rows={3}
                        label="Your answer"
                        answerLength={isEnglishPaper ? undefined : (Number(currentQuestion.answer_data?.answer_length) || undefined)}
                        questionIdx={currentQuestionIndex}
                        marks={Number(currentQuestion.marks ?? 3) || 3}
                        allowDraw={allowsDrawAnswer(currentQuestion)}
                        defaultTab={isDrawingType(currentQuestion) ? 1 : 0}
                        measurementMode={isMathsPaper ? getMeasurementMode(currentQuestion) : null}
                        showMathTools={isMathsPaper}
                        contextImageUrl={questionContextSnapshot}
                        pendingFloatImage={annotationFloatImage}
                        onFloatImageConsumed={() => setAnnotationFloatImage(null)}
                        questionImages={collectAllQuestionImageEntries(currentQuestion)}
                        checkAnswerButton={
                          isPracticeMode ? (
                            <Button variant="outlined" size="small" startIcon={<FactCheckIcon />} onClick={() => handleVerify()} disabled={verifyLoading} sx={{ textTransform: "none", fontWeight: 700, fontSize: { xs: "0.9rem", sm: "0.8rem" }, py: { xs: 0.875, sm: 0.5 } }}>
                              {verifyLoading ? "Checking…" : "Check answer"}
                            </Button>
                          ) : null
                        }
                      />
                      <ScratchPad questionIdx={currentQuestionIndex} scratchDrawingsRef={scratchDrawingsRef} marks={Number(currentQuestion.marks ?? 3) || 3} />
                    </>
                  ) : (
                    <>
                      <FormControl component="fieldset" fullWidth>
                        {isMatchingType(currentQuestion) ? (
                          <>
                            <FormLabel component="legend">Match each item to its pair</FormLabel>
                            <MatchingInput
                              options={currentQuestion.options || []}
                              matchOptions={currentQuestion.match_options || []}
                              value={answers[currentQuestion.id]}
                              onChange={(next) => handleAnswerChange(currentQuestion.id, next)}
                            />
                          </>
                        ) : isOrderingType(currentQuestion) ? (
                          <>
                            <FormLabel component="legend">Put the items in the correct order</FormLabel>
                            <OrderingInput
                              options={currentQuestion.options || []}
                              value={answers[currentQuestion.id]}
                              onChange={(next) => handleAnswerChange(currentQuestion.id, next)}
                            />
                          </>
                        ) : isMultiSelectType(currentQuestion) ? (
                          <>
                            <FormLabel component="legend">Select your answer(s)</FormLabel>
                            <FormGroup>
                              {(currentQuestion.options || []).map((option, oi) => {
                                const optVal = option.id ?? `q${currentQuestion.id}_o${oi}`;
                                const checked = Array.isArray(answers[currentQuestion.id]) && answers[currentQuestion.id].includes(optVal);
                                return (
                                  <FormControlLabel
                                    key={optVal}
                                    control={<Checkbox checked={checked} onChange={() => handleAnswerChange(currentQuestion.id, optVal, true)} />}
                                    label={
                                      <Box sx={{ display: "flex", flexDirection: option.image_url ? "column" : "row", alignItems: "flex-start", gap: 1 }}>
                                        {option.option_text && <HtmlWithLatex html={String(option.option_text)} style={optionRichHtmlSx} />}
                                        {option.image_url && (
                                          <img
                                            src={option.image_url}
                                            alt={option.option_text || `Option ${oi + 1}`}
                                            style={{ maxWidth: "100%", maxHeight: 110, objectFit: "contain", borderRadius: 4 }}
                                            onError={(e) => { e.target.style.display = "none"; }}
                                          />
                                        )}
                                      </Box>
                                    }
                                    sx={{ mb: 1, p: 1, borderRadius: 1, alignItems: "center", "&:hover": { backgroundColor: "action.hover" } }}
                                  />
                                );
                              })}
                            </FormGroup>
                          </>
                        ) : (
                          <>
                            <FormLabel component="legend">Select your answer</FormLabel>
                            <RadioGroup value={answers[currentQuestion.id] || ""} onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}>
                              {(currentQuestion.options || []).map((option, oi) => (
                                <FormControlLabel
                                  key={option.id ?? `q${currentQuestion.id}_o${oi}`}
                                  value={option.id ?? `q${currentQuestion.id}_o${oi}`}
                                  control={<Radio />}
                                  label={
                                    <Box sx={{ display: "flex", flexDirection: option.image_url ? "column" : "row", alignItems: "flex-start", gap: 1 }}>
                                      {option.option_text && <HtmlWithLatex html={String(option.option_text)} style={optionRichHtmlSx} />}
                                      {option.image_url && (
                                        <img
                                          src={option.image_url}
                                          alt={option.option_text || `Option ${oi + 1}`}
                                          style={{ maxWidth: "100%", maxHeight: 110, objectFit: "contain", borderRadius: 4 }}
                                          onError={(e) => { e.target.style.display = "none"; }}
                                        />
                                      )}
                                    </Box>
                                  }
                                  sx={{ mb: 1, p: 1, borderRadius: 1, alignItems: "center", "&:hover": { backgroundColor: "action.hover" } }}
                                />
                              ))}
                            </RadioGroup>
                          </>
                        )}
                      </FormControl>
                      <ScratchPad questionIdx={currentQuestionIndex} scratchDrawingsRef={scratchDrawingsRef} marks={Number(currentQuestion.marks ?? 3) || 3} />
                      {isPracticeMode ? (
                        <Box sx={{ mt: 2 }}>
                          <Button variant="outlined" startIcon={<FactCheckIcon />} onClick={() => handleVerify()} disabled={verifyLoading}>
                            {verifyLoading ? "Checking…" : "Check answer"}
                          </Button>
                        </Box>
                      ) : null}
                    </>
                  )
                ) : (
                  <TypeDrawInput
                    key={currentQuestion.id}
                    answer={answers[currentQuestion.id] ?? ""}
                    onChange={(value) => handleAnswerChange(currentQuestion.id, value)}
                    multiline={isEssayType(currentQuestion)}
                    rows={isEssayType(currentQuestion) ? 6 : 3}
                    label={isEssayType(currentQuestion) ? "Your essay" : "Your answer"}
                    answerLength={(isEssayType(currentQuestion) || (isEnglishPaper && !isDrawingType(currentQuestion))) ? undefined : (Number(currentQuestion.answer_data?.answer_length) || undefined)}
                    unitSuffix={showAnswerUnit && currentQuestion.answer_data?.answer_unit && currentQuestion.answer_data?.answer_unit_prefix !== true ? currentQuestion.answer_data.answer_unit : undefined}
                    unitPrefix={showAnswerUnit && currentQuestion.answer_data?.answer_unit && currentQuestion.answer_data?.answer_unit_prefix === true ? currentQuestion.answer_data.answer_unit : undefined}
                    questionIdx={currentQuestionIndex}
                    marks={Number(currentQuestion.marks ?? 3) || 3}
                    allowDraw={allowsDrawAnswer(currentQuestion)}
                    defaultTab={isDrawingType(currentQuestion) ? 1 : 0}
                    measurementMode={isMathsPaper ? getMeasurementMode(currentQuestion) : null}
                    showMathTools={isMathsPaper}
                    contextImageUrl={collectQuestionImages(currentQuestion)[0]}
                    pendingFloatImage={annotationFloatImage}
                    onFloatImageConsumed={() => setAnnotationFloatImage(null)}
                    questionImages={collectAllQuestionImageEntries(currentQuestion)}
                    checkAnswerButton={
                      isPracticeMode ? (
                        <Button variant="outlined" size="small" startIcon={<FactCheckIcon />} onClick={() => handleVerify()} disabled={verifyLoading} sx={{ textTransform: "none", fontWeight: 700, fontSize: { xs: "0.9rem", sm: "0.8rem" }, py: { xs: 0.875, sm: 0.5 } }}>
                          {verifyLoading ? "Checking…" : "Check answer"}
                        </Button>
                      ) : null
                    }
                  />
                );

                return (
                  <>
                    <QuestionAnnotationCanvas
                      questionIdx={currentQuestionIndex}
                      visible={annotating}
                      onCapture={
                        isTextQuestionType(currentQuestion) ||
                        isPlotPointsQuestion(currentQuestion) ||
                        isDiagramPlacementQuestion(currentQuestion) ||
                        (currentQuestion?.options || []).length === 0
                          ? handleAnnotationCapture
                          : undefined
                      }
                    >
                      {questionDisplay}
                    </QuestionAnnotationCanvas>
                    {answerInput}
                  </>
                );
              })()
            )}

            {isPracticeMode && verifyResult && verifyQuestionId === currentQuestion?.id && !isGroupedQuestion(currentQuestion) ? (
              <Alert severity={getVerifyDisplayProps(verifyResult)?.severity || "info"} sx={{ mt: 2 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                  <Typography variant="body2">
                    {verifyResult.hint || getVerifyDisplayProps(verifyResult)?.prefix || ""}
                  </Typography>
                  {normalizeQuestionType(currentQuestion) === 'table'
                    ? renderTableCorrectAnswer(currentQuestion, getVerifyDisplayProps(verifyResult)?.answerLabel || "Correct answer:")
                    : isBoxModeImageFill(currentQuestion)
                    ? renderImageFillCorrectAnswer(currentQuestion, getVerifyDisplayProps(verifyResult)?.answerLabel || "Correct answer:", verifyResult.correct_answer)
                    : (verifyResult.correct_answer ? renderCorrectAnswer(verifyResult.correct_answer, getVerifyDisplayProps(verifyResult)?.answerLabel || "Correct answer:", currentQuestion?.type === 'essay') : null)}
                  {verifyResult.correct_option_image ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 0.25 }}>
                      {!verifyResult.correct_answer && (
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{getVerifyDisplayProps(verifyResult)?.answerLabel || "Correct answer:"}</Typography>
                      )}
                      <Box component="img" src={verifyResult.correct_option_image} alt="Correct option" onError={(e) => { e.target.style.display = "none"; }} sx={{ maxWidth: "100%", maxHeight: 120, objectFit: "contain", borderRadius: 1, border: "1px solid", borderColor: "divider", bgcolor: "white", alignSelf: "flex-start" }} />
                    </Box>
                  ) : null}
                  {verifyResult.submitted_drawing ? (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {verifyResult.ai_evaluated ? "Your drawing (as seen by AI):" : "Your drawing:"}
                      </Typography>
                      <Box component="img" src={verifyResult.submitted_drawing} alt="Your submitted drawing" sx={{ maxWidth: "100%", maxHeight: 340, borderRadius: 1, border: "1px solid", borderColor: "divider", bgcolor: "white" }} />
                    </Box>
                  ) : null}
                  {verifyResult.correct_drawing && !verifyResult.correct_interactive ? (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                        Correct plotted solution:
                      </Typography>
                      <Box component="img" src={verifyResult.correct_drawing} alt="Correct plotted solution" sx={{ maxWidth: "100%", maxHeight: 300, borderRadius: 1, border: "1px solid", borderColor: "divider", bgcolor: "white" }} />
                    </Box>
                  ) : null}
                  {verifyResult.correct_interactive ? (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>Correct solution:</Typography>
                      <InteractiveDiagramCanvas
                        spec={verifyResult.correct_interactive}
                        imageUrl={interactiveImageOf(currentQuestion)}
                        maxWidth={480}
                        readOnly
                        revealValue={revealValueOf(verifyResult.correct_interactive)}
                      />
                      {interactiveWrittenOf(verifyResult.correct_interactive) ? (
                        <Typography variant="body2" sx={{ mt: 0.75 }}>
                          <strong>Written answer:</strong> {verifyResult.correct_interactive.written.answer}
                        </Typography>
                      ) : null}
                    </Box>
                  ) : null}
                </Box>
              </Alert>
            ) : null}
          </Box>

          {/* Sticky bottom navigation */}
          <Box sx={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            position: "sticky", bottom: 0, zIndex: 10, bgcolor: "#f5f5f5",
            mx: { xs: -1.5, sm: -3 }, px: { xs: 1.5, sm: 3 }, pt: 1.5, pb: { xs: 2.5, sm: 2 }, mt: 1, borderTop: "1px solid #e8e8e8",
          }}>
            <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handlePrevious} disabled={currentQuestionIndex === 0}
              sx={{ textTransform: "none", fontWeight: 600, fontSize: { xs: "0.95rem", sm: "0.875rem" }, py: { xs: 1, sm: 0.75 }, px: { xs: 1.5, sm: 1.5 } }}>
              Prev
            </Button>
            <Button variant="outlined" startIcon={<BookmarkIcon fontSize="small" />} onClick={markAnswerLater}
              sx={{ textTransform: "none", fontWeight: 600, color: "info.main", borderColor: "info.main", fontSize: { xs: "0.95rem", sm: "0.875rem" }, py: { xs: 1, sm: 0.75 } }}>
              Answer Later
            </Button>
            <Button variant="outlined" endIcon={<ArrowForwardIcon />} onClick={handleNext} disabled={currentQuestionIndex === questions.length - 1}
              sx={{ textTransform: "none", fontWeight: 600, fontSize: { xs: "0.95rem", sm: "0.875rem" }, py: { xs: 1, sm: 0.75 }, px: { xs: 1.5, sm: 1.5 } }}>
              Next
            </Button>
          </Box>
        </Box>

        {/* Right palette sidebar — always visible on desktop (lg+), toggled on mobile */}
        {(paletteOpen || !isMobile) && <Box sx={{
          width: { xs: "100%", lg: 280 },
          flexShrink: 0,
          bgcolor: "white",
          borderLeft: { xs: "none", lg: "1px solid #e0e0e0" },
          borderTop: { xs: "1px solid #e0e0e0", lg: "none" },
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          maxHeight: { xs: 300, lg: "none" },
        }}>
          {/* Palette header */}
          <Box sx={{ px: 2, py: 1, borderBottom: "1px solid #e0e0e0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, fontSize: { xs: "0.8rem", sm: "0.75rem" }, color: "text.secondary" }}>
                Question Palette
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: "0.82rem", sm: "0.75rem" } }}>
                {answeredCount} of {displayTotalQuestions} answered
              </Typography>
              {!isPracticeMode ? (
                <Box sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 0.5 }}>
                  <AccessTimeIcon sx={{ fontSize: 14, color: timeRemaining < 300 ? "warning.main" : "text.secondary" }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, fontFamily: "monospace", color: timeRemaining < 300 ? "warning.main" : "text.primary" }}>
                    {formatTime(timeRemaining)}
                  </Typography>
                </Box>
              ) : null}
            </Box>
            <Button size="small" onClick={() => setPaletteOpen(false)} sx={{ display: { xs: "flex", lg: "none" }, minWidth: 0, px: 1, textTransform: "none", fontSize: "0.75rem", color: "text.secondary" }}>
              ✕ Close
            </Button>
          </Box>
          {/* Question number grid */}
          <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
            {questionNavSections.map((section, sIdx) => (
              <Box key={`pal_section_${sIdx}`} sx={{ mb: 1.5 }}>
                {section.title ? (
                  <Typography variant="caption" sx={{ display: "block", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "#1565c0", fontSize: "0.68rem", mb: 0.75, borderBottom: "1px solid #e3f2fd", pb: 0.25 }}>
                    {section.title}
                  </Typography>
                ) : null}
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {section.items.map((item) => {
                    const isCurrent = item.isActive;
                    const isLong = item.label.length > 3;
                    return (
                      <Box key={`pal_${item.key}`} sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.3 }}>
                        <Box sx={{ position: "relative", display: "inline-flex" }}>
                          <Box
                            onClick={() => { goToQuestionEntry(item.index, item.subIdx); if (isMobile) setPaletteOpen(false); }}
                            sx={{
                              width: isLong ? "auto" : { xs: 44, sm: 40 }, minWidth: { xs: 44, sm: 40 }, height: { xs: 44, sm: 40 },
                              px: isLong ? 1 : 0, display: "flex", alignItems: "center", justifyContent: "center",
                              borderRadius: isLong ? 2 : "50%", cursor: "pointer", fontWeight: 700,
                              fontSize: isLong ? "0.65rem" : { xs: "0.9rem", sm: "0.85rem" },
                              bgcolor: statusColors[item.status],
                              color: item.status === STATUS.NOT_VISITED ? "#374151" : "white",
                              border: isCurrent ? "3px solid #1a1a1a" : "2px solid transparent",
                              boxShadow: isCurrent ? "0 0 0 1px #1a1a1a" : "none",
                              transition: "all 0.15s",
                              "&:hover": { opacity: 0.85, transform: "scale(1.1)" },
                            }}
                          >
                            {item.label}
                          </Box>
                          {item.status === STATUS.FLAGGED && (
                            <FlagIcon sx={{ position: "absolute", top: -6, right: -6, fontSize: 14, color: "#ff9800", filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.4))", pointerEvents: "none" }} />
                          )}
                        </Box>
                        {item.childLabels.length > 0 ? (
                          <Box sx={{ display: "flex", gap: 0.3, flexWrap: "wrap", justifyContent: "center", maxWidth: 64 }}>
                            {item.childLabels.map((sub) => (
                              <Box
                                key={sub.id}
                                onClick={() => goToQuestionEntry(item.index, typeof sub.subIdx === "number" ? sub.subIdx : null)}
                                sx={{ fontSize: "0.58rem", fontWeight: 700, px: 0.5, py: 0.05, borderRadius: 0.5, bgcolor: sub.answered ? "#4caf50" : "#e0e0e0", color: sub.answered ? "white" : "#374151", lineHeight: 1.6, minWidth: 14, textAlign: "center", cursor: "pointer", border: sub.isActive ? "1px solid #111" : "1px solid transparent" }}
                              >
                                {sub.label}
                              </Box>
                            ))}
                          </Box>
                        ) : null}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ))}
          </Box>
          {/* Legend */}
          <Box sx={{ px: 2, py: 1.5, borderTop: "1px solid #e0e0e0" }}>
            {isTester && paperMeta?.total_marks > 0 ? (
              <Box sx={{ mb: 1.5, p: 1, borderRadius: 1.5, border: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                  <Typography variant="caption" fontWeight={600} color="text.secondary">
                    Total marks — {questions.length}Q
                  </Typography>
                  <Typography variant="caption" fontWeight={700}>
                    {paperMeta.total_marks}m
                  </Typography>
                </Box>
                <LinearProgress variant="determinate" value={100} sx={{ borderRadius: 1, height: 6 }} />
              </Box>
            ) : null}
            {[
              { label: `Answered - ${answeredCount}`, color: statusColors[STATUS.ANSWERED] },
              ...(partialCount > 0 ? [{ label: `Partly answered - ${partialCount}`, color: statusColors[STATUS.PARTIAL] }] : []),
              { label: `Answer later - ${answerLaterCount}`, color: statusColors[STATUS.ANSWER_LATER] },
              { label: `Flagged - ${flaggedCount}`, color: statusColors[STATUS.FLAGGED] },
              { label: `Unanswered - ${unansweredCount}`, color: statusColors[STATUS.UNANSWERED] },
              { label: `Not visited - ${notVisitedCount}`, color: statusColors[STATUS.NOT_VISITED] },
            ].map(({ label, color }) => (
              <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: "0.8rem", sm: "0.75rem" } }}>{label}</Typography>
              </Box>
            ))}
          </Box>
        </Box>}
      </Box>

      {/* Submit dialog */}
      <Dialog open={mcqNoticeDialog.open} onClose={() => setMcqNoticeDialog((p) => ({ ...p, open: false }))} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Note about this paper</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5, whiteSpace: "pre-line" }}>
            {mcqNoticeDialog.message || "This paper was originally designed for pen and paper. The questions below are difficult to answer online (e.g. drawing tasks) and are recommended to be attempted in a multiple-choice style — do your best, and don't worry if they are hard to complete exactly on screen."}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {mcqNoticeDialog.items.map((item, i) => (
              <Box key={i} sx={{ p: 1, borderRadius: 1, bgcolor: "#fff8e1", border: "1px solid #ffe082" }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{item.label}</Typography>
                {item.note && (
                  <Typography variant="caption" color="text.secondary">{item.note}</Typography>
                )}
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setMcqNoticeDialog((p) => ({ ...p, open: false }))}>
            OK, start the paper
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showSubmitDialog} onClose={() => !submitting && setShowSubmitDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{isPracticeMode ? "Finish practice?" : "Submit paper?"}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", p: 1.5, borderRadius: 2, bgcolor: "#e8f5e9" }}>
              <Typography variant="body2">Answered</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: "success.main" }}>
                {answeredCount}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", p: 1.5, borderRadius: 2, bgcolor: "#ffebee" }}>
              <Typography variant="body2">Unanswered</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: "error.main" }}>
                {unansweredCount}
              </Typography>
            </Box>
          </Box>
          <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
            Jump to question
          </Typography>
          <Box sx={{ maxHeight: 220, overflow: "auto", border: "1px solid #e0e0e0", borderRadius: 1, p: 0.5, mt: 0.5 }}>
            {questions.map((question, index) => (
              <Box key={question.id} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 0.5, px: 1, borderRadius: 0.5, "&:hover": { bgcolor: "action.hover" } }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: isQuestionAnswered(question, answers) ? "#4caf50" : "#ef5350", flexShrink: 0 }} />
                  <Typography variant="caption" noWrap sx={{ flex: 1 }}>
                    Q{getQuestionLabel(question, index)}: {getQuestionPreview(question, answers)}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  sx={{ textTransform: "none", fontSize: "0.7rem", flexShrink: 0 }}
                  onClick={() => {
                    setShowSubmitDialog(false);
                    goToQuestion(index);
                  }}
                >
                  Jump
                </Button>
              </Box>
            ))}
          </Box>
          <DialogContentText sx={{ mt: 2 }}>
            {isPracticeMode ? "Practice mode — your score will not be saved on your record." : "Once submitted, you cannot change your answers."}
            {!isPracticeMode && unansweredCount > 0 ? " You have unanswered questions — unanswered questions will be marked as incorrect." : ""}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setShowSubmitDialog(false)} sx={{ textTransform: "none" }}>
            Go back
          </Button>
          <Button onClick={() => handleSubmit()} variant="contained" disabled={submitting || aiScoring.active} sx={{ bgcolor: "#ef5350", "&:hover": { bgcolor: "#c62828" }, textTransform: "none" }}>
            {submitting ? <CircularProgress size={18} color="inherit" /> : isPracticeMode ? "Finish practice" : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Leave dialog (triggered by Back button / answered progress) */}
      <Dialog open={showLeaveDialog} onClose={() => setShowLeaveDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Leave this paper?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your answers are saved automatically. You can resume this paper and continue from where you left off. Submit when you are ready to record your attempt.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setShowLeaveDialog(false)} sx={{ textTransform: "none" }}>
            Stay
          </Button>
          <Button color="error" variant="contained" onClick={() => { setShowLeaveDialog(false); isLeavingRef.current = true; navigate(`/${STUDENT_BASE}/past-papers`); }} sx={{ textTransform: "none" }}>
            Leave without submitting
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={incompleteNextDialog.open} onClose={() => setIncompleteNextDialog((p) => ({ ...p, open: false }))} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, borderTop: "4px solid #ff9800" }}>Some parts not answered</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You still have {incompleteNextDialog.remaining} unanswered {incompleteNextDialog.remaining === 1 ? "part" : "parts"} in this question. Do you want to keep answering, or skip to the next question?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            variant="contained"
            onClick={() => {
              setGroupedSubIdx(incompleteNextDialog.firstUnansweredSub);
              setIncompleteNextDialog((p) => ({ ...p, open: false }));
            }}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Keep answering
          </Button>
          <Button
            color="warning"
            onClick={() => {
              setIncompleteNextDialog((p) => ({ ...p, open: false }));
              goToQuestion(currentQuestionIndex + 1);
            }}
            sx={{ textTransform: "none" }}
          >
            Skip to next question
          </Button>
        </DialogActions>
      </Dialog>

      {/* Navigation interception dialog (sidebar/pushState navigation) */}
      <Dialog open={leaveDialogOpen} onClose={() => setLeaveDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, borderTop: "4px solid #ff9800" }}>Leave this paper?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your answers are saved automatically. You can resume this paper and continue from where you left off. Submit when you are ready to record your attempt.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setLeaveDialogOpen(false)} sx={{ textTransform: "none" }}>
            Stay on paper
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setLeaveDialogOpen(false);
              isLeavingRef.current = true;
              const dest = pendingNavRef.current || `/${STUDENT_BASE}/past-papers`;
              navigate(dest);
            }}
            sx={{ textTransform: "none" }}
          >
            Leave without submitting
          </Button>
        </DialogActions>
      </Dialog>

      {/* Flag for Review dialog */}
      <Dialog open={flagReasonDialog.open} onClose={() => setFlagReasonDialog(EMPTY_FLAG_DIALOG)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Flag for Review</DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select the issue type(s) and/or add a brief reason — all optional.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={flagReasonDialog.issue_question}
                onChange={(e) => setFlagReasonDialog((p) => ({ ...p, issue_question: e.target.checked }))}
              />
            }
            label="Question issue"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={flagReasonDialog.issue_answer}
                onChange={(e) => setFlagReasonDialog((p) => ({ ...p, issue_answer: e.target.checked }))}
              />
            }
            label="Answer issue"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={flagReasonDialog.issue_image}
                onChange={(e) => setFlagReasonDialog((p) => ({ ...p, issue_image: e.target.checked }))}
              />
            }
            label="Image issue"
          />
          <Divider sx={{ my: 1.5 }} />
          <Box sx={{ '& .ql-container': { minHeight: 100, fontSize: '0.9rem' }, '& .ql-editor.ql-blank::before': { fontStyle: 'normal', color: '#9e9e9e' } }}>
            <ReactQuill
              theme="snow"
              value={flagReasonDialog.reason}
              onChange={(html) => processFlagReasonHtml(html, (v) => setFlagReasonDialog((p) => ({ ...p, reason: v })))}
              modules={FLAG_QUILL_MODULES}
              formats={FLAG_QUILL_FORMATS}
              placeholder="e.g. Unclear wording, possible typo, wrong answer..."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setFlagReasonDialog(EMPTY_FLAG_DIALOG)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleFlagReasonSave}
            disabled={!!flaggingInProgress[currentQuestionIndex]}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {flaggingInProgress[currentQuestionIndex] ? <CircularProgress size={16} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {aiScoring.active && (
        <Box sx={{ position: "fixed", inset: 0, bgcolor: "rgba(10,10,30,0.82)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", px: 2 }}>
          <Card sx={{ width: "100%", maxWidth: 520, borderRadius: 3, p: 2.5, textAlign: "center" }}>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 1.2 }}>
              AI is scoring your paper
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              Please wait while we evaluate typed and drawn answers.
            </Typography>
            <LinearProgress variant="determinate" value={aiScoring.progress} sx={{ height: 12, borderRadius: 999, mb: 1.2 }} />
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {Math.round(aiScoring.progress)}%
            </Typography>
          </Card>
        </Box>
      )}
    </Box>
    <VirtualProtractorOverlay visible={showProtractor} />
    <VirtualRulerOverlay visible={showRuler} pxPerCm={activePxPerCm} />
    <VirtualSetSquareOverlay visible={showSetSquare} />
    <VirtualCompassOverlay visible={showCompass} pxPerCm={activePxPerCm} />
    {canVerifyQuestion && currentQuestion && (
      <QuestionVerifyDialog
        open={verifyDialogOpen}
        question={verifyQuestionFull ?? currentQuestion}
        loading={verifyQuestionLoading}
        onClose={() => setVerifyDialogOpen(false)}
        onSubmit={async ({ status, notes, platforms }) => {
          const bankId = currentQuestion.qb_id || currentQuestion.id;
          const ok = await submitVerification({ type: "question", id: bankId, status, notes, platforms });
          if (ok) {
            toast.success(`Question marked as ${status}.`);
            setVerifyDialogOpen(false);
            const isLastQuestion = currentQuestionIndex === questions.length - 1;
            if (isLastQuestion && canVerifyPaper && paperMeta?.paper_id) {
              setVerifyPaperDialogOpen(true);
            }
          } else {
            toast.error("Failed to save verification.");
          }
        }}
        onEdit={verifyQuestionFull ? (q) => {
          setVerifyDialogOpen(false);
          setEditFromVerifyQuestion(q);
          setEditFromVerifyOpen(true);
        } : undefined}
        itemData={getItemData("question", currentQuestion.qb_id || currentQuestion.id)}
        submitting={verifySubmitting}
      />
    )}
    {editFromVerifyOpen && editFromVerifyQuestion && (
      <QuestionBankQuestionEditDialog
        question={editFromVerifyQuestion}
        saving={editFromVerifySaving}
        onClose={() => { setEditFromVerifyOpen(false); setEditFromVerifyQuestion(null); }}
        onSave={async (payload) => {
          const savedQuestionIndex = currentQuestionIndex;
          setEditFromVerifySaving(true);
          try {
            await api.put(`/admin/question-bank/${editFromVerifyQuestion.id}`, payload);
            await refreshPaperQuestions();
            setCurrentQuestionIndex(savedQuestionIndex);
            toast.success("Question saved.");
            setEditFromVerifyOpen(false);
            setEditFromVerifyQuestion(null);
          } catch {
            toast.error("Failed to save question.");
          } finally {
            setEditFromVerifySaving(false);
          }
        }}
      />
    )}
    {canVerifyPaper && paperMeta?.paper_id && (
      <Dialog open={verifyPaperDialogOpen} onClose={() => setVerifyPaperDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Mark entire paper as verified?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            You verified the last question. Do you want to mark the whole paper <strong>{paperMeta.paper_name}</strong> as approved?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerifyPaperDialogOpen(false)} disabled={verifyPaperSubmitting}>Skip</Button>
          <Button
            variant="contained"
            color="error"
            disabled={verifyPaperSubmitting}
            onClick={async () => {
              setVerifyPaperSubmitting(true);
              try {
                await api.post("admin/content-verifications", {
                  verifiable_type: "paper",
                  verifiable_id: paperMeta.paper_id,
                  status: "approved",
                  notes: null,
                });
                toast.success("Paper marked as approved.");
              } catch {
                toast.error("Failed to mark paper as verified.");
              } finally {
                setVerifyPaperSubmitting(false);
                setVerifyPaperDialogOpen(false);
              }
            }}
          >
            {verifyPaperSubmitting ? <CircularProgress size={18} color="inherit" /> : "Approve paper"}
          </Button>
        </DialogActions>
      </Dialog>
    )}
    </MeasurementToolsProvider>
    </AnnotateProvider>
  );
};

export default StudentPaperTake;
